///
//  {yAxisPrecision}
// Copyright (c) 2009-2018 Bequant S.L.
// All rights reserved.
//
// This product or document is proprietary to and embodies the
// confidential technology of Bequant S.L., Spain.
// Possession, use, duplication or distribution of this product
// or document is authorized only pursuant to a valid written
// license from Bequant S.L.
//
// DESCRIPTION:
//   Main functions and classes.
//

import * as React from "react";
import { createRoot } from "react-dom/client";
import App from './App';
import { parsePolicy as parsePolicyRate, showDynPolicyRate } from "views/Config/PolicyEdit/utils";
import { responsePolicyMap } from "views/Config/SubscriberRates/PolicyEdit/utils";

window.quoteWrap = input => `'${input}'`;

const colorsGeneric = [
  "#006ab7",
  "#FFA500",
  "#228B22",
  "#FA8072",
  "#F522F5",
  "#DC143C",
  "#556B2F",
  "#696969",
  "#483D8B",
  "#00008B",
  "#8FBC8F",
  "#B03060",
  "#1DF11D",
  "#48E2AA",
  "#8B4513",
  "#16F8F8",
  "#1A1AF1",
  "#B7EC64",
  "#DA70D6",
  "#7F007F",
  "#008B8B",
  "#F4F436",
  "#90EE90",
  "#ADD8E6",
  "#F81B93",
  "#7B68EE",
  "#FFDEAD",
  "#D6D6D6",
  "#7F0000",
  "#556B2F",
];
const allSpecialSymbols = /[#"'|\\]/g;
const escapedSymbol = '\\$&';

const safeStrUnderscore = input =>
  input
    .trim(/\s/)
    .replace(/\s/g, "_")
    .replace(allSpecialSymbols, escapedSymbol);

window.safeStrKeepSpaces = input =>
  input
    .replace(allSpecialSymbols, escapedSymbol);
const _SAFE_STR_DEFAULTS = {
  preserveSpaces: false,
}

window.safeStr = (input, options=_SAFE_STR_DEFAULTS) =>
  quoteWrap(
    options.preserveSpaces === true
    ? safeStrKeepSpaces(String(input))
    : safeStrUnderscore(String(input))
  );

const safeSubsId = input => safeStr(input);

// Imports table default module
const whenTableAvailable = import("./common/table.js").then(
  (module) => module.default
);

const validateIPv4WithMask = (address) => {
  const ipv4_regex =
    /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/gm;

  return address === "::/0" || ipv4_regex.test(address);

};

const validateIPv6WithMask = (address) => {
  const ipv6_regex =
    /^(?:(?:[a-fA-F\d]{1,4}:){7}(?:[a-fA-F\d]{1,4}|:)|(?:[a-fA-F\d]{1,4}:){6}(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}|:[a-fA-F\d]{1,4}|:)|(?:[a-fA-F\d]{1,4}:){5}(?::(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}|(?::[a-fA-F\d]{1,4}){1,2}|:)|(?:[a-fA-F\d]{1,4}:){4}(?:(?::[a-fA-F\d]{1,4}){0,1}:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}|(?::[a-fA-F\d]{1,4}){1,3}|:)|(?:[a-fA-F\d]{1,4}:){3}(?:(?::[a-fA-F\d]{1,4}){0,2}:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}|(?::[a-fA-F\d]{1,4}){1,4}|:)|(?:[a-fA-F\d]{1,4}:){2}(?:(?::[a-fA-F\d]{1,4}){0,3}:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}|(?::[a-fA-F\d]{1,4}){1,5}|:)|(?:[a-fA-F\d]{1,4}:){1}(?:(?::[a-fA-F\d]{1,4}){0,4}:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}|(?::[a-fA-F\d]{1,4}){1,6}|:)|(?::(?:(?::[a-fA-F\d]{1,4}){0,5}:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}|(?::[a-fA-F\d]{1,4}){1,7}|:)))(?:%[0-9a-zA-Z]{1,})?(\/([1-9]|[0-9][0-9]|1[0-2][0-8]))?$/gm;

  return ipv6_regex.test(address);
};
//
// Period (selector)
//
var Period = (function() {
  var CONST = {
      PERIOD_STR_VALUES: [
        'Day',
        'Week',
        'Month',
        '12 Hours',
        '1 Day',
        '2 Days',
        '3 Days',
        '3 Months',
      ]
    };

  function Period(periodSel, dateStr, roundToPreviousHour) {
    // Parameter roundToPreviousHour is optional
    // Substitute remote timezone with local timezone
    var totalMins = moment().utcOffset(),
      sign = (totalMins > 0 ? ' +' : ' -'),
      dateArr = dateStr.split(/\s+/),
      dateStrNew = null,
      hour = null,
      min = null;
    if (totalMins < 0) totalMins = - totalMins;
    dateArr.splice(5, 2);
    hour = Math.floor(totalMins / 60);
    min = totalMins % 60;
    dateStrNew = dateArr.join(' ');
    dateStrNew += sign;
    dateStrNew += (hour < 10 ? '0' : '') + hour.toString();
    dateStrNew += (min < 10 ? '0' : '') + min.toString();

    // Dates must be created as 'new instances', forcing local timezone
    this.endMoment = moment.parseZone(dateStrNew);
    this.beginMoment = moment.parseZone(dateStrNew);
    // Adjust roundToPreviousHour
    if ((roundToPreviousHour !== undefined) && roundToPreviousHour) {
      this.beginMoment.minutes(0);
      this.beginMoment.seconds(0);
      this.beginMoment.milliseconds(0);
    }

    // Compute beginMoment
    if (periodSel == '12 Hours') {
      this.beginMoment.subtract(12, 'hours');
    } else if (periodSel == 'Day' || periodSel == '1 Day') {
      this.beginMoment.subtract(1, 'days');
    } else if (periodSel == 'Week') {
      this.beginMoment.subtract(7, 'days');
    } else if (periodSel == 'Month') {
      this.beginMoment.subtract(1, 'months');
    } else if (periodSel == '2 Days') {
      this.beginMoment.subtract(2, 'days');
    } else if (periodSel == '3 Days') {
      this.beginMoment.subtract(3, 'days');
    } else if (['3 Months', '3 months', 'ThreeMonths'].includes(periodSel)) {
      this.beginMoment.subtract(3, 'months');
    } else {
      console.log('period.unknown: %s', periodSel);
    }
    console.log('Period: %s [%s, %s]', periodSel, this.beginMoment.format(), this.endMoment.format());
  };
  Period.prototype = {
    isValid: function(periodSel) {
      return (CONST.PERIOD_STR_VALUES.indexOf(periodSel) !== -1);
    },
    days: function() {
      return this.endMoment.diff(this.beginMoment, 'days');
    },
    hours: function() {
      return this.endMoment.diff(this.beginMoment, 'hours');
    },
    minutes: function() {
      return this.endMoment.diff(this.beginMoment, 'minutes');
    },
    fiveMinutes: function() {
      return this.endMoment.diff(this.beginMoment, 'minutes') / 5;
    },
    minMoment: function() {
      return this.beginMoment;
    },
    minDate: function() {
      return this.beginMoment.toDate();
    },
    maxMoment: function() {
      return this.endMoment;
    },
    maxDate: function() {
      return this.endMoment.toDate();
    },
    isOutOfDate: function(targetDate) {
      // Check only before condition because endMoment is 'now'.
      //console.log('Period.targetDate: %s', moment(targetDate).format());
      //console.log('Period.outOfRange: %s', moment(targetDate).isBefore(this.beginMoment));

      return moment(targetDate).isBefore(this.beginMoment);
    }
  };

  return Period;
})();


//
// Dataset (generic dataset)
//
window.Dataset = function Dataset(key, pointsPerChart) {
  this.key = key;
  this.storedValues = [];
  this.values = this.storedValues;
  this.interval = this.getStoredInterval();
  this.isZeroOrNaN = true;
  this.low = null;
  this.high = null;
  this.subsample = null;
  this.pointsPerChart = (pointsPerChart === undefined ?
    window.innerWidth / 2 : pointsPerChart);
  this.resetData();
};
Dataset.prototype = {
  resetData: function() {
    this.storedValues = [];
    this.values = this.storedValues;
    this.interval = this.getStoredInterval();
  },
  pushData: function(datasetItem) {
    if (this.isZeroOrNaN) {
      var p, value;
      for (p in datasetItem) {
        value = datasetItem[p];
        if (p != 'date' && value != null && value != 0) {
          this.isZeroOrNaN = false;
          break;
        }
      }
    }
    this.storedValues.push(datasetItem);
  },
  resetInterval: function() {
    this.values = this.storedValues;
  },
  getStoredInterval: function() {
    var from = null,
      to = null;
    // Returns total dates interval
    if (this.storedValues.length > 0) {
      from = this.storedValues[0]['date'];
      to = this.storedValues[this.storedValues.length - 1]['date'];
    }
    return {from: from, to: to};
  },
  binSearch: function(el, arr, compareCb) {
    var low = 0;
    var high = arr.length - 1;
    while (low <= high) {
      var mid = (high + low) >> 1;
      var cmp = compareCb(el, arr[mid]);
      if (cmp > 0) {
        low = mid + 1;
      } else if (cmp < 0) {
        high = mid - 1;
      } else {
        return mid;
      }
    }
    //return -low - 1; // Original
    return -low;
  },
  getSelectedInterval: function() {
    var from = null,
      to = null;
    // Returns selected dates interval
    if (this.values.length > 0) {
      from = this.values[0]['date'];
      to = this.values[this.values.length - 1]['date'];
    }
    return {from: from, to: to};
  },
  computeIntervalSelParameters: function(interval) {
    this.low = this.binSearch(interval.from, this.storedValues, function(a, b) {
      return (a.getTime() - b.date.getTime());
    });
    if (this.low < 0) this.low = -this.low;
    this.high = this.binSearch(interval.to, this.storedValues, function(a, b) {
      return (a.getTime() - b.date.getTime());
    });
    if (this.high < 0) this.high = -this.high;
    this.subsample = Math.floor((this.high - this.low)/this.pointsPerChart) + 1;
    console.log('Dataset.selection: [%d, %d] %d from %d sampling 1/%d',
      this.low, this.high,
      ((this.high === 0 && this.low === 0) ? 0 : this.high - this.low + 1),
      this.storedValues.length, this.subsample);
  },
  evalSelection: function() {
    // From this.low to this.high [closed interval]
    // Samples are decimated at 'subsample' rate
    this.values = []; // Clear selection
    if (this.high !== 0 || this.low !== 0) {
      for (var i = 0, j = this.low; j <= this.high; i++) {
        this.values[i] = this.storedValues[j];
        j+=this.subsample;
      }
    }
  },
  selectInterval: function(interval) {
    // Select from interval (from, to)
    // Used in one dataset selection
    this.computeIntervalSelParameters(interval);
    this.evalSelection();
  },
  getSelectedParameters: function() {
    return {low: this.low, high: this.high, subsample: this.subsample};
  },
  selectIntervalUsingParameters: function(interval, params) {
    // Select from parameters (low, high, subsample)
    // Used in repetitive (multiple datasets) selections
    this.low = params.low;
    this.high = params.high;
    this.subsample = params.subsample;
    this.evalSelection();
  }
};


//
// IntervalDataset
//
function IntervalDataset() {
  this.storedValues = [];
  this.storedValuesR = [];
  this.values = this.storedValues;
  this.low = null;
  this.high = null;
  this.resetData();
};
IntervalDataset.prototype = {
  resetData: function() {
    this.storedValues = [];
    this.storedValuesR = [];
    this.values = this.storedValues;
  },
  pushData: function(datasetItem) {
    this.storedValues.push(datasetItem);
  },
  resetInterval: function() {
    this.values = this.storedValues;
  },
  getStoredInterval: function() {
    var from = null,
      to = null;
    // Returns total dates interval
    if (this.storedValues.length > 0) {
      from = this.storedValues[0]['iniDate'];
      to = this.storedValues[this.storedValues.length - 1]['finDate'];
    }
    return {from: from, to: to};
  },
  buildReverseIndex: function() {
    for (var i = 0, len = this.storedValues.length; i < len; i++) {
      this.storedValuesR.push(this.storedValues[i]);
    }
    this.storedValuesR.sort(function(a,b) {
      return b.finDate.getTime() - a.finDate.getTime();
    });
  },
  dumpStoredValues: function(txt) {
    if (txt) console.log(txt);
    for (var i = 0, len = this.storedValues.length; i < len; i++) {
      console.log('INT[%d]: %s', i, JSON.stringify(this.storedValues[i]));
    }
  },
  dumpStoredValuesR: function(txt) {
    if (txt) console.log(txt);
    for (var i = 0, len = this.storedValuesR.length; i < len; i++) {
      console.log('INT[%d]: %s', i, JSON.stringify(this.storedValuesR[i]));
    }
  },
  binSearch: function(el, arr, compareCb) {
    var low = 0;
    var high = arr.length - 1;
    while (low <= high) {
      var mid = (high + low) >> 1;
      var cmp = compareCb(el, arr[mid]);
      if (cmp > 0) {
        low = mid + 1;
      } else if (cmp < 0) {
        high = mid - 1;
      } else {
        return mid;
      }
    }
    //return -low - 1; // Original
    return -low;
  },
  getSelectedInterval: function() {
    var from = null,
      to = null;
    // Returns selected dates interval
    if (this.values.length > 0) {
      from = this.values[0]['date'];
      to = this.values[this.values.length - 1]['date'];
    }
    return {from: from, to: to};
  },
  getLowExtraEntriesR: function(range) {
    var extraValues = [],
      it, rLow;

    rLow = this.binSearch(range.to, this.storedValuesR, function(a, b) {
      //return (b.iniDate.getTime() - a.getTime());
      return (b.iniDate.getTime() - a.getTime());
    });
    if (rLow < 0) rLow = -rLow;
    //console.log('LOW-R-IDX: %d', rLow);

    for (var i = rLow, len = this.storedValuesR.length;
      i < len && (it = this.storedValuesR[i]).finDate >= range.from; i++) {
      if (it.iniDate < range.from) {
        extraValues.push(it);
      }
    }

    return extraValues;
  },
  selectIntervalR: function(range) {
    if (this.storedValues.length > 0 && this.storedValuesR.length == 0) {
      this.buildReverseIndex();
    }

    // Using reverse order algorithm
    var extraValues = [], i, len, it, rLow, rHigh;
    //console.log('SELECT-INTERVAL: [%s, %s] from %d entries', range.from, range.to, this.storedValues.length);

    this.low = this.binSearch(range.from, this.storedValues, function(a, b) {
      return (a.getTime() - b.iniDate.getTime());
    });
    if (this.low < 0) this.low = -this.low;
    //console.log('LOW-IDX: %d', this.low);

    this.high = this.binSearch(range.to, this.storedValues, function(a, b) {
      return (a.getTime() - b.iniDate.getTime());
    });
    if (this.high < 0) this.high = -this.high;
    //console.log('HIGH-IDX: %d', this.high);

    this.values = [];
    extraValues = this.getLowExtraEntriesR(range);

    // Add extra values
    for (var i = 0, len = extraValues.length; i < len; i++) {
      this.values.push(extraValues[i]);
    }
    // Add (from, to) values
    for (i = this.low; i < this.high; i++) {
      this.values.push(this.storedValues[i]);
    }

    console.log('Dataset.selectionR: [%d, %d] %d extra values',
      this.low, this.high, extraValues.length);
  },
};


//
// TooltipMgr
//
window.TooltipMgr = (function() {
  function TooltipMgr() {
  };
  TooltipMgr.prototype = {
    clear: function() {
      //console.log('TooltipMgr.clear');
      $('.nvtooltip').each(function() {
        this.style.opacity = 0;
        $(this).remove();
        //console.log('TooltipMgr.clear: removed %s', this.id);
      });
    },
    onMouseLeave: function(chart) {
      //var that = this;
      $('.nvtooltip').each(function() {
        //console.log('TooltipMgr.onMouseLeav: %f, %s', this.style.opacity, this.id);
        // Remove even when opacity == 0
        if (chart.interactiveLayer.tooltip.id() != this.id ) {
          this.style.opacity = 0;
          $(this).remove();
          //console.log('TooltipMgr.onMouseLeave: removed %s', this.id);
        }
      });
    },
  }

  return TooltipMgr;
})();


//
// TimeSlider
//
window.TimeSlider = (function() {
  function TimeSlider(parentThis, jqSelector) {
    this.slider = null;
    this.jqSelector = jqSelector;
    this.parentThis = parentThis;
  };
  TimeSlider.prototype = {
    render: function(interval, executeSelection) {
      // Stored interval is the same for all 'data' datasets
      var begin = moment(interval.from),
        end = moment(interval.to),
        parseDate = d3.timeParse('%Y-%m-%d %H:%M'),
        that = this;

      if (this.slider === null) {
        var rangeSelector = $(this.jqSelector);
        rangeSelector.ionRangeSlider({
          type: 'double',
          grid: true,
          min: begin,
          max: end,
          //step: (end - begin) / 10,
          hide_min_max: true,
          keyboard: false,
          from: begin,
          to: end,
          drag_interval: true,
          //prefix: "$",
          prettify: function (val) {
            var m = moment(val, 'x').locale('es');
            return (m.format('YYYY-MM-DD HH:mm'));
          },
          onFinish: function (data) {
            var interval = {
              from: parseDate(data.from_pretty),
              to: parseDate(data.to_pretty)
            };
            executeSelection.call(that.parentThis, interval);
          }
        });
        this.slider = rangeSelector.data("ionRangeSlider");
      }

      this.slider.update({
        min: begin,
        max: end,
        from: begin,
        to: end
      });
      executeSelection.call(this.parentThis, interval);
    },
  }

  return TimeSlider;
})();


//
// Login
//
var Login = (function() {
  window.CONST = {
      LOCAL_STORAGE_TOKEN_NAME: 'BQN_SESS_TOKEN',
      LOGO_FILE: {
        generic: 'images/logo-experimental-small.png',
        allot: 'images/logo-allot-small.png',
        cambium: 'images/logo-cambium-big.png',
        viewtinet: 'images/logo-viewtinet-small.png',
        'gs-kr': 'images/logo-generic-small.png',
      },
      LOGO_FILE_DARK: {
        generic: 'images/logo-experimental-small-dark.png',
        allot: 'images/logo-allot-small.png',
        cambium: 'images/logo-cambium-big-dark.png',
        viewtinet: 'images/logo-viewtinet-small.png',
        'gs-kr': 'images/logo-generic-small.png',
      },
      LOGO_LOGIN_FILE: {
        generic: 'images/logo-experimental-big.png',
        allot: 'images/logo-allot-big.png',
        cambium: 'images/logo-cambium-big.png',
        'gs-kr': 'images/logo-generic-big.png',
        viewtinet: 'images/logo-viewtinet-big.png',
      },
      LOGIN_COPYRIGHT: {
        generic: { copyright: 'Bequant S.L.', link: 'https://www.bequant.com' },
        allot: { copyright: 'Bequant S.L.', link: 'https://www.bequant.com' },
        cambium: { copyright: 'Bequant S.L.', link: 'https://www.bequant.com' },
        'gs-kr': { copyright: 'Bequant S.L.', link: 'https://www.bequant.com' },
        viewtinet: { copyright: 'Bequant S.L.', link: 'https://www.bequant.com' },
      },
      PLATFORMS: [
        'generic',
        'allot',
        'cambium',
        'viewtinet',
        'gs-kr',
      ],
  };

  function Login() {
    var that = this;

    this.user = null;
    this.remoteHostname = null;
    this.localAddr = null;
    this.remoteAddr = null;
    this.platform = null;
    this.next = null;
    this.theme = null;

    $('#loginUser').on('keydown', function(e) {
      $('#loginAlert').html("&nbsp;");
    });
    $('#loginPasswd').on('keydown', function(e) {
      $('#loginAlert').html("&nbsp;");
      if (e.keyCode === 13) {
        that.doLogin();
      }
    });
    $('#btnLogin').click(function() {
      that.doLogin();
    });
    $('#menuLogout').click(function() {
      $('#modalLogout').modal('show');
    });
    $('#btnLogout').click(function(event) {
      that.doLogout();
    });

  };
  Login.prototype = {
    isPlatform: function(name) {
      return (this.platform == name);
    },
    isTheme: function(name) {
      return (this.theme == name);
    },
    dumpPlatforStyleVariables() {
      var elem = document.querySelector(':root'),
        style = getComputedStyle(elem),
        props = [
          '--navbar-color',
          '--sidebar-border',
          '--sidebar-bg-color',
          '--sidebar-color',
          '--thead-bg-color',
          '--thead-color',
          '--sidebar-node-selected',
          //'--sidebar-group-flag-before',
          //'--sidebar-group-flag-after',
        ],
        traceProp = function(name) {
          console.log('%s: %s', name, style.getPropertyValue(name));
        };

      for (let prop of props) {
        traceProp(prop);
      }
    },
    switchStyle: function(platform) {
      console.log('Login.style: %s', platform);
      this.dumpPlatforStyleVariables();
      const darkEnabledStored = localStorage.getItem('darkEnabled');
      this.switchTheme(platform, darkEnabledStored==="true" || darkEnabledStored===null);
    },
    switchTheme: function(platform, dark) {
      for (let linkTag of document.getElementsByTagName('link')) {
        if (linkTag.rel.indexOf('stylesheet') > -1 && linkTag.title) {
          if(linkTag.title === "theme" && platform === 'generic' && !dark){
            linkTag.disabled = false;
          }else if(linkTag.title === "theme-dark" && platform === 'generic' && dark){
            linkTag.disabled = false;
          }else if(linkTag.title === "theme-cambium" && platform === 'cambium' && !dark){
            linkTag.disabled = false;
          }else if(linkTag.title === "theme-cambium-dark" && platform === 'cambium' && dark){
            linkTag.disabled = false;
          }else if(linkTag.title === "theme-gs-kr" && platform === 'gs-kr' && !dark){
            linkTag.disabled = false;
          }else if(linkTag.title === "theme-gs-kr-dark" && platform === 'gs-kr' && dark){
            linkTag.disabled = false;
          }
        }
      }

      document.body.style.opacity = 1;
    },
    platformRestrictions: function(platform) {
      var restrictClass = null;
      // Iterate platforms and apply restrictions via restriction class
      for (let platName of CONST.PLATFORMS) {
        restrictClass = '.plat-'+platName;
        for (let it of document.querySelectorAll(restrictClass)) {
          //console.log('## BEFORE [%s]: %s', platform, it.style.display);
          //it.style.display = this.platform == platName ? 'none' : 'block';
          if (this.platform == platName) {
            if (it.style.display != 'none') it.style.setProperty('display', 'none');
          } else {
            if (it.style.display != 'none') it.style.removeProperty('display');
          }
        }
      }
    },
    displayLogo: function(platform) {
      var logoLoginFile = CONST.LOGO_LOGIN_FILE[platform] ||
          CONST.LOGO_LOGIN_FILE.generic,
        logoFile = CONST.LOGO_FILE[platform] ||
          CONST.LOGO_FILE.generic;

      const logoLoginImg = $('#logoLoginImg');

      if(logoLoginImg.length > 0){
        logoLoginImg.attr('src', logoLoginFile);
        logoLoginImg[0].style.opacity = 1;
      }

      $('#logoImg').attr('src',logoFile);
      $('#logoImgDark').attr('src',CONST.LOGO_FILE_DARK[platform]);
      //$('#logoImg').show(); 
      $('#logoImgTopBar').attr('src',logoFile);
      $('#logoImgDarkTopBar').attr('src',CONST.LOGO_FILE_DARK[platform]);
      //$('#logoImgTopBar').show();
    },
    setCopyright: function(platform) {
      var cr = CONST.LOGIN_COPYRIGHT[platform].copyright ||
          CONST.LOGIN_COPYRIGHT.generic.copyright,
        link = CONST.LOGIN_COPYRIGHT[platform].link ||
          CONST.LOGIN_COPYRIGHT.generic.link;

          CONST.LOGO_FILE.generic;

      const loginCopyright = $('#loginCopyright');
      if(loginCopyright.length === 0){
        return;
      }

      $('#loginCopyright')[0].href = link;
      $('#loginCopyright span').text(cr);
    },
    onConnected: function(e) {
      this.platform = e.ConnectResponse.platform;
      this.switchStyle(this.platform);
      this.displayLogo(this.platform);
      this.setCopyright(this.platform);
      // Platform restrictions must execute before funtionality restrictions
      this.platformRestrictions(this.platform);

      // Set hostname
      if (e.ConnectResponse.host != 'unknown') {
        this.remoteHostname = e.ConnectResponse.host.slice(0, -1);
        document.title = this.remoteHostname;
        $('#loginHostname').text(this.remoteHostname);
        $('#varHostname').text(this.remoteHostname);
      }
      // Set user
      $('#varUsername').text(e.ConnectResponse.user);

      if (e.ConnectResponse.uaa) {
        this.localAddr = e.ConnectResponse.uaa;
      }

      if (e.ConnectResponse.newSession) {
        // New session
        if (e.ConnectResponse.code !== 0) {
          this.setAlertMsg(e.ConnectResponse.reason);
        }
        views.setNext(window.location.hash);
        navigateToLogin();
      } else {
        // Recover user from last unexpired login
        this.user = e.ConnectResponse.user;
        ifCl.execute('show license');
        if (window.setUsername) window.setUsername(this.user);
        functionality.execute();
        views.apply();
        configureInactivityTimeout();
      }

      if(this.platform === 'generic'){
        $('.menu .list a').removeClass('waves-effect waves-block')
      }else if(this.platform === 'cambium'){
        document.body.classList.add('cambium');
      }

      this.applyTheme();


      //ssh2PeriodicTest(); // Used to test resource leaks in ssh2 CLI command executions
      //getFileTest(); // Used to test GetFile
    },
    onUnavailable: function(e) {
      this.setAlertMsg('BQN unavailable');
      navigateToLogin();
    },
    doLogin: function(url, params) {
      var that = this,
        passwd = $('#loginPasswd').val().trim();
      this.user = $('#loginUser').val().trim();
      // ifCl exists at this point
      CONST.LOCAL_STORAGE_TOKEN_NAME = ifCl.getSessionTokenName();
      const user = this.user;


      if (this.user && this.user.length) {
        $('#varUsername').text(this.user);
        ifCl.login(this.user, passwd, function(res) {
          if (res.LoginResponse && res.LoginResponse.code === 0) {
            sessionStorage.setItem(CONST.LOCAL_STORAGE_TOKEN_NAME,
              res.LoginResponse.token);

            if (window.PasswordCredential) {
              const credentials = new window.PasswordCredential({
                id: user,
                password:passwd,
                name: user,
              });
              navigator.credentials.store(credentials);
            }

            eula.check(function() {
              ifCl.logout(function() {
                // Do nothing
              });
              navigateToLogin();
            });
            ifCl.execute('show license');
            if (window.setUsername)
              window.setUsername(user);
            functionality.execute().then(() => {
              globalNavigate(url ?? 'viewDashboard', params)
            });
            //views.apply();
            // Clear alert message (not really necessary)
            $('#loginAlert').html("&nbsp;");
            configureInactivityTimeout();
          } else {
            const additionalMessage = that.platform == 'gs-kr'?'.<br>More than 5 consecutive failures may block access for 5 minutes.':''
            that.setAlertMsg(res.LoginResponse.reason + additionalMessage);
          }
        });
      }
    },
    isLogged: function() {
      return sessionStorage.getItem(CONST.LOCAL_STORAGE_TOKEN_NAME) !== null;
    },
    doLogout: function() {
      ifCl.logout(function() {
        // Do nothing
      });
      sessionStorage.removeItem(CONST.LOCAL_STORAGE_TOKEN_NAME);
      navigateToLogin();
    },
    setAlertMsg: function(msg) {
      $('#loginAlert').html(msg); 
    },

    applyTheme: function(){
      const darkEnabledStored = localStorage.getItem('darkEnabled')
      if(darkEnabledStored === 'true' || darkEnabledStored === null){
        $('#darkModeSwitch').attr('checked', true)
        document.body.classList.add('dark')
        this.theme = "dark"
      }else{
        $('#darkModeSwitch').attr('checked', false)
        document.body.classList.remove('dark')
        this.theme = "light"
      }

      $('#darkModeSwitch').change(() => {
        if ($('#darkModeSwitch').is(':checked')) {
          localStorage.setItem('darkEnabled', "true");
        } else {
          localStorage.setItem('darkEnabled', "false");
        }
        location.reload();
      });
    }
  }

  return Login;
})();


const userAndProfile = (row) => {
  const [name, profile] = row.trim(' ').split(/\s+/);
  return [name, profile];
}
const findUserProfile = user =>
  input => {
    const [_head, ...rows] = input.trim('\n').split('\n');
    const [_name, profile] = rows.map(userAndProfile).find( ([name, _profile]) => user === name ) || [];
    return profile || 'unknown';
  }

const getProfile = () =>
  ifCl.run('show users').then(findUserProfile(login.user))

const getPktEngSettings = (profile) =>
  ifCl.run('show pkteng parameters').then( response =>  {
    const [_all, hideRateStr] = response.match(/Hide policy rate details for operator: (yes|no)/) || [null, null];
    return {
      profile,
      hideRates: profile === 'operator' && hideRateStr === 'yes',
    }
  });

const getProfileSettings = () =>
  getProfile().then( profile => getPktEngSettings(profile))
//
// Functionality
//
var Functionality = (function() {
  function Functionality() {
    var that = this;

    this.functionalities = [];
    this.__profileReady = new Promise(
      (resolve) => {
        that.__solveProfileReady = resolve;
      }
    );
  };
  Functionality.prototype = {
    resetData: function() {
      this.functionalities = [];
    },
    isEnabled: function(feature) {
      const item = this.functionalities.find(it => it.name == feature);
      return item && item.active;
    },
    enable: function() {
      var showClass = null,
        isRateLimitEnabled = this.isEnabled('rate-limit');
      // Iterate platforms and apply restrictions via restriction class
      for (let f of this.functionalities) {
        window.showFClass = '.f-'+f.name;
        //console.log('FUNCTIONALITIES-ITERATING: %s, %s, %s',  f.name, f.active, showFClass);
        for (let it of document.querySelectorAll(showFClass)) {
          //it.style.display = (f.active ? 'block' : 'none');
          //console.log('## BEFORE [%s]: %s', f.name, it.style.display);
          if (f.active) {
            if (it.style.display != 'none') it.style.removeProperty('display');
          } else {
            if (it.style.display != 'none') it.style.setProperty('display', 'none');
          }
          //console.log('## AFTER [%s]: %s', f.name, it.style.display);

          // property visibility do not remove the placeholder (space)
          //it.style.visibility = (f.active ? 'visible' : 'hidden');
        }
      }

      // Exceptions
      if (!isRateLimitEnabled) {
        $('#menuPoliciesUseStatsSubs').text('Total');
        $('#policiesUseStatsSubsTitle').text('Subscribers Total Over Time');
        $('#policiesUseStatsSubsHelpTitle').text('Subscribers Total Over Time');
      }
    },
    dump: function(msg) {
      var item;
      if (msg) console.log(msg);
      for (let f of this.functionalities) {
        console.log('  %s, %s', f.name, f.active);
      }
    },
    isProfile: function(profile) { return this.__profile === profile; },
    onProfileReady: function(callback) {
      return typeof callback === 'function'
        ? this.__profileReady.then(callback)
        : this.__profileReady;},
    setProfile: function() {
      const that = this;
      const applyProfileHideRatesSettings = ({profile, hideRates=false}) => {
        const target = document.body.classList;
        const className =  'profile-operator-with-hidden-rate';
        hideRates ? target.add(className) : target.remove(className);
      }
      this.__profileReady = new Promise((resolve) => {
        return getProfile().then(profile => {
          const target = document.body.classList;
          target.add(`profile-${profile}`)
          that.__profile = profile;
          functionality.__solveProfileReady(profile);
          return profile
        }).then(getProfileSettings).then(applyProfileHideRatesSettings);
      })
    },
    execute: function() {
      var that = this;

      this.resetData();

      return ifCl.run('show system functionality').then(function(resp) {
        // REMOVE
        //resp.CmdResponse.response = 'compression:    enabled\ndpi: enabled\ndpi-per-user:   disabled\nrate-limit:   enabled\n';
        parseStrTable(resp, function(attrs) {
          that.functionalities.push({name: attrs[0].split(':')[0],
            active: (attrs[1].indexOf('enabled') > -1 ? true : false)});
        }, false);
        that.dump('FUNCTIONALITIES:');
        that.enable();
      }).then((d) => that.setProfile(d));

    },
  };

  return Functionality;
})();


//
// IpV4
//
var IpV4 = (function() {
  var _decode = function(s) {
    return s.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  };

  var _decodeCidr = function(s) {
    return s.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/);
  };

  var _isValid = function(items) {
    return items != null &&
      items[1] <= 255 && items[2] <= 255 &&
      items[3] <= 255 && items[4] <= 255;
  };

  var _isValidCidr = function(items) {
    return items != null &&
      items[1] <= 255 && items[2] <= 255 &&
      items[3] <= 255 && items[4] <= 255 &&
      items[5] <= 32;
  };

  var _parse = function(items) {
    // Validate arguments
    //console.log('_parse.items[%d] %s', items.length, items);
    if (items.length == 5) {
      if (!_isValid(items)) return null;
    } else {
      if (!_isValidCidr(items)) return null;
    }
    var octStr = items.slice(1), // Remove 1rst item (the original address)
      i, len,
      octets = [];
    for (i = 0, len = octStr.length; i < len; i++) {
      octets.push(parseInt(octStr[i]));
    }
    return octets;
  };

  var _maskFromPrefix = function(prefixLen) {
    var idx = 0,
      octets = [0, 0, 0, 0],
      byteMaskValue = [0, 128, 192, 224, 240, 248, 252, 254, 255];

      idx = Math.floor(prefixLen / 8);
      for (var i = 0; i < idx; i++) {
        octets[i] = 255;
        prefixLen -= 8;
      }
      octets[idx] = byteMaskValue[prefixLen];
      return octets;
  };

  var _normalizeCidr = function(items) {
    // Validate arguments
    //console.log('_parse.items[%d] %s', items.length, items);
    if (items.length == 5) {
      return null;
    } else {
      if (!_isValidCidr(items)) return null;
    }
    var octets = [0, 0, 0, 0, 0],
      prefix = items[5],
      mask = _maskFromPrefix(prefix);
    for (var i = 0; i < 4; i++) {
      octets[i] = items[i+1] & mask[i];
    }
    octets[i] = prefix;
    return octets;
  };

  var _public = {
    isValid: function(s) {
      return _isValid(_decode(s));
    },
    isValidCidr: function(s) {
      return _isValidCidr(_decodeCidr(s));
    },
    isValidCidrOrAddr: function(s) {
      return (_isValidCidr(_decodeCidr(s)) || _isValid(_decode(s)));
    },
    parse: function(s) {
      return _parse(_decode(s));
    },
    parseCidr: function(s) {
      return _parse(_decodeCidr(s));
    },
    normalizeCidr: function(s) {
      if (s.indexOf('/') < 0) return s+'/32';
      var normalized = _normalizeCidr(_decodeCidr(s));
      return (normalized.slice(0, 4).join('.') + '/' + normalized[4]);
    },
    toCidr: function(s) {
      if (_decode(s)) {
        return s+'/32';
      } else if (_decodeCidr(s)) {
        return s;
      } else {
        return null;
      }
    },
    match: function(addrStr, rangeStr) {
      // Optimized for preformance
      var items = _decode(addrStr),
        itemsCidr = _decodeCidr(rangeStr);
      // If addrStr is a cidr normalize it
      if (items === null) {
        items = _decodeCidr(addrStr);
        if (_isValidCidr(items) && items[5] == 32) {
          items.splice(5, 1);
        } else {
          return null;
        }
      }
      if (!_isValid(items) || !_isValidCidr(itemsCidr)) {
        return false;
      }
      var addr = _parse(items),
        cidr = _parse(itemsCidr),
        cidrBits = cidr[4],
        i, shift;

      for (i = 0; cidrBits > 0; i++) {
        if (cidrBits >= 8) {
          //console.log('Byte: %d, %d', addr[i], cidr[i]);
          if (addr[i] != cidr[i]) return false;
        } else {
          shift = 8 - cidrBits;
          //console.log('Byte: %d:%d, %d:%d', addr[i], shift, cidr[i], shift);
          if ((addr[i] >> shift) != (cidr[i] >> shift)) return false;
        }
        cidrBits -= 8;
      }
      return true;
    },
    isSubnetOf: function(rangeA, rangeB) {
      // Optimized for clarity
      var itemsA = _decodeCidr(rangeA),
        itemsB = _decodeCidr(rangeB);
      if (!_isValidCidr(itemsA) || !_isValidCidr(itemsB)) {
        return false;
      }
      var cidrA = _parse(itemsA),
        cidrB = _parse(itemsB),
        cidrABits = cidrA[4],
        cidrBBits = cidrB[4],
        i, j, mask;

      for (i = 0; i < 4; i++) {
          for (j = 0, mask= 0x80; j < 8; j++, mask >>= 1) {
            if ((cidrA[i] & mask) != (cidrB[i] & mask)) return false;
            cidrABits--;
            cidrBBits--;
            if (cidrBBits === 0) return true;
            if (cidrABits === 0) return false;
          }
      }
      return true;
    }
  };

  return _public;
})();

//
// IpV6
//
var IpV6 = (function() {
  let CONST = {
    ST_INIT: 'INI',
    ST_PRE_NUM: 'pN',
    ST_PRE_COL: 'pCO',
    ST_HEAD_DCOL: 'HDC',
    ST_EMB_DCOL: 'EDC',
    ST_POST_NUM: 'PN',
    ST_POST_COL: 'PCO',
    ST_SLASH: 'SL',
    ST_CIDR: 'CIDR',
    ST_ERROR: 'ERR',
    ST_ADDR_OK: 'ADDR_OK',
    ST_CIDR_OK: 'CIDR_OK',
  };

  function Scanner() {
    this.str;
    this.idx;
    this.length;
    this.c;
  };
  Scanner.prototype = {
    init: function(str) {
      this.str = str;
      this.idx = 0;
      this.length = str.length;

      this.COLON = ':';
      this.SLASH = '/';
      this.HEX_CHAR_L = '0123456789abcdef';
      this.HEX_CHAR_U = '0123456789ABCDEF';
      this.TK_SLASH = 'SL';
      this.TK_SINGLE_COLON = 'SC';
      this.TK_DOUBLE_COLON = 'DC';
      this.TK_HEX_NUMBER = 'N';
      this.TK_EOF = 'EOF';
      this.TK_ERROR = 'ERR';

      this.c = this.getChar();
    },
    getChar: function() {
      if (this.idx ==  this.length) return null; // EOF
      else return this.str.charAt(this.idx++);
    },
    isHexChar: function(c) {
      return (this.HEX_CHAR_L.indexOf(c) > -1 || this.HEX_CHAR_U.indexOf(c) > -1);
    },
    token: function(type, value) {
      return {type: type, value: value};
    },
    getToken: function() {
      //console.log('idx = %d, len = %d', this.idx, this.length);
      if (this.c ===  null) {
        return this.token(this.TK_EOF, null);
      } else if (this.c == this.SLASH) {
        this.c = this.getChar();
        return this.token(this.TK_SLASH, null);
      } else if (this.c == this.COLON) {
        this.c = this.getChar();
        if (this.c == this.COLON) {
          this.c = this.getChar();
          return this.token(this.TK_DOUBLE_COLON, null);
        } else {
          return this.token(this.TK_SINGLE_COLON, null);
        }
      } else if (this.isHexChar(this.c)) {
        var number = this.c;
        while (this.isHexChar(this.c = this.getChar())) {
          number += this.c;
        }
        if (number.length > 4) {
          return this.token(this.TK_ERROR, 'invalid number '+number);
        } else {
          return this.token(this.TK_HEX_NUMBER, number);
        }
      } else {
          return this.token(this.TK_ERROR, 'unexpected char '+this.c);
      }
    },
  };

  var contractNumber = function(value) {
    var i = 0, len = value.length - 1;
    while (value.charAt(i) == '0' && i < len) i++;
    return value.substr(i);
  };
  var expandNumber = function(value) {
    var exp = value;
    for (var i = 4 - value.length; i; i--) {
      exp = '0' + exp;
    }
    return exp;
  };
  var parseResult = function(status, errorMsg, items, numCnt, prefix) {
    return {
      status: status,
      errorMsg: errorMsg,
      items: items,
      numCnt: numCnt,
      prefix: prefix,
    };
  };
  var expanded = function(itCnt, items) {
    var exp = [];
    for (var j = 0, len = items.length; j < len; j++) {
      if (items[j] == '*') {
        for (var k = 8 - itCnt; k > 0; k--) exp.push('0');
      } else {
        exp.push(items[j]);
      }
    }
    return exp;
  };
  var full = function(itCnt, items) {
    var exp = [];
    for (var j = 0, len = items.length; j < len; j++) {
      if (items[j] == '*') {
        for (var k = 8 - itCnt; k > 0; k--) exp.push('0000');
      } else {
        exp.push(expandNumber(items[j]));
      }
    }
    return exp;
  };
  var canonical = function(expandedItems) {
    var zeroCnt = [0,0,0,0,0,0,0,0],
      zeroIdx = null,
      can = [],
      canStr = '';
    // Count successive 0 in zeroCnt
    for (var j = 0; j < 8; j++) {
      if (expandedItems[j] == 0) {
        if (zeroIdx === null) { zeroIdx = j; }
        zeroCnt[zeroIdx] += 1;
      } else {
        zeroIdx = null
      }
    }
    //console.log('ZEROES: %s', JSON.stringify(zeroCnt));
    // Get max 0 sequence index in zeroIdx
    zeroIdx = null;
    for (var j = 0; j < 8; j++) {
      if (zeroCnt[j] > 1) {
        if (zeroIdx === null) zeroIdx = j;
        else if (zeroCnt[j] > zeroCnt[zeroIdx]) zeroIdx = j;
      }
    }
    //console.log('MAX-ZEROES: %d', zeroIdx);
    // Contract max 0 sequence using '::'
    for (var j = 0; j < 8; j++) {
      if (j == zeroIdx) {
        can.push('*');
        while (--zeroCnt[zeroIdx]) j++;
      } else {
        can.push(expandedItems[j]);
      }
    }
    //console.log('CANONICAL: %s', can);
    // Get string from items
    for (var j = 0, len = can.length; j < len; j++) {
      if (j == 0) canStr += (can[j] == '*' ? '::' : can[j]+':');
      else if (j == len-1) canStr += (can[j] == '*' ? ':' : can[j]);
      else canStr += (can[j] == '*' ? ':' : can[j]+':');
    }
    return canStr;
  };

  var scanner = new Scanner();

  var _parse = function(s) {
    var st = CONST.ST_INIT,
      tk = null,
      numCnt = 0,
      prefix = null,
      items = [];

    scanner.init(s);
    do {
      tk = scanner.getToken();
      //console.log('ST: %s, TK: %s', st, JSON.stringify(tk));
      switch (st) {
        case CONST.ST_INIT:
          if (tk.type == 'N') {
            numCnt++;
            items.push(contractNumber(tk.value))
            st = CONST.ST_PRE_NUM;
          } else if (tk.type == 'DC') {
            items.push('*')
            st = CONST.ST_HEAD_DCOL;
          } else st = CONST.ST_ERROR;
          break;
        case CONST.ST_PRE_NUM:
          if (tk.type == 'SC') {
            st = CONST.ST_PRE_COL;
          } else if (tk.type == 'DC') {
            items.push('*');
            st = CONST.ST_EMB_DCOL;
          } else if (tk.type == 'EOF') {
            if (numCnt == 8) st = CONST.ST_ADDR_OK;
            else st = CONST.ST_ERROR; // Incomplete addr
          } else if (tk.type == 'SL') {
            if (numCnt == 8) st = CONST.ST_SLASH;
          } else st = CONST.ST_ERROR;
          break;
        case CONST.ST_PRE_COL:
          if (tk.type == 'N') {
            numCnt++;
            items.push(contractNumber(tk.value));
            st = CONST.ST_PRE_NUM;
          } else st = CONST.ST_ERROR;
          break;
        case CONST.ST_HEAD_DCOL:
          if (tk.type == 'N') {
            numCnt++;
            items.push(contractNumber(tk.value));
            st = CONST.ST_POST_NUM;
          } else if (tk.type == 'EOF') st = CONST.ST_ADDR_OK; // '::'
          else if (tk.type == 'SL') st = CONST.ST_SLASH;
          else st = CONST.ST_ERROR;
          break;
        case CONST.ST_EMB_DCOL:
          if (tk.type == 'N') {
            numCnt++;
            items.push(contractNumber(tk.value));
            st = CONST.ST_POST_NUM;
          } else if (tk.type == 'EOF') st = CONST.ST_ADDR_OK; // '...::'
          else if (tk.type == 'SL') st = CONST.ST_SLASH;
          else st = CONST.ST_ERROR;
          break;
        case CONST.ST_POST_NUM:
          if (tk.type == 'SC') {
            st = CONST.ST_POST_COL;
          } else if (tk.type == 'EOF') st = CONST.ST_ADDR_OK; // '::...'
          else if (tk.type == 'SL') st = CONST.ST_SLASH;
          else st = CONST.ST_ERROR;
          break;
        case CONST.ST_POST_COL:
          if (tk.type == 'N') {
            numCnt++;
            items.push(contractNumber(tk.value));
            st = CONST.ST_POST_NUM;
          } else st = CONST.ST_ERROR;
          break;
        case CONST.ST_SLASH:
          if (tk.type == 'N') {
            prefix = tk.value;
            if (Number(prefix) <= 128) st = CONST.ST_CIDR;
            else st = CONST.ST_ERROR;
          } else st = CONST.ST_ERROR;
          break;
        case CONST.ST_CIDR:
          if (tk.type == 'EOF') st = CONST.ST_CIDR_OK; // CIDR
          else st = CONST.ST_ERROR;
          break;
        default:
          console.log('SCANNER: unknown status <%s>', st);
          CONST.ST_ERROR;
          break;
      }
    } while (tk.type != 'EOF' && tk.type != 'ERR');
    // Stop condition may be based on status too
    //console.log('END ST: %s, TK: %s, N: %d', st, JSON.stringify(tk), numCnt);
    //console.log('  ADR: %s', s);
    //console.log('  IDX: %s', items);
    //console.log('  EXP: %s', expanded(numCnt, items));
    //console.log('  FUL: %s', full(numCnt, items));
    //console.log('  CAN: %s', canonical(expanded(numCnt, items)));

    return parseResult (st, null, items, numCnt, prefix);
  };

  var _public = {
    isValid: function(s) {
      return (_parse(s).status == CONST.ST_ADDR_OK);
    },
    isValidCidr: function(s) {
      return (_parse(s).status == CONST.ST_CIDR_OK);
    },
    isValidCidrOrAddr: function(s) {
      var ipv6 = _parse(s);
      return (ipv6.status == CONST.ST_CIDR_OK ||
        ipv6.status == CONST.ST_ADDR_OK);
    },
    parse: function(s) {
      return _parse(s);
    },
    normalizeCidr: function(s) {
      if (s.indexOf('/') < 0) return s+'/128';
    },
    toCidr: function(s) {
    },
    canonical: function(s) {
      var ipv6 = _parse(s);
      return canonical(expanded(ipv6.numCnt, ipv6.items));
    },
    expanded: function(s) {
      var ipv6 = _parse(s);
      return expanded(ipv6.numCnt, ipv6.items);
    },
    fullExpanded: function(s) {
      var ipv6 = _parse(s);
      return full(ipv6.numCnt, ipv6.items);
    },
  };

  return _public;
})();

//
// IpV4V6
//
var IpV4V6 = (function() {
  var _public = {
    isValid: function(s) {
      return (IpV4.isValid(s) || IpV6.isValid(s));
    },
    isValidCidr: function(s) {
      return (IpV4.isValidCidr(s) || IpV6.isValidCidr(s));
    },
    isValidCidrOrAddr: function(s) {
      return (IpV4.isValidCidrOrAddr(s) || IpV6.isValidCidrOrAddr(s));
    },
    normalizeCidr: function(s) {
      if (IpV4.isValid(s)) {
        return IpV4.normalizeCidr(s);
      } else if (IpV6.isValid(s)) {
        return IpV6.normalizeCidr(s);
      } else {
        return s;
      }
    },
  };

  return _public;
})();


//
// SequentialCmd
//
var SequentialCmd = (function() {
  var executeCmdPromise = function(cmd) {
    return new Promise(function(resolve, reject) {
      ifCl.execute(cmd, function(res) {
        if (res.CmdResponse.response.indexOf('%ERR') > -1) {
          reject(res.CmdResponse.response);
        } else {
          resolve(res.CmdResponse.response);
        }
      });
    });
  };

  var _public = {
    execute: function(cmds, resps, okF, koF) {
      // Sequential execution of CLI commands using promises
      cmds.reduce(function(promise, cmd, i) {
        return promise.then(function(response) {
          // First 'undefined' response corresponds to the explicitly resolved 'first' promise
          if (resps) resps[i-1] = response;
          return executeCmdPromise(cmd);
        });
      }, Promise.resolve()).then(function(response) {
        // End of sequence (last promise)
        //console.log('RESPONSE: %s', response);
        var len = cmds.length - 1;
        if (resps) resps[len] = response;
        if (okF) okF(len);
      }).catch(function(error) {
        //console.log('ERROR: %s', error);
        if (koF) koF(error);
      });
    },
    executePromise: function(cmd) {
      return executeCmdPromise(cmd);
    },
  };

  return _public;
})();


//
// SequentialInteraction
//
var SequentialInteractions = (function() {
  var executeCmdPromise = function(cmd) {
    return new Promise(function(resolve, reject) {
      ifCl.execute(cmd, function(res) {
        if (res.CmdResponse.response.indexOf('%ERR') > -1) {
          reject(res.CmdResponse.response);
        } else {
          resolve(res.CmdResponse.response);
        }
      });
    });
  },
  setOutput = function(interaction, response) {
    var values = interaction.filter(response);
    if (Array.isArray(values)) {
      for (var i = 0, len = interaction.jqSel.length; i < len; i++) {
        $(interaction.jqSel[i]).text(values[i]);
      }
    } else {
      $(interaction.jqSel).text(values);
    }
  };

  var _public = {
    execute: function(interactions, okF, koF) {
      // Sequential execution of CLI commands using promises
      interactions.reduce(function(promise, interaction, i) {
        return promise.then(function(response) {
          // First 'undefined' response corresponds to the explicitly resolved 'first' promise
          // Process response of previous command
          if (response) {
            setOutput(interactions[i-1], response);
          }

          return executeCmdPromise(interaction.cmd);
        });
      }, Promise.resolve()).then(function(response) {
        // End of sequence (last promise)
        //console.log('RESPONSE: %s', response);
        var lastIdx = interactions.length - 1;
        setOutput(interactions[lastIdx], response);
        if (okF) okF(lastIdx);
      }).catch(function(error) {
        //console.log('ERROR: %s', error);
        if (koF) koF(error);
      });
    },
  };

  return _public;
})();

//
// FileInput
//
var FileInput = (function() {
  function FileInput(inputName, btnNames, eofCb) {
    var that = this;

    this.eofCb = eofCb;
    this.input = $('#'+inputName);
    this.btnName = null;
    for (var i = 0; i < btnNames.length; i++) {
      $('#'+btnNames[i]).click(function() {
        // Set current button nkame
        that.btnName = this.id;
        // Reset value
        that.input.val('');
        // Start FileInput
        that.input.click();
      });
    }
    this.input.change(function() {
      that.readFile();
    });
  };
  FileInput.prototype = {
    readFile: function() {
      //console.log('FileInput.reading: %s', file.name);
      var that = this,
        reader = new FileReader(),
        file = this.input.get(0).files[0];

      reader.onload = function(ev) {
        if (ev.target.error) {
          console.log("FileInput.error: " + evt.target.error);
        } else {
          //console.log('FileInput.read: len=%d, max=%d', ev.target.result.length, file.size);
          if (that.eofCb) that.eofCb(ev.target.result, that.btnName);
        }
      };

      reader.readAsText(file);
    },
  };

  return FileInput;
})();


//
// TimeSeries
//
var TimeSeries = (function() {
  var CONST = {
      N_A_VALUE: 'n/a',
    };

  function ColToDatasetMap() {
    this.allDsNames = [];
    this.colsToDs = [];
    this.absentsDs = [];
  };
  ColToDatasetMap.prototype = {
    reset: function() {
      this.allDsNames = [];
      this.colsToDs = [];
      this.absentsDs = [];
    },
    hdrBegin: function() {
      this.colsToDs = [];
      this.absentsDs = [];
    },
    hdrEnd: function() {
      // Compute absentsDs
      for (var i = 0, len = this.allDsNames.length; i < len; i++) {
        if (this.colsToDs.indexOf(i) == -1) {
          this.absentsDs.push(i);
        }
      }
    },
    add: function(name, col) {
      // Returns: NEW column_index, OLD -1, EMPTY -1
      var idx = this.allDsNames.indexOf(name);

      // Check empty header
      if (name.length == 0) return -1;

      // Check non-existence of the header column name
      if (idx < 0) {
        // New column
        idx = this.allDsNames.length; // Last index
        this.allDsNames.push(name);
        this.colsToDs.push(idx);
        //console.log('ADD-NEW: name=%s col=%d ds=%d', name, col, idx);
        return idx;
      } else {
        // Old column
        this.colsToDs.push(idx);
        //console.log('USE-OLD: name=%s col=%d ds=%d', name, col, idx);
        return -1;
      }
    },
    dump: function(title) {
      if (title) console.log('%s', title);
      console.log('    NAMES: %s', JSON.stringify(this.allDsNames));
      console.log('    COLMAP: %s', JSON.stringify(this.colsToDs));
      console.log('    ABSENT: %s', JSON.stringify(this.absentsDs));
    },
  };

  window.TimeSeries = function TimeSeries(useColMap) {
    this.initF = null;
    this.endF = null;
    this.headerF = null;
    this.lineF = null;
    this.gapF = null;
    this.nullDateF = null;

    this.datasets = [];
    this.gapUnits = null;
    this.gapAmount = null;

    this.colMap = null;
    if (useColMap !== undefined && useColMap) {
      this.colMap = new ColToDatasetMap();
    }
  };
  window.TimeSeries.prototype = {
    setInitF: function(initF) {
      this.initF = initF;
    },
    setEndF: function(endF) {
      this.endF = endF;
    },
    setHeaderF: function(headerF) {
      this.headerF = headerF;
    },
    setLineF: function(lineF) {
      this.lineF = lineF;
    },
    setGapF: function(units, amount, gapF) {
      this.gapF = gapF;
      this.gapUnits = units;
      this.gapAmount = amount;
      // 'minutes', 'hours', 'days', ...
    },
    setGapAmount: function(amount) {
      this.gapAmount = amount;
    },
    setNullDateF: function(nullDateF) {
      this.nullDateF = nullDateF;
      // nullDateF must return the number of extra skip lines
    },
    generateGapItems: function(prevDate, currDate) {
      if (this.gapF === null) return;

      var prevMoment = moment(prevDate),
        currMoment = moment(currDate),
        gapCnt = 0;

      //console.log('TimeSeries.generateGapItems: %d\' (%s, %s]',
      //  currMoment.diff(prevMoment, this.gapUnits),
      //  prevMoment.format(), currMoment.format());
      prevMoment.add(this.gapAmount, this.gapUnits); // Previous item exists
      while (prevMoment.isBefore(currMoment)) {
        this.gapF(moment(prevMoment).toDate());
        gapCnt++;
        prevMoment.add(this.gapAmount, this.gapUnits);
      }
      //console.log('TimeSeries.generateGapItems: gapCnt = %d', gapCnt);
    },
    generateItem: function(date, attrs) {
      if (this.lineF === null) return;

      // Check line with DATE and NO-DATA
      if (attrs.length == 1) {
        this.gapF(date);
      } else {
        this.lineF(date, attrs);
      }
    },
    // lines format 'date attr0 ... attrN'
    process: function(period, lines) {
      var parseDate = d3.timeParse('%Y-%m-%dT%H:%M:%S'),
        prevDate = period.minDate(),
        currDate = null,
        attrs = null,
        hdrLinesCnt = 0,
        data = [];

      if (this.colMap) this.colMap.reset;

      // Initialize
      this.datasets = [];
      if (this.initF) this.initF();

      // Total_lines = header_lines + data_lines + last_empty_line
      // Process headers
      if (this.headerF) {
        hdrLinesCnt = this.headerF(lines);
      }

      // Process lines
      for (var i = hdrLinesCnt; i < (lines.length - 1); i++) {
        //console.log('[%d] %s', i, lines[i]);
        attrs = lines[i].split(/\s+/);
        currDate = parseDate(attrs[0]);

        if (currDate !== null) {
          // Bqn CLI may send out of date data
          if (!period.isOutOfDate(currDate)) {
            this.generateGapItems(prevDate, currDate);
            this.generateItem(currDate, attrs);
            prevDate = currDate; // Consume date only when (not null && inside period)
          } else {
            //console.log('TimeSeries.isOutOfDate: CURR:%s / MIN:%s / LINE:%s', moment(currDate).format(), period.minMoment().format(), lines[i]);
          }
        } else if (this.nullDateF !== null) {
          // Probably intermediate headers or summary footers
          var skip = this.nullDateF(i, lines);
          i += (typeof(skip) == 'number' ? skip : 0);
        } else {
          console.log('TimeSeries.process.line %d: Date decode error', i);
        }
      }

      // Generate tail gap items
      this.generateGapItems(prevDate, period.maxDate());

      if (this.endF) this.endF();

      // Concatenate datasets
      for (var i = 0; i < this.datasets.length; i++) {
        data.push(this.datasets[i]);
      }
      //console.log('data.len=%d, datasets.len=%d', data.length, this.datasets[0].storedValues.length);

      return data;
    },
  };

  return TimeSeries;
})();

//
// Id
//
var Id = (function() {
  var CONST = {
    INITIAL_VALUE: 0,
  };

  function Id() {
    this.id = CONST.INITIAL_VALUE;
  };
  Id.prototype = {
    current: function() {
      return this.id;
    },
    next: function() {
      return this.id++;
    },
    nextStr: function() {
      return (this.id++).toString();
    },
  };

  return Id;
})();

//
// Resize
//
var Resize = (function() {
  function Resize(small, large, parameters) {
    var that = this;

    this.small = small;
    this.large = large;
    this.parameters = parameters || {};
    this.registrations =  [];
    this.size = this.update();

    //d3.select(window).on('resize.updatesvg', function() {
    d3.select(window).on('resize', function() {
      var oldSize = that.size,
        newSize = that.update();
      console.log('Resize.size: %d/%d', window.innerWidth, that.size);
      if (oldSize != newSize) {
        for (var i = 0, len = that.registrations.length; i < len; i++) {
          // Callback may be fired in any view !!!
          if (that.registrations[i]) that.registrations[i]();
        }
      }
    });
  };
  Resize.prototype = {
    update: function() {
      // 0:SMALL, 1:MEDIUM, 2:LARGE
      var width = window.innerWidth;
      if (width < this.small) this.size = 0;
      else if (width < this.large) this.size = 1;
      else this.size = 2;

      return this.size;
    },
    register: function(callback) {
      this.registrations.push(callback);
    },
    get: function(name) {
      var par = this.parameters[name];
      return (par ? par[this.size] : null)
    },
    add: function(name, valuesArr) {
      if (valuesArr.length == 3) {
        this.parameters[name] = valuesArr;
      }
    },
  };

  return Resize;
})();

//
// Dirty
//
var Dirty = (function() {
  function Dirty(showName, labelName) {
    // labelName is used to disable 'pointer cursor' from false radio button
    if (labelName === undefined) {
      labelName = showName.slice(0, showName.lastIndexOf('Show'));
      //console.log('Dirty.labelName: %s', labelName);
    }
    this.jqSel = $(showName);
    this.showCb = null;
    this.hideCb = null;
    // Set cursor to labelName (dirty message)
    $(labelName).css('cursor', 'default');
  };
  Dirty.prototype = {
    addShowCb: function(showCb) {
      this.showCb = showCb;
    },
    addHideCb: function(hideCb) {
      this.hideCb = hideCb;
    },
    is: function() {
      return this.jqSel.is(':visible');
    },
    show: function() {
      if (this.showCb) this.showCb();
      this.jqSel.show();
    },
    hide: function() {
      if (this.hideCb) this.hideCb();
      this.jqSel.hide();
    },
  };

  return Dirty;
})();

//
// NextView
//
var NextView = (function() {
  function NextView(defaultView) {
    this.nextView = null;
    this.defaultView = defaultView;
    this.nextParams = null;
  };
  NextView.prototype = {
    reset: function() {
      //console.log('!!!!!!!!!! NextView.reset');
      this.nextView = null;
    },
    set: function(nextView, nextParams) {
      //console.log('!!!!!!!!!! NextView.set %s', nextView);
      this.nextView = (nextView == null ? null : nextView);
      this.nextParams = (nextParams == null ? null : nextParams);
    },
    get: function(nextView) {
      return this.nextView;
    },
    isSet: function() {
      return (this.nextView == null ? false : true);
    },
    go: function() {
      if (this.nextView && this.nextParams) {
        globalNavigate(this.nextView, this.nextParams, false);
        this.nextView = null;
        this.nextParams = null;
      } else if (this.nextView) {
        //console.log('!!!!!!!!!! NextView.go (stored) %s', this.nextView);
        globalNavigate(this.nextView);
        this.nextView = null;
      } else {
        //console.log('!!!!!!!!!! NextView.go (default) %s', this.defaultView);
        globalNavigate(this.defaultView);
      }
    },
  };

  return NextView;
})();

//
// Differential Commands
//
function pushDifferentialCommands(cmd, ini, fin, properties,
  removeItemF, addItemF, uEquals) {
  // cmd: array of commands (output)
  // ini: inital array of objects with properties
  // fin: final array of objects with properties
  // properties: properties to be tested (null if uEquals is defined)
  // removeItemF: function(ini[i])
  // addItemF: function(fin[i])
  // uEquals: user defined equals function(iniItem, finItem). OPTIONAL
  // IMPORTANT! duplicates are filtered
  var iniLen = ini.length,
    finLen = fin.length,
    itemIni = null,
    itemFin = null,
    remCmd = null,
    addCmd = null,
    defEquals = function(iniItem, finItem) {
      for (var k = 0; k < properties.length; k++) {
        if (iniItem[properties[k]] != finItem[properties[k]]) return false;
      }
      return true;
    };

  if (uEquals === undefined || uEquals === null) {
    equals = defEquals;
  } else {
    equals = uEquals;
  }

  // Add to CLI commands array

  // Check removed items
  for (var i = 0; i < iniLen; i++) {
    itemIni = ini[i];
    for (var j = 0; j < finLen; j++) {
      itemFin = fin[j];
      if (equals(itemIni, itemFin)) break;
    }
    if (j == finLen && removeItemF) {
      // Check duplicates
      remCmd = removeItemF(ini[i]);
      if (cmd.indexOf(remCmd) < 0) cmd.push(remCmd);
    }
  }

  // Check added items
  for (var i = 0; i < finLen; i++) {
    itemFin = fin[i];
    for (var j = 0; j < iniLen; j++) {
      itemIni = ini[j];
      if (equals(itemIni, itemFin)) break;
    }
    if (j == iniLen && addItemF) {
      // Check duplicates
      addCmd = addItemF(fin[i]);
      if (cmd.indexOf(addCmd) < 0) cmd.push(addCmd);
    }
  }
  return cmd;
};

//
// Differential Repetitive-Groups Commands
//
function pushDifferentialRgCommands(cmds, ini, fin, groupProps, itemProps,
  removeItemF, addItemF) {
  // cmds: array of commands (output)
  // ini: inital array of objects with properties
  // fin: final array of objects with properties
  // groupProps: item properties to be tested in isEqual function
  // itemProps: group properties to be tested in isEqual function
  // removeItemF: function(ini[i])
  // addItemF: function(fin[i])
  // IMPORTANT! duplicates are filtered
  var iniLen = ini.length,
    finLen = fin.length,
    cmd = null,
    itEquals = function(iniItem, finItem) {
      for (var k = 0; k < itemProps.length; k++) {
        if (iniItem[itemProps[k]] != finItem[itemProps[k]]) return false;
      }
      return true;
    },
    grEquals = function(iniItem, finItem) {
      for (var k = 0; k < groupProps.length; k++) {
        if (iniItem[groupProps[k]] != finItem[groupProps[k]]) return false;
      }
      return true;
    };

  // Add to CLI commands array

  // Check removed items
  for (var i = 0; i < iniLen; i++) {
    var itemIni = ini[i],
      found = {gr: false, it: false};

    found = fin.reduce(function(found, it) {
      found.gr = (found.gr ? found.gr : grEquals(itemIni, it));
      found.it = (found.it ? found.it : itEquals(itemIni, it));
      return found;
    }, found)

    if (removeItemF && !found.it) {
      //console.log('REMOVED-IT: found=%s, ini_it=%s', JSON.stringify(found), JSON.stringify(itemIni));
      if (!found.gr) {
        // Remove group
        cmd = removeItemF(ini[i]);
        // Check duplicates
        if (cmds.indexOf(cmd) < 0) cmds.push(cmd);
      } else {
        // Recreate group excluding item.
        cmd = addItemF(ini[i]);
        // Check duplicates
        if (cmds.indexOf(cmd) < 0) cmds.push(cmd);
      }
    }
  }

  // Check added items
  for (var i = 0; i < finLen; i++) {
    var itemFin = fin[i],
      found = {gr: false, it: false};

    found = ini.reduce(function(found, it) {
      found.gr = (found.gr ? found.gr : grEquals(itemFin, it));
      found.it = (found.it ? found.it : itEquals(itemFin, it));
      return found;
    }, found)

    if (addItemF && !found.it) {
      //console.log('ADDED-IT: found=%s, end_it=%s', JSON.stringify(found), JSON.stringify(itemFin));
      // Check duplicates
      cmd = addItemF(fin[i]);
      if (cmds.indexOf(cmd) < 0) cmds.push(cmd);
    }
  }
  return cmds;
};

//
// capitalize
//
function capitalize(str) {
  return str.charAt(0).toUpperCase()+str.slice(1);
};

//
// isInteger
//
function isInteger(intStr) {
  return (intStr.match(/^\d+$/g) !== null);
};

//
// isSignedInteger
//
function isSignedInteger(intStr) {
  return (intStr.match(/^[-]?\d+$/g) !== null);
};

// isNameValid
//
function isNameValid(str, maxLen) {
  var len = str.length,
    invalidChars = ' ?<>!$%&()\'[]*+';
  if (len == 0) return false;
  if (maxLen !== undefined && maxLen && len > maxLen) return false;
  /*for (var i = len -1; i >= 0; i--) {
    if (invalidChars.indexOf(str.charAt(i)) >= 0) return false;
  }*/
  return true;
};

//
// isPolicyNameValid
//
function isPolicyNameValid(str) {
  if ((str == 'flow') || (str == 'monitor') || (str == 'rate') || (str == 'subscriber')) {
    return false;
  } else {
    return isNameValid(str, POLICY_LENGTH_MAX);
  }
};

//
// isProfileNameValid
//
function isProfileNameValid(str) {
  return isNameValid(str, PROFILE_LENGTH_MAX);
};

//
// removeQuotes
//
function removeQuotes(str) {
  return (str.charAt(0) == '"' ? str.substring(1, str.length - 1) : str);
};

//
// formatLargeStrNum
//
function formatLargeStrNum(valueStr, precision) {
  return Number(valueStr).toLocaleString('en-US',
    { maximumFractionDigits: precision });
}

//
// formatLargeNum
//
function formatLargeNum(valueNum, precision) {
  return valueNum.toLocaleString('en-US',
    { maximumFractionDigits: precision });
}

//
// DataTable
//
function isDataTableInit(tableId) {
  if (typeof DataTable !== 'undefined' && jQuery.isFunction(DataTable))
    return ($.fn.DataTable.isDataTable(tableId) ? true : false );
}

function sumCharCodes(input){
  let output = 0;
  for(let i=0; i<input.length; i++){
    output += input.charCodeAt(i);
  }

  return output;
}

//
// TableRow
//
var TableRow = (function() {
  function TableRow() {
    this.rowId = null;
    this.bodySelector = null;
    this.operations = null;
    this.operationAttribute = '';
    this.attrOperations = null;
    this.html = null;
  };
  TableRow.prototype = {
    initialize: function(bodySelector, head, parent, className) {
      this.rowId = id.nextStr();
      this.bodySelector = bodySelector;
      this.operations = [];
      this.operationAttribute = '';
      this.attrOperations = [];
      this.html = `<tr id="${this.rowId}" ${head ? 'head="' + sumCharCodes(head) + '"' : ''} ${parent ? 'parent="' + sumCharCodes(parent) + '"' : ''} style="${parent ? 'display:none' : ''}" class="${className ?? ''}">`;
      this.nColumns = 0;

      return this.rowId;
    },
    addAttribute: function(value, tdAttr, clickCb) {
      const isNumber = !isNaN(value);
      let tdClassName = isNumber ? 'text-right' : 'text-left';

      let th = $(`${this.bodySelector.split(' ')[0]} th`)[this.nColumns];
      const thClassName = th.classList.contains('text-right') ? 
      "text-right" : (th.classList.contains('text-left') ? 
      "text-left" : (th.classList.contains('text-center') ? 
      "text-center" : null));

      if(thClassName){
        tdClassName = thClassName;
      }

      if (clickCb === undefined) {
        this.html += '<td' + (tdAttr ? tdAttr : '') + ` class="${tdClassName}"` + '>' + value + '</td>';
      } else {
        var attrId = id.nextStr();
        if (value.indexOf('any') > -1) {
          this.html += '<td ' + (tdAttr ? tdAttr : '') + ` class="${tdClassName}"` +'><span id="' + attrId + '">'+ value + '</span></td>';
        } else {
          this.html += '<td ' + (tdAttr ? tdAttr : '') + ` class="${tdClassName}"` +'><span id="' + attrId + '" style="cursor:pointer;">'+ value + '</span></td>';
        }
        this.attrOperations.push({id: attrId, cb: clickCb});
      }
      this.nColumns++;
    },
    addAttributeWithIcon: function(value, tdAttr, clickCb,
      iconName, iconAttr, iconCb) {
        var attrId = id.nextStr(),
          iconId = id.nextStr();
        this.html += '<td '+(tdAttr ? tdAttr : '')+'><i id="'+iconId+'" class="material-icons hyperlink-generic" style="vertical-align: middle; font-size: 1.1em;">'+iconName+'&nbsp;&nbsp;</i><span id="'+attrId+'" class="hyperlink-text">'+ value + '</span></td>';
        this.attrOperations.push({id: attrId, cb: clickCb});
        this.attrOperations.push({id: iconId, cb: iconCb});
    },
    addOperation: function(name, icon, cb) {
      this.operations.push({id: id.nextStr(), name: name, icon: icon, cb: cb});
    },
    addOperationAttribute: function(attr) {
      this.operationAttribute = attr;
    },
    render: function() {
      let that = this;
      let cnt = this.operations.length;
      let item = null;
      let delimiter = null;
      let oprExecCb = function(operation, rowId) {
        // Defined to allow callback arguments
        //console.log('TBL-OPR-CB: rowId=%s', rowId);
        operation.cb(rowId);
      };
      let attrOprExecCb = function(operation, rowId) {
        // Defined to allow callback arguments
        //console.log('TBL-ATTR-CB: rowId=%s', rowId);
        operation.cb(rowId);
      };

      if (cnt > 0) {
        this.html += `<td ${this.operationAttribute}>`;
        for (let i = 0; i < cnt; i++) {
          item = this.operations[i];
          delimiter = (i < (cnt - 1) ? '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;' : '');
          this.html += '<a href="javascript:void(0);" id="' + item.id + '" data-toggle="tooltip" data-placement="top" title="' + item.name + '">' +
            '<i class="material-icons actions-icon-color">' + item.icon +'</i></a>' + delimiter;
        }
        this.html += '</td>';
      //} else {
        // Empty data
        //this.html += '<td></td>'
      }

      this.html += '</tr>';

      //console.log('TABLE-ROW-BODY-SELECTOR: %s', this.bodySelector);
      //console.log('TABLE-ROW-HTML: %s', this.html);
      const newRow = $(this.bodySelector).append(this.html);
      // Callbacks must be activated after html is appended
      this.operations.forEach(function(item, i) {
        newRow.on('click', '#'+item.id, oprExecCb.bind(this, that.operations[i], that.newRowId));
      });
      for (let i = 0, len = this.attrOperations.length; i < len; i++) {
        $('#'+this.attrOperations[i].id).on("click", attrOprExecCb.bind(this, this.attrOperations[i], this.rowId));
      }
      this.operationAttribute = '';
    },
  }

  return TableRow;
})();


//
// ParseStrTable
//
var parseStrTable = function(strTable, parseLine, skipHdr) {
  // Parameter skipHdr is optional and defaults to true
  var lines = strTable.split('\n'),
    firstLine = ((skipHdr === undefined || skipHdr) ? 1 : 0);
  for (var i = firstLine, len = lines.length - 1; i < len; i++) {
    parseLine(lines[i].split(/\s+/), i);
  }
};


//
// IndentedStrAttrsList
//
var IndentedStrAttrsList = (function() {
  var baseNameItems = [],
    nameItems = [];

  return {
    init: function() {
      baseNameItems = [],
      nameItems = [];
    },
    getTuple: function(attrs) {
      var item, name;

      if (attrs[0] == '') {
        // Indented string
        attrs.shift(); // Discard white_spaces
        while ((item = attrs.shift()) !== undefined) {
          nameItems.push(item);
          if (item.slice(-1) == ':') break;
        }
        name = baseNameItems.join(' ')+' '+nameItems.join(' ');
        nameItems = [];
        return { name: name, values: attrs };
      } else {
        // Base name string
        baseNameItems = [];
        nameItems = [];
        while ((item = attrs.shift()) !== undefined) {
          baseNameItems.push(item);
          if (item.slice(-1) == ':') break;
        }
        return { name: baseNameItems.join(' '), values: attrs };
      }
    }
  };
})();


//
// UnTooltip
//
var UnTooltipJqSel = function (jqSel, tooltipId, tooltipF) {
  var tooltip = d3.select('#'+tooltipId);
  if (tooltip.empty()) {
    tooltip = d3.select('body').append('div')
      .style('opacity', 0)
      .attr('id', tooltipId)
      .attr('class', 'untooltip')
      ;
  }

  d3.selectAll(jqSel.toArray())
    .on('mouseenter', function(d) {
      // Y position must be adjusted with window.scrollY
      var position = jqSel[0].getBoundingClientRect(),
        x = position.left + 5,
        y = position.top+window.scrollY + 5;

      tooltip.transition()
        .duration(100)
        .style('opacity', 0.9);
      tooltip.html(tooltipF())
        .style('left', x+'px')
        .style('top', y+'px');
    })
    .on('mouseleave', function(d) {
      tooltip.transition()
        .duration(10)
        .style("opacity", 0);
    })
    ;
};


//
// CliConfig
//
var CliConfig = (function() {
  var CONST = {
    CMD_SHOW_DIFF: 'show config diff',
    CMD_SHOW_COMMIT: 'show config commit',
    CMD_LOAD: function(path) { return 'configure\nload config '+path+' restore\n' },
    CMD_LOAD_MERGE: function(path) { return 'configure\nload config '+path+'\n' },
    CMD_COMMIT: 'configure\ncommit\n',
    CMD_CLEAR: 'configure\nclear config changes\n',
    CMD_ROLLBACK: function(id) { return 'configure\nload rollback '+id+'\n' },
  };

  var executeCmdPromise = function(cmd) {
    return new Promise(function(resolve, reject) {
      ifCl.executeBatch(cmd, function(res) {
        if (res.CmdResponse.response.indexOf('%ERR') > -1) {
          reject(res.CmdResponse.response);
        } else {
          resolve(res.CmdResponse.response);
        }
      });
    });
  };

  return {
    sequentialExecute: function(cmds, resps, okF, koF) {
      var that = this;

      // Avoid empty command list execution
      if (cmds.length == 0) {
        if (okF) okF();
        return;
      }

      // Check configuration pending changes
      executeCmdPromise(CONST.CMD_SHOW_DIFF).then(function(response) {
        if (response.length > 0) {
          showModalError('Cannot execute configuration changes:',
            'BQN has pending configuration changes');
        } else {
          // Sequential execution of CLI commands using promises
          cmds.reduce(function(promise, cmd, i) {
            return promise.then(function(response) {
              // First 'undefined' response corresponds to the explicitly resolved 'first' promise
              if (resps) resps[i-1] = response;
              return executeCmdPromise(cmd);
            });
          }, Promise.resolve()).then(function(response) {
            // End of sequence (last promise)
            //console.log('RESPONSE: %s', response);
            var len = cmds.length - 1;
            if (resps) resps[len] = response;
            that.commit(okF, koF);
          }).catch(function(error) {
            //console.log('ERROR: %s', error);
            that.clear();
            if (koF) koF(error);
          });
        }
      });
    },
    // Not used, added for documentation
    commitYari: function() {
      ifCl.edbLicLimxecuteBatch(CONST.CMD_COMMIT, function(resp) {
        r = resp.CmdResponse;
        if (r.code != 0 || r.response.length > 0) {
          if (r.response.indexOf(
            '%WARN: No configuration changes available to commit') > -1) {
            showModalError('Warning:', 'No configuration changes to commit');
          } else {
            ifCl.executeBatch(CONST.CMD_CLEAR, function(resp) {
            });
            showModalError('Error:', r);
          }
        } else {
          showModalInfo('Loaded configuration successfully:', null);
        }
      });
    },
    commit: function(okF, koF) {
      var r;

      ifCl.executeBatch(CONST.CMD_COMMIT, function(resp) {
        r = resp.CmdResponse;
        if (r.code != 0) {
          // SYSTEM-ERROR
          if (koF) koF('Unable to commit changes:', 'System error');
        } else if (r.response.indexOf('%ERR') > -1) {
          // CLI-ERROR
          ifCl.executeBatch(CONST.CMD_CLEAR, function(resp) {
          });
          if (koF) koF('Error in commit', r.response);
        } else if (r.response.indexOf('%WARN') > -1) {
          // CLI-WARNING
          if (okF) okF('Successfully commited changes:', r.response);
        } else {
          // CLI-OK
          if (okF) okF('Successfully commited changes:', null);
        }
      });
    },
    cmd: function(cmd) {
      var that = this,
        r;

      ifCl.execute(CONST.CMD_SHOW_DIFF, function(resp) {
        if (resp.CmdResponse.response.length > 0) {
          showModalError('Unable to load configuration:',
            'BQN has pending configuration changes');
          return;
        }
        ifCl.executeBatch(cmd, function(resp) {
          r = resp.CmdResponse.response;
          if (r.indexOf('%ERR') > -1) {
            showModalError('Error:', r);
          } else if (r.indexOf('%WARN') > -1) {
            showModalInfo('Warning:', r);
          } else {
            that.commit(
              (result, detail) => { showModalInfo('Configuration updated successfully:', null) },
              (result, detail) => { showModalError(result, detail) });
          }
        });
      });
    },
    load: function(overwrite, confPath) {
      var that = this,
        r, cmd;

      ifCl.execute(CONST.CMD_SHOW_DIFF, function(resp) {
        if (resp.CmdResponse.response.length > 0) {
          showModalError('Unable to load configuration:',
            'BQN has pending configuration changes');
          return;
        }

        // Load remote configuration file
        cmd = (overwrite ? CONST.CMD_LOAD(confPath) :
          CONST.CMD_LOAD_MERGE(confPath));
        ifCl.executeBatch(cmd, function(resp) {
          if (resp.CmdResponse.code != 0) {
            showModalError('Unable to load configuration:',
              'System error:');
          } else {
            r = resp.CmdResponse.response;

            if (r.indexOf('%ERR') > -1) {
              showModalError('Error:', r);
            } else {
              // Loaded 81 commands (0 errors)
              // r.match(/\d errors/g)
              // [ '0 errors' ] | null
              if (Number(r.match(/\d+ errors/g)[0].match(/\d+/g)[0]) > 0) {
                showModalError('Errors present, aborting configuration:', r);
                that.clear();
              } else {
                that.commit(
                  (result, detail) => { showModalInfo('Loaded configuration successfully:', null) },
                  (result, detail) => { showModalError(result, detail) });
              }
            }
          }
        });
      });
    },
    clear: function(clearCb) {
      ifCl.executeBatch(CONST.CMD_CLEAR, function(resp) {
        if (clearCb) clearCb();
      });
    },
    rollback: function(okF, koF) {
      var that = this,
        lines, id, r, errCnt;

      ifCl.execute(CONST.CMD_SHOW_DIFF, function(resp) {
        if (resp.CmdResponse.response.length > 0) {
          if (koF) koF('Unable to rollback:',
            'BQN has pending configuration changes');
          return;
        }

        ifCl.execute(CONST.CMD_SHOW_COMMIT, function(resp) {
          r = resp.CmdResponse.response;
          lines = r.split('\n');
          if (!lines || !lines[2]) {
            if (koF) koF('Unable to rollback:',
              'Cannot get last commit ID');
            return;
          }
          id = lines[2].split(' ')[0]
          if (!id || isNaN(Number(id))) {
            if (koF) koF('Unable to rollback:',
              'Cannot get last commit ID');
            return;
          }
          ifCl.executeBatch(CONST.CMD_ROLLBACK(id), function(resp) {
            r = resp.CmdResponse;
            if (r.code != 0) {
              // SYSTEM-ERROR
              if (koF) koF('Unable to rollback:', 'System error');
            } else if (r.response.indexOf('%ERR') > -1) {
              // CLI-ERROR
              if (koF) koF('Unable to rollback:', r.response);
            } else if (r.response.indexOf('%WARN') > -1) {
              // CLI-WARNING ???
              if (okF) okF('Configuration rolledback:', r.response);
            } else if ((errCnt = r.response.match(/Errors:\s+\d+/g)[0]) &&
              (errCnt.match(/\d+/g)[0] == '0')) {
              // CLI-OK
              that.commit(okF, koF);
            } else {
              if (koF) koF('Unable to rollback:', r.response);
            }
          });
        });
      });
    },
  };
})();



//
// useMemoryUsage
//
function useMemoryUsage(userF) {
  var cmds = [], resps = [],
    memUsage = {
      systemTotal: 0, systemUsed: 0,
      mpoolTotal: 0, mpoolUsed: 0,
      dpdkTotal: [], dpdkUsed: []
    };
  cmds.push('show system memory');
  cmds.push('show pkteng memory');
  cmds.push('show pkteng memory dpdk');
  SequentialCmd.execute(cmds, resps, function() {
    var alloc = 0,
      attrs = resps[0].split('\n')[1].split(/\s+/);
    memUsage.systemTotal = Number(attrs[1]) / 1024;
    memUsage.systemUsed = Number(attrs[2]) / 1024;

    parseStrTable(resps[1], function(attrs) {
      if (attrs[0] == '') attrs.shift();
      memUsage.mpoolTotal += Number(attrs[2]);
      alloc += Number(attrs[5]);
      memUsage.mpoolUsed += Number(attrs[6]);
    });
    memUsage.mpoolUsed *= memUsage.mpoolTotal / alloc / 1024;
    memUsage.mpoolTotal /= 1024;

    parseStrTable(resps[2], function(attrs) {
      if (attrs[0] == '') attrs.shift();
      if (attrs[2] == 'n/a' || attrs[1] == 'n/a') {
        memUsage.dpdkTotal.push(0.0);
        memUsage.dpdkUsed.push(0.0);
      } else {
        memUsage.dpdkTotal.push(Number(attrs[2]));
        memUsage.dpdkUsed.push(Number(attrs[1]));
      }
    });
    return userF(memUsage);
  }, function(err) {
    console.log('ERROR: executing useMemoryUsage ', err);
    return null;
  });
}


//
// ModalWait
//
var ModalWait = (function() {
  function ModalWait() {
    this.usingProgress = false;
    this.usingCancel = false;

    this.waitSel = $('#modalWait');
    this.waitTxtSel = $('#modalWaitTxt');
    this.progressSel = $('#modalWaitProgress');
    this.cancelSel = $('#modalWaitCancel');
  };
  ModalWait.prototype = {
    show: function(text) {
      if (text) this.waitTxtSel.text(text);
      this.progressSel[(this.usingProgress ? 'show' : 'hide')]();
      this.cancelSel[(this.usingCancel ? 'show' : 'hide')]();
      this.waitSel.modal('show');
    },
    hide: function() {
      this.waitSel.modal('hide');
      this.usingProgress = false;
      this.usingCancel = false;
    },
    setText: function(text) {
      this.waitTxtSel.text(text);
    },
    setProgressMax: function(max) {
      this.usingProgress = true;
      this.progressSel[0].max = max;
      this.progressSel[0].value = 0;
      this.progressSel.show()
    },
    setProgressValue: function(value) {
      this.progressSel[0].value = value;
    },
    setCancel: function(cancelCb) {
      this.usingCancel = true;
      this.cancelSel.off().click(function() {
        cancelCb();
      });
      this.cancelSel.show();
    },
    hideProgress: function() {
      this.progressSel.hide()
      this.cancelSel.hide();
      this.usingProgress = false;
      this.usingCancel = false;
    },
  };

  return ModalWait;
})();


//
// GetFile
//
var GetFile = (function() {
  function GetFile() {
    this.gets = [];
  };
  GetFile.prototype = {
    fromBase64ToBinary: function(base64) {
      var rawStr = window.atob(base64),
        rawLen = rawStr.length,
        binary = new Uint8Array(rawLen);

      for (let i = 0; i < rawLen; i++) {
        binary[i] = rawStr.charCodeAt(i);
      }
      // Return underlying ArrayBuffer
      return binary.buffer;
    },
    begin: function(id, path) {
      var it = { id: id, path: path, buffers: [] };
      this.gets.push(it);
    },
    add: function(id, length, chunk) {
      var it = this.gets.find(it => it.id == id),
        bin;
      if (it) it.buffers.push(this.fromBase64ToBinary(chunk));
      else console.log('Warning. GetFile.add: unknown file id %s', id);
    },
    end: function(id) {
      var idx = this.gets.findIndex(it => it.id == id),
        blob, a, it;

      if (idx > -1) {
        it = this.gets[idx];
        blob = new Blob(it.buffers, { type: 'application/octet-stream' }),
        a = document.createElement('a');
        a.download = it.path.split('/').reverse()[0];
        a.href = window.URL.createObjectURL(blob);
        a.click();
        window.URL.revokeObjectURL(a.href);
        this.gets.splice(idx, 1);
      }
    },
    execute: function(filePath, cb, silent) {
      ifCl.getFile(filePath, function(res) {
        var resp = res.GetFileResponse;
        var thereIsError = false;

        if (resp.code != 0) {
          thereIsError = true;
          console.log('GetFile.error: %s %s', filePath, resp.reason);
          if(!silent){
            showModalError('Error:', 'Cannot find file: '+filePath);
          }
        } else {
          getFile.begin(resp.id, filePath);
        }

        if(cb){
          cb(thereIsError);
        }
      });
    },
  };

  return GetFile;
})();


//
// SubsStatsUsage
//
var SubsStatsUsage = (function() {
  var CONST = {
    INTERVAL: 10, // Minutes
    TYPE: {
      'flows': {
        title: 'SUBSCRIBER FLOWS OVER TIME',
        units: 'flows',
        },
      'max-speed': {
        title: 'SUBSCRIBER MAXIMUM SPEED OVER TIME',
        units: 'Mbps',
      },
      'latency': {
        title: 'SUBSCRIBER ACCESS LATENCY OVER TIME',
        units: 'ms',
      },
      'retransmission': {
        title: 'SUBSCRIBER ACCESS RETRANSMISSIONS OVER TIME',
        units: '%',
      },
    },
    SUBTITLES: {
      'SUBSCRIBER FLOWS OVER TIME': 'Subscriber flows over time:<br>' +
        '<li>Fl-Created: new flows created.</li><li>Fl-Active: total active flows (with traffic).</li>',
      'SUBSCRIBER MAXIMUM SPEED OVER TIME': 'Subscriber maximum speed over time<br>' +
        '<li>Down-Mbps: maximum speed in downlink direction in Mbps.</li><li>Up-Mbps: maximum speed in uplink direction in Mbps.</li>',
      'SUBSCRIBER ACCESS LATENCY OVER TIME': 'Access latencies over time:<br>' +
        '<li>Subs-Access-Rtt-Ms: subscriber access latency in milliseconds. Measures the RTT between the BQN and this subscriber.</li>' +
        '<li>Network-Access-Rtt-Ms: average access latency (RTT from BQN to subscribers) in milliseconds for the network for all subscribers.</li>',
      'SUBSCRIBER ACCESS RETRANSMISSIONS OVER TIME': 'Access retransmissions over time:<br>' +
        '<li>Subs-Access-Rtx-%: subscriber access retransmission percentage. Measures the retransmissions between the BQN and this subscriber.</li>' +
        '<li>Network-Access-Rtx-%: average access (happenning between the BQN and the subscribers) retransmission percentage for the network for all subscribers.</li>',
    }
  };

  function SubsStatsUsage(loadViewCb, name) {
    var that = this,
      bindExecute = this.execute.bind(this);

    this.jqSelector = '#'+name;
    this.chartShowSel = $(this.jqSelector);
    this.typeSel = $(this.jqSelector+'Type');
    this.typeSelModal = $(this.jqSelector+'TypeModal');
    this.rangeShowSel = $(this.jqSelector+'RangeShow');
    this.rangeModal = $('#selectSubsStatsUsageRangeModal');
    this.modalUsage = $('#subsStatsUsageModal');
    this.totalsShowSel = $(this.jqSelector+'Totals');
    this.multicolStats = new MultiColStats(bindExecute, name,
      { columnNameF: function(name) {
        // AnObj -> An-Obj
        var chunks = name.split('-');
        // Normalize first chunk alone
        for (var i = 0; i < 1; i++) {
          var item = chunks[i];
          chunks[i] = item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
        }
        chunks[1] = 'Mbps';
        return chunks.join('-');
      }
    });

    this.typeSel.change(function() {
      var typeSel = $('#selectSubsStatsUsageRange option:selected').val();

      if (typeSel === undefined)
        typeSel = 'Day';
      var typeName = $(this).val(),
        type = CONST.TYPE[typeName];
      if (typeName == 'volume') {
        // Do not change view
      } else {
        globalNavigate('viewSubsStatsMetrics',
          {period: typeSel,
          addr: that.addr.get(),
          title: type.title,
          type: typeName, units: type.units});
      }
    });

    this.typeSelModal.change(function() {
      let typeSel = $('#selectSubsStatsUsageRange option:selected').val();

      if (typeSel === undefined)
        typeSel = 'Day';
      var typeName = $(this).val(),
        type = CONST.TYPE[typeName];
      if (typeName == 'volume') {
        // Do not change view
      } else {
        let modalMetrics = $('#subsStatsMetricsModal');
        let modalUsage = $('#subsStatsUsageModal');
        let titleMetrics;
        modalUsage.modal('hide');

        subsStatsMetrics.execute({
          period: typeSel,
          addr: that.addr.get(),
          title: type.title,
          type: typeName,
          units: type.units
        });
        if (typeName == 'flows') {
          titleMetrics = 'Subscriber Flows Over Time';
        } else if (typeName == 'max-speed') {
          titleMetrics = 'Subscriber Maximum Speed Over Time';
        } else if (typeName == 'latency') {
          titleMetrics = 'Subscriber Access Latency Over Time';
        } else {
          titleMetrics = 'Subscriber Access Retransmissions Over Time';
        }
        $('#subsStatsMetricsModalTitle').text(titleMetrics);
        $('#subsStatsMetricsTypeModal').val(typeName);
        modalMetrics.modal('show');
      }
    });

    $('#subsStatsMetricsHelp').on('show.bs.modal', function (typeName) {
      var titleModal = $('#subsStatsMetricsTitle').text();
      $('#titleModal').text(titleModal);
      $('#subTitleModal').html(CONST.SUBTITLES[titleModal]);
    });

    this.addr = new InputIpAddrCombo(name, 'Ip', 'GoBtn', bindExecute,
      'Ex: 10.1.0.1 or 2001:db8:80::');

    this.rangeModal.change(function () {
      that.execute();
    });
  };
  SubsStatsUsage.prototype = {
    hideChart: function() {
      this.chartShowSel.hide();
      this.rangeShowSel.hide();
      this.totalsShowSel.hide();
    },
    showChart: function() {
      this.chartShowSel.show();
      this.rangeShowSel.show();
      this.totalsShowSel.show();
    },
    showModalDetail(optArgs) {
      this.modalUsage.modal('show');
      this.execute(optArgs);
    },
    execute: function(optArgs) {
      var that = this;

      ifCl.execute('show system date', function(res) {
        var dateStr = res.CmdResponse.response,
          period, cmd,
          periodStr = that.multicolStats.getPeriodSelector(),
          ip = that.addr.get();
        subsMetrics.modalClick = false;

        if (optArgs) {
          periodStr = optArgs.period || 'Day';
          that.multicolStats.setPeriodSelector(periodStr);
          ip = optArgs.ip;
          that.addr.set(ip);
        }
        that.typeSel.val('volume');

        console.log('SubsStatsUsage: period=<%s>, addr=<%s>', periodStr, ip);

        period = new Period(periodStr, dateStr, true),
        cmd = 'show statistics subscribers volume ' + ip +
          ' hours ' + period.hours() + ' interval ' + CONST.INTERVAL;
        if (ip.length > 0) {
          that.showChart();
          ifCl.execute(cmd, function(res) {
            var resp = res.CmdResponse.response,
              i, totals = [], space = '&nbsp;&nbsp;&nbsp;&nbsp;';

            // REMOVE
            //resp='               TIME     DOWN-MBYTES  UP-MBYTES\n2021-12-27T08:10:00          20.540     40.000\n2021-12-27T08:20:00          23.600     50.000\n2021-12-27T08:30:00          29.710     60.000\n2021-12-27T08:40:00          33.940     70.000\n2021-12-27T08:50:00          44.580     86.970\n2021-12-27T09:00:00          46.890     67.520\n2021-12-27T09:10:00          55.360     57.420\n2021-12-27T09:20:00          79.630     38.360\n2021-12-27T09:30:00          45.800     45.170\n2021-12-27T09:40:00          13.000     71.830\n2021-12-27T09:50:00          25.700     16.270\n2021-12-27T10:00:00          23.270     56.090\n2021-12-27T10:10:00          20.010     40.000\n2021-12-27T10:20:00          10.010     30.000\n2021-12-27T10:30:00          12.010     30.000\n2021-12-27T10:40:00          10.010     20.000\n2021-12-27T10:50:00          09.010     20.000\n2021-12-27T11:00:00          08.010     15.000\n2021-12-27T11:10:00          10.010     10.000\n2021-12-27T11:20:00          00.010     00.000\n2021-12-27T11:30:00          00.010     00.000\n2021-12-27T11:40:00          00.010     00.000\n2021-12-27T11:50:00          00.010     00.000\n2021-12-27T12:00:00          00.110     00.000\n';
            //resp='               TIME     DOWN-MBYTES  UP-MBYTES\n2021-11-15T08:10:00          20.540     40.000\n2021-11-15T08:20:00          23.600     50.000\n2021-11-15T08:30:00          29.710     60.000\n2021-11-15T08:40:00          33.940     70.000\n';

            totals[0] = 0.0;
            totals[1] = 0.0;
            parseStrTable(resp, function(attrs, i) {
               totals[0] += Number(attrs[1]);
               totals[1] += Number(attrs[2]);
            });

            that.multicolStats.resetData();
            that.multicolStats.parser.setGapAmount(CONST.INTERVAL);
            that.multicolStats.setParserUserItemF(function(it, i) {
              return (Number(it) / (CONST.INTERVAL * 60)) * 8;
            });
            that.multicolStats.parse(resp, periodStr, dateStr);
            that.multicolStats.setUnits('Mbps');
            that.multicolStats.setTooltipContentGenerator(myTooltipContentGenerator);
            that.multicolStats.render();
            that.totalsShowSel.html('<p>Total Down: '+formatLargeNum(totals[0]/1000, 3)+' GB'+space+'Total Up: '+formatLargeNum(totals[1]/1000, 3)+' GB<p>');
          });
        } else {
          that.hideChart();
        }
      });
    },
  };

  return SubsStatsUsage;
})();

//
//
// OneCommandExecution
//
var OneCommandExecution = (function() {
  function OneCommandExecution(name, paramSuffix, responseF) {
    var that = this;

    this.name = name;
    this.responseF = responseF;
    this.jqSelector = '#'+name;
    this.textSel = $(this.jqSelector+'Text');
    this.paramSel = $(this.jqSelector+paramSuffix);

    /* Not Used, remove in the future
    $(this.jqSelector+'Title').text(title);
    $(this.jqSelector+'HelpBtn').click(function() {
      $(that.jqSelector+'Help').modal({backdrop: true});
    });
    $(this.jqSelector+'ReloadBtn').click(function() {
      that.execute();
    });
    $(this.jqSelector+'ParamLabel').text(paramLabel);
    */

    this.paramSel.on('keyup', function(e) {
      if (e.keyCode === 13) {
        that.go();
      } else {
        that.textSel.empty();
      }
    });
    $(this.jqSelector+'Go').click(function() {
      that.go();
    });
  };
  OneCommandExecution.prototype = {
    resetData: function() {
      this.resp = null;
      this.paramSel.val('');
      this.textSel.empty();
    },
    getParamSelector: function() {
      return this.paramSel.get(0).value.trim();
    },
    go: function() {
      var param = this.getParamSelector();

      if (param.length == 0) {
        // Do nothing, empty IP address
      } else {
        this.responseF(this.name, param, this.textSel);
      }
    },
    execute: function() {
      this.resetData();
    },
  };

  return OneCommandExecution;
})();


//
// OneLongCommandExecution
//
var OneLongCommandExecution = (function() {
  function OneLongCommandExecution(name, title, paramLabel, paramSuffix, responseF, showChunkF) {
    var that = this;

    this.name = name;
    this.responseF = responseF;
    this.lines = [];
    this.chunkLines = 1;
    this.currChunk = 0;
    this.jqSelector = '#'+name;
    this.textSel = $(this.jqSelector+'Text');
    this.linesSel = $(this.jqSelector+'Lines');
    this.PrevSel = $(this.jqSelector+'Prev');
    this.NextSel = $(this.jqSelector+'next');
    this.paramSel = $(this.jqSelector+paramSuffix);

    $(this.jqSelector+'Title').text(title);
    /* Not Used, remove in the future
    $(this.jqSelector+'HelpBtn').click(function() {
      $(that.jqSelector+'Help').modal({backdrop: true});
    });
    $(this.jqSelector+'ReloadBtn').click(function() {
      that.execute();
    });
    */
    $(this.jqSelector+'ParamLabel').text(paramLabel);

    this.paramSel.on('keyup', function(e) {
      if (e.keyCode === 13) {
        that.go();
      } else {
        that.textSel.empty();
      }
    });
    $(this.jqSelector+'Go').click(function() {
      that.go();
    });
  };
  OneLongCommandExecution.prototype = {
    resetData: function() {
      this.resp = null;
      this.paramSel.val('');
      this.textSel.empty();
    },
    getParamSelector: function() {
      return this.paramSel.get(0).value.trim();
    },
    getLinesSelector: function() {
      return this.lines.val();
    },
    setChunkLines: function(lines) {
      this.chunkLines = lines;
    },
    selectChunckLines: function() {
      var chunkLines = []
        from = this.currChunk * this.chunkLines,
        to = (this.currChunk+1) * this.chunkLines;

      if (to > this.lines.length) this.lines.length;
      for (var i = from;  i < to; i++) {
        chunkLines.push(this.lines[i]);
      }
    },
    prevChunk: function() {
      if (this.currChunk > 0) {
        this.currChunk--;
        this.showChunkF(this.selectChunckLines());
      }
    },
    nextChunk: function() {
      if (this.lines.length > ((this.currChunk+1) * this.chunkLines)) {
        this.currChunk++;
        this.showChunkF(this.name, this.selectChunckLines());
      }
    },
    go: function() {
      var param = that.getParamSelector();

      if (param.length == 0) {
        // Do nothing, empty IP address
      } else {
        this.responseF(this.name, param, this.getLinesSelector, lines);
      }
    },
    execute: function() {
      this.resetData();
    },
  };

  return OneLongCommandExecution;
})();


//
// InputIpAddrCombo
//   ATTENTION! if undefined userPlaceholder or userPlaceholder without CIDR
//   will accept plain ip adresses else cidr adresses.
//
window.InputIpAddrCombo = (function() {
  var CONST = {
    CIDR_PLACEHOLDER: 'Ex: 10.1.0.0/16 or 2001:db8:80::/48',
    ERROR_MSG: 'Invalid IP Address',
  };


  function InputIpAddrCombo(baseName, inputSuffix, btnSuffix, fireF, userPlaceholder) {
    var that = this,
      useCidr = (userPlaceholder == undefined ||
        userPlaceholder.indexOf('/') > -1);

    this.jqSelector = '#'+baseName;
    this.input = $(this.jqSelector + inputSuffix);
    this.btn = $(this.jqSelector + btnSuffix);
    this.placeholder = (userPlaceholder === undefined ?
      CONST.CIDR_PLACEHOLDER : userPlaceholder),
    this.validateAddrF = (useCidr ? IpV4V6.isValidCidrOrAddr : IpV4V6.isValid),
    this.fireF = fireF;

    this.arm();
  };
  InputIpAddrCombo.prototype = {
    arm: function() {
      var that = this,
        validateAndFire = function() {
          if (!that.validateAddrF(that.input.val().trim())) {
            that.input.val('');
            that.input.attr('placeholder', CONST.ERROR_MSG);
          } else {
            that.input.attr('placeholder', that.placeholder);
              that.fireF();
          }
          that.btn.hide();
        };

      this.input.attr('placeholder', this.placeholder);

      this.input.on('keyup click', function(e) {
        var isEmpty = (that.input.val().trim().length === 0);
        // Update placeholder text and button visibility
        // When empty (after validation error) remove error message.
        if (isEmpty) {
          that.input.attr('placeholder', that.placeholder);
          that.btn.hide();
        } else {
          that.btn.show();
        }

        // 'Return' key acts like:
        //   'Filter' when value present
        //   'Reload' when value absent
        if (e.keyCode === 13) {
          if (isEmpty) {
            that.fireF();
          } else {
            validateAndFire();
          }
        }
      });

      this.input.focusin(function() {
        //console.log('FOCUS-IN: len=%d', focusLen);
      });

      this.btn.click(function() {
        validateAndFire();
      });
    },
    reset: function() {
      this.input.val('');
      this.input.attr('placeholder', CONST.CIDR_PLACEHOLDER);
      this.btn.hide();
    },
    set: function(value) {
      this.input.val(value);
    },
    get: function() {
      return this.input.val().trim();
    },
    setPlaceholder: function(msg) {
      this.input.val('');
      this.input.attr('placeholder', msg);
    },
  };

  return InputIpAddrCombo;
})();


//
// HtmlSelect
//
var HtmlSelect = (function() {
  function HtmlSelect(name, suffix) {
    this.name = '#'+name+suffix;
    this.jqSel = $(this.name);
  };
  HtmlSelect.prototype = {
    reset:function() {
      this.empty();
      this.jqSel.val('');
    },
    empty:function() {
      this.jqSel.empty();
    },
    addLegend: function(text) {
      this.jqSel.append('<option value="" selected disabled hidden>'+
        text+'</option>');
    },
    add: function(text, selected, value) {
      // Value '' returns text value
      // Instead of value '', use new String('')
      this.jqSel.append($('<option>', {
        value: (value !== undefined ? (value == '' ? new String('') : value) : text),
        text: text,
        selected: selected
      }));
    },
    get:function() {
      // If null val then return ''
      return (this.jqSel.val() ? this.jqSel.val().trim() : '');
    },
    set:function(value) {
      this.jqSel.val(value);
    },
    onChange:function(changeF) {
      this.jqSel.change(function() {
        changeF();
      });
    },
  };

  return HtmlSelect;
})();


//
// Legend
//
var Legend = (function() {
  var CONST = {
    RULER_ID: 'ruler',
  };

  function Legend(externalKeys, chartThis) {
    this.RULER_ID = 'ruler';
    this.keys = externalKeys;
    this.circleGroup = null;
    this.chartThis = chartThis;
    this.clickCnt = 0;

    this.createRuler();
    this.statusInit();
  };
  Legend.prototype = {
    createRuler : function() {
      var span = document.getElementById(this.RULER_ID);

      if (span == null) {
        span = document.createElement('span');
        span.id = this.RULER_ID;
        span.style.visibility = 'hidden';
        span.style.whiteSpace = 'nowrap';
        document.getElementsByTagName('body')[0].appendChild(span);
      }
    },
    ruler : function(str) {
      var ruler = document.getElementById(this.RULER_ID)
      ruler.innerHTML = str;
      return ruler.offsetWidth;
    },
    statusInit : function() {
      for (var i = 0; i < this.keys.length; i++) {
        this.keys[i].status = true;
      }
    },
    statusRender: function() {
      var that = this;
      this.circleGroup.each(function(d, i) {
        if (that.keys[i].status) {
          d3.select(this).style('fill', function(d) { return d.color; });
        } else {
          d3.select(this).style('fill', function(d) { return 'white'; });
        }
      });
    },
    statusPattern: function() {
      var pattern = [];
      for (var i = 0, len = this.keys.length; i < len; i++) {
        if (this.keys[i].status) pattern.push(true);
        else pattern.push(false);
      }
      return pattern;
    },
    statusSetAlone: function(idx) {
      for (var i = 0, len = this.keys.length; i < len; i++) {
        if (i == idx) this.keys[i].status = true;
        else this.keys[i].status = false;
      }
    },
    statusIsLast: function(idx) {
      for (var i = 0, len = this.keys.length; i < len; i++) {
        if (i != idx && this.keys[i].status) {
          return false;
        }
      }
      if (this.keys[idx].status) return true;
      return false;
    },
    statusClick: function(i) {
      if (this.statusIsLast(i)) {
        this.statusInit();
      } else {
        this.keys[i].status = !this.keys[i].status;
      }
      this.statusRender();
      this.chartThis.filterF(this.statusPattern());
    },
    statusDoubleClick: function(i) {
      this.statusSetAlone(i);
      this.statusRender();
      this.chartThis.filterF(this.statusPattern());
    },
    render: function(svg, width, height, margin) {
      var that = this,
        xOff = margin.left + width,
        yOff = - Math.floor(margin.top * 1 / 3),
        rDot = 5,
        legendsAttrs = {
          dy: '-1',
          dx: '-1',
          'font-size': '16px',
          //'text-anchor': 'middle',
          //'fill-opacity': 1
        },
        legendGroup;

      // Compute initial legends offset (right justified)
      for (var i = 0, len = this.keys.length; i < len; i++) {
        xOff -= 4*rDot + that.ruler(this.keys[i].key);
      }
      xOff += rDot*2;

      // TEST, not used
      // Add legend circles using SVG
      //this.keys.forEach(function(it, i) {
      //  svg.append('circle')
      //    .attr('cx', i*80)
      //    .attr('cy', 0)
      //    .attr('r', 4)
      //    .attr('stroke', it.color)
      //    //.attr('stroke-width', 2)
      //    .style('fill', it.color)
      //    .attr('transform', function (d) {
      //      return 'translate(' + xOff + ',' + yOff + ')';
      //    })
      //    .on('click', function() {
      //      console.log('CLICK: %s', id);
      //    })
      //    ;
      //});

      // Add legend circles using D3
      legendGroup = svg.append('g')
        .attr('class', 'interval-legend')
        .selectAll('g')
        .data(this.keys)
        .enter()
        .append('g')
        .attr('transform', function (d, i) {
           var off = xOff;
           // Update next x-offset
           xOff += 5*rDot + that.ruler(that.keys[i].key);
          return 'translate(' + off + ',' + yOff + ')';
        })
        .append('g')
        .attr('transform', function (d, i) {
          return 'translate(' + 0 + ',' + rDot + ')';
        })
        .style('cursor', 'pointer')
        ;

      this.circleGroup = legendGroup.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', rDot)
        .attr('stroke', function(d) { return d.color; })
        .attr('stroke-width', 2)
        .style('fill', function(d) { return d.color; })
        .on('click', function(d, i) {
          // Detect single and double click
          that.clickCnt++;
          if (that.clickCnt == 1) {
            setTimeout(function() {
              if (that.clickCnt == 1) {
                that.statusClick(i);
              } else {
                that.statusDoubleClick(i);
              }
              that.clickCnt = 0;
            }, 300);
          }
        })
        ;
      /* Iterate selection (array)
      this.circleGroup.each(function(d, i) {
        console.log('PILLO: %d, %s', i, JSON.stringify(d));
      });
      */

      legendGroup.append('text')
        .text(function(d) { return d.key; })
        .attr('class', 'unselectable')
        .attr('x', function(d) { return rDot*2; })
        .attr('y', function(d) { return rDot; })
        .attr(legendsAttrs)
        ;
    },
  };

  return Legend;
})();


//
// IntervalChart
//
var IntervalChart = (function() {
  var CONST = {
    LINE_WIDTH_MIN: 3,
    LINE_WIDTH_INI: 24,
    LINE_GAP: 5,
    ID_PREFIX: 'interval-',
    GRID_COLOR: 'var(--chart-grid-color)',
   DOS_TOOLTIP_NAME: 'dosTooltip',
    INT_EXCESS_PX: 1,
  };

  function IntervalChart(name, keys) {
    this.keys = keys;
    this.parseDate = d3.timeParse('%Y-%m-%dT%H:%M:%S');
    this.formatDate = d3.timeFormat('%Y-%m-%d %H:%M:%S');
    this.range = null;

    this.svg = null;
    this.scale = null;
    this.intsGroup = null;
    this.margin = null;
    this.width = null;
    this.height = null;
    this.lineWidth = null;
    this.tooltipContent = null;

    this.legend = new Legend(keys, this);
    this.jqSelector = '#' + name;

    this.levels = [];
    this.dosTooltip = d3.select('body').append('div')
      .style('opacity', 0)
      .attr('id', CONST.DOS_TOOLTIP_NAME)
      .attr('class', 'untooltip')
      ;

    // intervalExample = {iniDate, finDate, level, id, type, details: {...}};
    // levelsExample = [
    //   [ {level0-interval0}, {level0-interval1}, {level0-interval2} ],
    //   [ {level1-interval0}, {level1-interval1} ],
    //   [ {level2-interval0} ],
    // ];
  };
  IntervalChart.prototype = {
    resetData: function() {
      this.levels = [];
      this.lineWidth = CONST.LINE_WIDTH_INI;
    },
    filterF: function(pattern) {
      //console.log('LEGEND-FILTER: %s', JSON.stringify(pattern));
      // Filter intervals by filter pattern
      this.renderIntervals(pattern);
    },
    setTooltipContent: function(tooltipF) {
      this.tooltipContent = tooltipF;
    },
    position: function(interval) {
      var pos = {x1: null, y1: null, x2: null, y2: null};

      pos.x1 = this.scale(interval.iniDate) + this.margin.left;
      pos.y1 = this.height - (3 * CONST.LINE_GAP) -
        (interval.level * (CONST.LINE_GAP + this.lineWidth));
      pos.x2 = this.scale(interval.finDate) + this.margin.left;
      pos.y2 = pos.y1;

      return pos;
    },
    renderInterval: function(interval) {
      var that = this,
        id = CONST.ID_PREFIX+interval.id,
        color = this.keys[interval.type].color,
        newInt = {},
        pos;

      // Truncate interval if do not fit in range
      newInt.level = interval.level;
      newInt.iniDate = (interval.iniDate < this.range.from ?
        this.range.from : interval.iniDate);
      newInt.finDate = (interval.finDate > this.range.to ?
        this.range.to : interval.finDate);
      pos = this.position(newInt);

      //console.log('renderInterval: (%s, %s) - %s - %d', this.formatDate(interval.iniDate), this.formatDate(interval.finDate), id, pos.x2 - pos.x1);

      //this.intsGroup.append('line')
      this.intsGroup.append('line').datum(interval)
        .attr('id', id)
        .attr('class', 'dos-interval')
        .style('stroke', color)
        .style('stroke-width', this.lineWidth)
        .attr('x1', pos.x1-CONST.INT_EXCESS_PX)
        .attr('y1', pos.y1)
        .attr('x2', pos.x2+CONST.INT_EXCESS_PX)
        .attr('y2', pos.y2)
        .on('mouseover', function(d) {
          //console.log('INTERVAL-HOVER[%s]: %s', id, JSON.stringify(d));
          if (that.tooltipContent) {
            var x = d3.event.pageX - 80,
              y = d3.event.pageY - 230;
            that.dosTooltip.transition()
              .duration(100)
              .style('opacity', .9);
            that.dosTooltip.html(that.tooltipContent(d))
              .style('left', x+'px')
              .style('top', y+'px');
          }
        })
        .on('mouseout', function(d) {
          if (that.tooltipContent) {
            that.dosTooltip.transition()
              .duration(100)
              .style("opacity", 0);
          }
        });
        ;
    },
    hideIntervals: function() {
      this.intsGroup.html(null);
    },
    renderIntervals: function(filterPattern) {
      var level;

      //console.log('renderIntervals: %s', JSON.stringify(filterPattern));

      // Clear  interval nodes
      //this.svg.select('.jc-interval').html(null);
      this.intsGroup.html(null);

      for (var i = 0, len = this.levels.length; i < len; i++) {
        level = this.levels[i];
        if (level) {
          for (var j = 0; j < level.length; j++) {
            if (!filterPattern || filterPattern[level[j].type]) {
              this.renderInterval(level[j]);
            }
          }
        }
      }
    },
    setLineWidth: function() {
      while (((this.levels.length * (CONST.LINE_GAP + this.lineWidth)) >
        this.height) && this.lineWidth > CONST.LINE_WIDTH_MIN) {
        this.lineWidth = Math.floor(this.lineWidth * 2 / 3);
      }
    },
    build: function() {
      // Decide this.height based on this.levels.length !!!!!!!!
//console.log('DIV_CLIENT: %dx%d', document.querySelector('#dosStatsTime').clientWidth, document.querySelector('#dosStatsTime').clientHeight);
//console.log('DIV_OFFSET: %dx%d', document.querySelector('#dosStatsTime').offsetWidth, document.querySelector('#dosStatsTime').offsetHeight);
//var rect =  document.querySelector('#dosStatsTime').getBoundingClientRect();
//console.log('BOUNDING_RECT: %dx%d', rect.width, rect.height);

      var that = this,
        availableWidth = document.querySelector(this.jqSelector).getBoundingClientRect().width,
        margin = availableWidth > 700 ? 100 : 10,
        width = document.querySelector(this.jqSelector).offsetWidth - margin,
        height = Number(document.querySelector(this.jqSelector+' svg').style.height.substring(0, 3));

      // Resize callabck
      d3.select(window).on('resize', function() {
        setTimeout( () => that.build(), 250);
      });

      this.margin = availableWidth > 700
        ? {top: 100, right: 120, bottom: 70, left: 50}
        : {top: 100, right: 20, bottom: 70, left: 10};
      this.width = width - this.margin.left - this.margin.right;
      this.height = height - this.margin.top - this.margin.bottom;

      // Clear previous chart
      //d3.select('#dosStatsTime svg').html(null);
      // Clear previous nvd3 chart (remove all 'g' children)
      //$(this.jqSelector + ' svg').find('*').remove();
      $(this.jqSelector + ' svg').html(null);

      // Execute when this.height is already computed
      this.setLineWidth();

      this.svg = d3.select(this.jqSelector+' svg')
          .attr("width", this.width + this.margin.left + this.margin.right)
          .attr("height", this.height + this.margin.top + this.margin.bottom)
        .append("g")
          .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")")

/*
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .attr('viewBox', '0 0 800 400')
        .classed('svg-content', true)
*/
        ;

      this.legend.render(this.svg, this.width, this.height, this.margin);

      // Create scale
      this.scale = d3.time.scale()
        .domain([that.range.from, that.range.to])
        .range([0, this.width])
        ;

      // Add scales to xAxis
      /* Recent D3 versions
      var xAxis = d3.axisBottom()
        .scale(this.scale)
      */
      var xAxis = d3.svg.axis()
        .scale(this.scale)
        .orient('bottom')
        .tickSize(1)
        .ticks(8)
        .tickFormat(function(d) {
          var xPos = that.scale(d) + that.margin.left;
          that.svg.append('line')
            .style('stroke', CONST.GRID_COLOR)
            .style('stroke-width', 0.5)
            .attr('x1', xPos)
            .attr('y1', 0)
            .attr('x2', xPos)
            .attr('y2', that.height);
          return d3.time.format('%m-%d %H:%M')(new Date(d));
        })
        ;

      // Append group for axis
      this.svg.append('g')
        .attr('transform', 'translate('+this.margin.left+','+this.height+')')
        .attr('class', 'unselectable')
        .call(xAxis)
        // Rotate tic labels
        .selectAll('text')
          .attr('transform', 'translate(-5,5)rotate(-30)')
          .style('text-anchor', 'end')
        ;

      // Append group for intervals
      //this.intsGroup = this.svg.append('g').attr('class', 'jc-interval');
      this.intsGroup = this.svg.append('g');
      this.renderIntervals();

      return this;
    },
    dump: function(text) {
      if (text !== undefined) console.log(text);

      for (var i = 0, len = this.levels.length; i < len; i++) {
        level = this.levels[i];
        if (level != undefined) {
          console.log('LEVEL[%d]: len=%d', i, level.length);
          for (var j = 0; j < level.length; j++) {
            console.log('  INT[%d]: (%s, %s) %d', j,
              this.formatDate(level[j].iniDate),
              this.formatDate(level[j].finDate), level[j].id);
          }
        }
      }
    },
    computeLevel: function(iniDate, finDate) {
      var level,
        iniDate = moment(iniDate),
        finDate = moment(finDate);

      // Search in levels to locate new interval position
      for (var i = 0, len = this.levels.length; i < len; i++) {
        level = this.levels[i];
        if (level.length == 0 ||
          iniDate.isAfter(level[level.length-1].finDate)) {
          return i;
        }
      }
      // Create new level
      this.levels.push([]);
      return len;
    },
    render: function(range, dataset) {
      var it;

      this.resetData();
      this.range = range;

      // Compute dataset.storedValues intervals levels
      // This way levels are preserved in zooms
      for (var i = 0, len = dataset.storedValues.length; i < len; i++) {
        it = dataset.storedValues[i];
        it.level = this.computeLevel(it.iniDate, it.finDate);
        this.levels[it.level].push(it);
      }
      //this.dump('storedValues-levels:');

      // Compute dataset.values intervals levels
      this.levels = [];
      for (var i = 0, len = dataset.values.length; i < len; i++) {
        it = dataset.values[i];
        if (this.levels[it.level] == undefined) {
          this.levels[it.level] = [];
        }
        this.levels[it.level].push(it);
      }
      //this.dump('LEVELS:');

      this.build();
    },
  };

  return IntervalChart;
})();


//
// DosStatsTime
//
var DosStatsTime = (function() {
  var CONST = {
    NO_ATTACKS_FOUND: 'No Attacks Found',
    N_A_VALUE: 'n/a',
    KEYS: [
      {key: 'Volume', color: '#d62728'},
      {key: 'Syn-Rcv', color: '#1f78b4'},
      {key: 'Syn-Snt', color: '#a6cee3'},
    ],
  };

  function DosStatsTime(reloadDataCb, name) {
    var that = this;

    this.parseDate = d3.timeParse('%Y-%m-%dT%H:%M:%S');
    this.formatDate = d3.timeFormat('%Y-%m-%dT%H:%M:%S');
    this.id = 0;
    this.chart = new IntervalChart(name, CONST.KEYS);
    this.dataset = new IntervalDataset();
    this.volDetail = null;
    this.synDetail = null;

    this.jqSelector = '#'+name;
    this.periodSel = $(this.jqSelector + 'Period');
    this.detailModal = $(this.jqSelector+'DetailModal');
    this.dataShow = $(this.jqSelector + 'DataShow');
    this.noDataShow = $(this.jqSelector + 'NoDataShow');
    this.timeline = new TimeSlider(this, this.jqSelector+'Slider');

    // intervalExample = {iniDate, finDate, level, id, type, details: {...}};
    // levelsExample = [
    //   [ {level0-interval0}, {level0-interval1}, {level0-interval2} ],
    //   [ {level1-interval0}, {level1-interval1} ],
    //   [ {level2-interval0} ],
    // ];

    $(this.jqSelector+'HelpBtn').click(function() {
      $(that.jqSelector+'Help').modal({backdrop: true});
    });
    $(this.jqSelector + 'ReloadBtn').click(function() {
      reloadDataCb();
    });
    this.periodSel.change(function() {
      reloadDataCb();
    });
    this.chart.setTooltipContent(this.tooltipContent);
  };
  DosStatsTime.prototype = {
    resetData: function() {
      this.dataset.resetData();
      this.volDetail = null;
      this.synDetail = null;
    },
    getPeriodSelector: function() {
      return this.periodSel.val();
    },
    tooltipContent: function(d) {
      var date = d3.time.format('%Y-%m-%d  %H:%M')(new Date(d.iniDate)),
        html = '<span><b>'+date+'</b></span><table>',
        top = (d.details.top == CONST.N_A_VALUE ? 'none' : d.details.top);

      html += '<tr><td>Duration</td><td><b>'+d.duration+'</b></td></tr>';
      html += '<tr><td>IP Address</td><td><b>'+d.details.ip+'</b></td></tr>';
      switch (d.type) {
      case 0:
        html += '<tr><td>Mbytes</td><td><b>'+d.details.megabytes+'</b></td></tr>';
        html += '<tr><td>Rate</td><td><b>'+d.details.rate+'</b></td></tr>';
        html += '<tr><td>Top Contributor</td><td><b>'+top+'</b></td></tr>';
        html += '<tr><td>Top Mbytes</td><td><b>'+d.details.topmbytes+'</b></td></tr>';
        break;
      case 1:
      case 2:
        html += '<tr><td>Fail SYN</td><td><b>'+d.details.failsyn+'</b></td></tr>';
        html += '<tr><td>Max Rate</td><td><b>'+d.details.maxrate+'</b></td></tr>';
        html += '<tr><td>Top Contributor</td><td><b>'+top+'</b></td></tr>';
        html += '<tr><td>Top Fail-SYN</td><td><b>'+d.details.topfailsyn+'</b></td></tr>';
        break;
      }
      html += '</table>';
      return (html);
    },
    dump: function(intervals, text) {
      if (text !== undefined) console.log(text);
      for (var i = 0, len = intervals.length; i < len; i++) {
        console.log('INTERVAL[%d]: (%s, %s)', i,
          this.formatDate(intervals[i].iniDate),
          this.formatDate(intervals[i].finDate));
      }
    },
    createInterval: function(date, lenStr, type, details) {
      var interval = {iniDate:null, finDate:null, level:null, duration: lenStr,
        id: this.id++, type: type, details: details},
        duration = function(lenStr) {
          var chunk = lenStr.split(':');
          return moment.duration(Number(chunk[2])+
            Number(chunk[1])*60+
            Number(chunk[0])*3600, 'seconds');
        };

      interval.iniDate = moment(date),
      interval.finDate = moment(interval.iniDate),
      interval.finDate.add(duration(lenStr), 'seconds');

      interval.iniDate = interval.iniDate.toDate();
      interval.finDate = interval.finDate.toDate();

      //console.log('creating interval: %s', JSON.stringify(interval));
      return interval;
    },
    parseVol: function(respVol) {
      var that = this,
        VOL_TYPE = 0,
        interval;

      parseStrTable(respVol, function(attrs) {
        var details = {};
        details.ip = attrs[1];
        details.megabytes = attrs[4];
        details.rate = attrs[5];
        details.top = attrs[7];
        details.topmbytes = attrs[8];
        interval = that.createInterval(attrs[0], attrs[6], VOL_TYPE, details);
        that.dataset.pushData(interval);
      });
    },
    parseSyn: function(respSyn) {
      var that = this,
        interval,
        type,
        getType = function (side, direction) {
          if (side == 'access') {
            if (direction == 'ingress') return 1;
            else return 2;
          } else {
            return null;
          }
        };

      parseStrTable(respSyn, function(attrs) {
        var details = {};
        details.ip = attrs[1];
        details.failsyn = attrs[4];
        details.maxrate = attrs[5];
        details.top = attrs[7];
        details.topfailsyn = attrs[8];
        type = getType(attrs[2], attrs[3]);
        if (type != null) {
          interval = that.createInterval(attrs[0], attrs[6], type, details);
          that.dataset.pushData(interval);
        }
      });
    },
    parse: function(respVol, respSyn) {
      this.parseVol(respVol);
      this.parseSyn(respSyn);

      // Sort intervals dataset in ascending date order
      this.dataset.storedValues.sort(function(a, b) {
        if (a.iniDate < b.iniDate) return -1;
        if (a.iniDate > b.iniDate) return 1;
        return 0;
      });

      //this.dataset.dumpStoredValues('DIRECT-ORDER:');
      // Reverse order has not been created yet
      //this.dataset.dumpStoredValuesR('REVERSE-ORDER:');
      //this.dump(this.dataset.storedValues, 'PARSE-LINES:');
    },
    selectRangeAndRender: function(range) {
      this.dataset.selectIntervalR(range);
      this.chart.render(range, this.dataset);
    },
    execute: function(range, respVol, respSyn) {
      this.resetData();
      this.parse(respVol, respSyn);
      if (this.dataset.storedValues.length > 0) {
        this.noDataShow.hide();
        this.dataShow.show();
        this.timeline.render(range, this.selectRangeAndRender);
      } else {
        this.dataShow.hide();
        this.noDataShow.text(CONST.NO_ATTACKS_FOUND);
        this.noDataShow.show();
      }
    },
  };

  return DosStatsTime;
})();

//
// PolicySubs
//
var PolicySubs = (function() {
  var CONST = {
    POLICY: [
      {type:'flow', tab:'flows', tabName:'flowTblName'},
      {type:'rate', tab:'rates', tabName:'rateTblName'},
      {type:'monitor', tab:'mons', tabName:'monTblName'},
    ],
  };

  function PolicySubs(name) {
    var that = this;

    this.jqSelector = '#'+name;

    // Table names are used in CONST.POLICY
    this.flowTblName = this.jqSelector+'FlowTbl';
    this.rateTblName = this.jqSelector+'RateTbl';
    this.monTblName = this.jqSelector+'MonTbl';

    this.subsTitleSel = $(this.jqSelector+'SingleTitle');
    this.subsHelpSel = $(this.jqSelector+'SingleHelpBtn');
    this.subsReloadSel = $(this.jqSelector+'SingleReloadBtn');
    this.subsLinesSel = $(this.jqSelector+'SingleLines');
    this.subsTblHdrSel = $(this.jqSelector+'SingleTbl thead');
    this.subsTblName = this.jqSelector+'SingleTbl';
    this.subsPolicy = $(this.jqSelector+'SinglePolicy');
    this.subsModal = $(this.jqSelector+'Modal');
    this.textSel = $(this.jqSelector+'Text');

    this.viewSingle = 'view'+capitalize(name)+'Single';

    $(this.jqSelector+'HelpBtn').click(function() {
      $(that.jqSelector+'Help').modal({backdrop: true});
    });
    $(this.jqSelector+'ReloadBtn').click(function() {
      if (isDataTableInit('#policySubsFlowTbl') == true) {
        $('#policySubsFlowTbl').DataTable().destroy();
      }
      if (isDataTableInit('#policySubsRateTbl') == true) {
        $('#policySubsRateTbl').DataTable().destroy();
      }
      if (isDataTableInit('#policySubsMonTbl') == true) {
        $('#policySubsMonTbl').DataTable().destroy();
      }
      that.execute();
    });

    $(this.jqSelector+'SingleReturnBtn').click(function() {
      globalNavigate('view'+capitalize(name));
    });
  };
  PolicySubs.prototype = {
    toPolyName: function(name) {
      return '<span class="hyperlink-text">'+name+'</span>';
    },
    toSubsName: function(name) {
      return '<span class="hyperlink-text">'+name+'</span>';
    },
    showSubsData: function(addr) {
      subsStatus.showDetail(addr);
    },
    renderSubsRow: function(type, tblName, entry) {
      var that = this;
      tableRow.initialize(tblName);
      tableRow.addAttribute(this.toSubsName(entry.addr), null, function() {
        that.showSubsData(entry.addr);
      });
      tableRow.addAttribute(entry.active, 'class="align-right"');
      tableRow.addAttribute(entry.created, 'class="align-right"');
      tableRow.addAttribute(entry.bytesup, 'class="align-right"');
      tableRow.addAttribute(entry.bytesdown, 'class="align-right"');
      tableRow.addAttribute(entry.lifetime, 'class="align-right"');

      tableRow.render();
    },
    renderSubsFlowRow: function(type, tblName, entry) {
      var that = this;
      tableRow.initialize(tblName);
      // 3.l
      //tableRow.addAttribute(entry.addr);
      tableRow.addAttribute(this.toSubsName(entry.addr), null, function() {
        that.showSubsData(entry.addr);
      });
      tableRow.addAttribute(entry.cnt, 'class="align-right"');
      tableRow.addAttribute(entry.bup, 'class="align-right"');
      tableRow.addAttribute(entry.bdown, 'class="align-right"');

      tableRow.render();
    },
    parsePolicyData: function(type, name) {
      var that = this,
        lines = this.subsLinesSel.val();

      if (lines < 1) {
        lines = 1;
        this.subsLinesSel.val(1);
      }
      // ReloadBtn callback
      this.subsReloadSel.off().click(function() {
        that.parsePolicyData(type, name);
      });
      $(this.subsTblName+' tbody').empty();

      switch (type) {
      case 'flow':
        globalNavigate("viewPolicyFlowEdit", {returnView: "viewPolicySubs", policy: name});
        break;
      case 'rate':
        ifCl.execute('show policy ' + safeStr(name), function(res) {
          resp = res.CmdResponse.response;
          const policy = parsePolicyRate(resp, responsePolicyMap);
          const { source } = policy;
          if (source === 'config' || source === 'static' ) {
            globalNavigate("viewPolicyRateEdit", {returnView: "viewPolicySubs", policy: name});
          } else {
            showDynPolicyRate(name);
          }
        });
        break;
      case 'monitor':
        globalNavigate("viewPolicyMonitorEdit", {returnView: "viewPolicySubs", policy:name})
        break;
      }
    },
    renderPolicyRow: function(type, tabName, entry) {
      var that = this;
      tableRow.initialize(tabName);
      if (type == 'flow' && !functionality.isEnabled('dpi-per-user')) {
        tableRow.addAttribute(entry.name);
      } else {
        tableRow.addAttribute(this.toPolyName(entry.name), null, function() {
          that.parsePolicyData(type, entry.name);
        });
      }
      tableRow.addAttribute(formatLargeStrNum(entry.cnt, 0),
        'class="align-right"');
      tableRow.addAttribute(formatLargeStrNum(entry.downRcv, 0),
        'class="align-right"');
      tableRow.addAttribute(formatLargeStrNum(entry.downSnt, 0),
        'class="align-right"');
      tableRow.addAttribute(formatLargeStrNum(entry.upRcv, 0),
        'class="align-right"');
      tableRow.addAttribute(formatLargeStrNum(entry.upSnt, 0),
        'class="align-right"');

      tableRow.render();
    },
    renderPolicyTbl: function(type, tabName, tab) {
      $(tabName+' tbody').empty();
      for (var i = 0, len = tab.length; i < len; i++) {
        this.renderPolicyRow(type, tabName, tab[i]);
      }

      if (isDataTableInit(tabName) == false) {
        $(tabName).DataTable({
          "pageLength": 50
        });
      }
    },
    parsePolicyTbl: function(type, tabName) {
      var that = this, tab = [];

      ifCl.execute('show policy '+safeStr(type)+' statistics', function(res) {
        parseStrTable(res.CmdResponse.response, function(attrs) {
          if (attrs[0] == '') attrs.shift();
          var entry = {
            name: attrs[0],
            downRcv: attrs[1],
            downSnt: attrs[2],
            upRcv: attrs[3],
            upSnt: attrs[4],
            cnt: attrs[5],
          };
          tab.push(entry);
        });

        that.renderPolicyTbl(type, tabName, tab);
      });
    },
    execute: function() {
      var that = this;

      CONST.POLICY.forEach(function(it, i) {
        that.parsePolicyTbl(it.type, that[it.tabName]);
      });
    },
  };

  return PolicySubs;
})();


//
// DosStats
//
var DosStats = (function() {
  var CONST = {
    TITLE_NAME: 'Title',
    RELOAD_BTN_NAME: 'ReloadBtn',
    HELP_BTN_NAME: 'HelpBtn',
    HELP_NAME: 'Help',
    APPLY_BTN: 'ApplyBtn',
    N_A_VALUE: 'n/a',
    DFLT_LINES: '10000',
  };

  function DosStats(name, title) {
    var that = this;

    this.jqSelector = '#'+name;
    this.tabHeadName = this.jqSelector+'Tbl thead';
    this.tabBodyName = this.jqSelector+'Tbl tbody';
  };
  DosStats.prototype = {
    toDate: function(rawDate) {
    },
    renderRow: function(entry) {
      tableRow.initialize(this.tabBodyName);
      tableRow.addAttribute(entry.date);
      tableRow.addAttribute(entry.cli);
      tableRow.addAttribute(entry.type);
      tableRow.addAttribute(entry.side);
      tableRow.addAttribute(entry.dir);
      tableRow.addAttribute(entry.dur);

      tableRow.render();
    },
    executeSummary: function() {
      var that = this;

      $(this.tabHeadName).html('<tr> <th>TIME</th> <th>IP-ADDRESS</th> <th>TYPE</th> <th>SIDE</th> <th>DIRECTION</th> <th>DURATION</th> </tr>');
      $(this.tabBodyName).empty();
      ifCl.execute('show statistics dos hours 744', function(res) {
        parseStrTable(res.CmdResponse.response, function(attrs) {
          entry = {
            date: attrs[0],
            cli: attrs[1],
            type: attrs[2],
            side: attrs[3],
            dir: attrs[4],
            dur: attrs[5],
          };
          that.renderRow(entry);
        });
      });
    },
  }
  return DosStats;
})();

// ATTENTION! if undefined userPlaceholder or userPlaceholder without CIDR
// wil accept plain ip adresses else cidr adresses.
function inputIpAddrCombo(baseName, inputName, btnName, fireAction, userPlaceholder) {
  var that = this,
    INPUT_ADDR_COMBO_PLACEHOLDER = 'Ex: 192.168.1.1',
    INPUT_ADDR_COMBO_CIDR_PLACEHOLDER = 'Ex: 10.1.0.0/16 or 2001:db8:80::/48',
    INPUT_ADDR_COMBO_ERROR = 'Invalid IP Address',
    input = $('#'+baseName+inputName),
    btn = $('#'+baseName+btnName),
    isCidr = userPlaceholder == undefined || userPlaceholder.indexOf('/') > -1,
    validateAddrF = (isCidr ? IpV4V6.isValidCidrOrAddr : IpV4V6.isValid),
    placeholder = (userPlaceholder === undefined ?
      INPUT_ADDR_COMBO_CIDR_PLACEHOLDER : userPlaceholder),
    validateAndFire = function() {
      var value = input.val().trim();
      if (!validateAddrF(value)) {
        input.val('');
        input.attr('placeholder', INPUT_ADDR_COMBO_ERROR);
      } else {
        input.attr('placeholder', placeholder);
        fireAction(value);
      }
      $(btn).hide();
    };

  input.attr('placeholder', placeholder);

  input.on('keyup click', function(e) {
    var isEmpty = (input.val().trim().length === 0);
    // Update placeholder text and button visibility
    // When empty (after validation error) remove error message.
    if (isEmpty) {
      input.attr('placeholder', placeholder);
      $(btn).hide();
    } else {
      $(btn).show();
    }

    // 'Return' key acts like:
    //   'Filter' when value present
    //   'Reload' when value absent
    if (e.keyCode === 13) {
      if (isEmpty) {
        fireAction('');
      } else {
        validateAndFire();
      }
    }
  });

  input.get(0).addEventListener('focusin', function() {
    //console.log('FOCUS-IN: len=%d', focusLen);
  });

  btn.click(function() {
    validateAndFire();
  });
};

function inputTargetCombo(baseName, addressInputName, btnName, fireAction, userPlaceholder) {
  var that = this,
    INPUT_ADDR_COMBO_PLACEHOLDER = 'Ex: 192.168.1.1',
    INPUT_ADDR_COMBO_CIDR_PLACEHOLDER = 'Ex: 10.1.0.0/16 or 2001:db8:80::/48',
    INPUT_ADDR_COMBO_ERROR = 'Invalid IP Address',
    targetSelect = $('#'+baseName+'TargetFilter'),
    addressInput = $('#'+baseName+addressInputName),
    subsIdInput = $('#'+baseName+'SubsId'),
    iconWrapper = $('<span class="input-group-addon"></span>').insertAfter(targetSelect),
    icon = $('<i class="material-icons">devices</i>').appendTo(iconWrapper),
    btn = $('#'+baseName+btnName),
    isCidr = userPlaceholder == undefined || userPlaceholder.indexOf('/') > -1,
    validateAddrF = (isCidr ? IpV4V6.isValidCidrOrAddr : IpV4V6.isValid),
    placeholder = (userPlaceholder === undefined ?
      INPUT_ADDR_COMBO_CIDR_PLACEHOLDER : userPlaceholder),
    validateAndFireAddress = function() {
      var value = addressInput.val().trim();
      if (!validateAddrF(value)) {
        addressInput.val('');
        addressInput.attr('placeholder', INPUT_ADDR_COMBO_ERROR);
      } else {
        addressInput.attr('placeholder', placeholder);
        fireAction(value);
      }
    },
    validateAndFireSubsId = function() {
      var value = subsIdInput.val().trim();
      fireAction(value);
    },
    validateAndFire = function() {
      var value = targetSelect.val();
      if (value === 'address') {
        validateAndFireAddress();
      } else {
        validateAndFireSubsId();
      }
      $(btn).hide();
    };
    if (targetSelect.length === 0) {
    console.warn('Not found select with sel: #'+baseName+'TargetFilter');
    return
    }

    targetSelect.change(function(event) {
      const wrapper = $(event.target.parentElement);
      wrapper.find('input').hide();
      wrapper.find(`input[for="${event.target.value}"]`).show();
      icon.text( event.target.value === 'subs_id' ? 'person_outline' : 'devices' );
    })
    targetSelect.val('address').trigger('change');

  addressInput.attr('placeholder', placeholder);

  addressInput.on('keyup click', function(e) {
    var isEmpty = (addressInput.val().trim().length === 0);
    // Update placeholder text and button visibility
    // When empty (after validation error) remove error message.
    if (isEmpty) {
      addressInput.attr('placeholder', placeholder);
      $(btn).hide();
    } else {
      $(btn).show();
    }

    // 'Return' key acts like:
    //   'Filter' when value present
    //   'Reload' when value absent
    if (e.keyCode === 13) {
      if (isEmpty) {
        fireAction('');
      } else {
        validateAndFire();
      }
    }
  });
    subsIdInput.on('keyup click', function(event) {
    var isEmpty = (event.target.value.length === 0);
    // Update placeholder text and button visibility
    // When empty (after validation error) remove error message.
    if (isEmpty) {
      $(btn).hide();
    } else {
      $(btn).show();
    }

    // 'Return' key acts like:
    //   'Filter' when value present
    //   'Reload' when value absent
    if (event.keyCode === 13) {

      if (isEmpty) {
        fireAction('');
      } else {
        validateAndFire();
      }
    }
    });


  btn.click(function() {
    validateAndFire();
  });
};

//
// SysMem
//
var SysMem = (function() {
  var CONST = {
    ANIMATION_DURATION: 100,
  };

  function SysMem(reloadDataCb, name) {
    var that = this;

    this.tooltipContentGenerator = null;
    this.jqSelector = '#'+name;
    this.tooltipMgr = new TooltipMgr();

    this.data = null;

    $(this.jqSelector+'ReloadBtn').click(function() {
      reloadDataCb();
    });
    $(this.jqSelector+'HelpBtn').click(function() {
      $(that.jqSelector+'Help').modal({backdrop: true});
    });
  };
  SysMem.prototype = {
    render: function() {
      var that = this,
        height;

      // Remove previous tooltip if present
      this.tooltipMgr.clear();
      // Clear previous nvd3 chart (remove all 'g' children)
      $(this.jqSelector + ' svg').find('*').remove();

      height = Math.ceil((this.data[0].values.length+1) * 60).toString();
      $(this.jqSelector + ' svg').attr('height', height);
      console.log('if.throughput.svg.height: %s', height);

      nv.addGraph(function() {
        var chart = nv.models.multiBarHorizontalChart()
          .margin({top: 30, right: 50, bottom: 30, left: 70})
          .x(function(d) { return d.label })
          .y(function(d) { return d.value })
          .stacked(true)
          .showControls(false)
          .showValues(true)
          .noData(NO_DATA_AVAILABLE)
          ;

        chart.color(colorsGeneric);

        const numbersWithThreeDecimals = d3.format(',.3f');

          chart.tooltip.valueFormatter(function(d) {
            return (numbersWithThreeDecimals(d) + ' GB');
          });

        chart.yAxis
          .showMaxMin(true)
          .ticks(7)
          .tickFormat(numbersWithThreeDecimals)
          ;

          //chart.forceY([0, 100]);
          d3.select(that.jqSelector+' svg')
            .datum(that.data)
            .transition().duration(CONST.ANIMATION_DURATION)
            .call(chart)
            ;

         // Detection of mouseleave is not needed

          nv.utils.windowResize(chart.update);

        return chart;
      });
    },
    createData: function(totSys, useSys, totMpool, useMpool,
      totDpdk, useDpdk) {
      this.data = [
        {key:'Used',color:'#1f77b4',values:[
          {label: 'System',value:useSys / 1000},
          {label: 'Mpool',value:useMpool / 1000},
          {label: 'Dpdk',value:useDpdk / 1000}]},
        {key:'Free',color:'#cacaca',values:[
          {label: 'System',value:totSys / 1000},
          {label: 'Mpool',value:totMpool / 1000},
          {label: 'Dpdk',value:totDpdk / 1000}]}
        ];
      console.log('data: %s', JSON.stringify(this.data));
    }
  };

  return SysMem;
})();


//
// MultiColStats
//
var MultiColStats = (function() {
  var CONST = {
    N_A_VALUE: 'n/a',
    POINTS_PER_CHART_MAX: 288,
    ANIMATION_DURATION: 200,
  };

  function MultiColStatsDataset(key, ppc) {
    // Call the parent constructor
    Dataset.call(this, key, ppc);
  }
  // MultiColStatsDataset.prototype = new Dataset();
  // This way we avoid calling Dataset constructor on 'new'
  MultiColStatsDataset.prototype = Object.create(Dataset.prototype);
  MultiColStatsDataset.prototype.constructor = MultiColStatsDataset;
  /* TEST
  MultiColStatsDataset.prototype.resetData = function() {
    Dataset.prototype.resetData.call(this);
    console.log('TE-PILLE!!!');
  };
  */

  function MultiColStatsTile(name) {
    this.units = '';
    this.tooltipContentGenerator = null;
    this.jqSelector = '#'+name;
    this.tooltipMgr = new TooltipMgr();
    this.threshold = null;
    this.data = null;
  };
  MultiColStatsTile.prototype = {
    setUnits: function(units) {
      this.units = units;
      currentChartUnits = units;
    },
    setTooltipContentGenerator: function(tooltipContentGenerator) {
      this.tooltipContentGenerator = tooltipContentGenerator;
    },
    setData: function(data) {
      this.data = data;
    },
    remove: function() {
      // Remove previous tooltip if present
      this.tooltipMgr.clear();

      // Clear previous nvd3 chart (remove all 'g' children)
      $(this.jqSelector + ' svg').find('*').remove();
      // $(this.jqSelector + ' svg:nth-of-type(1)').find('*').remove();
    },
    setThresholdLine: function(value) {
      this.threshold = value;
    },
    render: function() {
      var that = this,
        svgSelector = this.jqSelector + ' svg',
        yMax,
        dataLimits = function(data) {
          var dataset, val, max = 0, isZeroOrNull = true;
          for (var i = 0; i < data.length; i++) {
            dataset = data[i].values;
            for (var j = 0, len = dataset.length; j < len; j++) {
              val = dataset[j].value;
              if (val !=  null) {
                if (val != 0) isZeroOrNull = false;
                if (val > max) max = val;
              }
            }
          }
          return {isZero: isZeroOrNull, max: max};
        },
        limits = dataLimits(that.data),
        ULTP = 1.25;

      this.remove();

      $(svgSelector).attr('height', Math.ceil(window.innerHeight/2));

      nv.addGraph(function() {
        var chart = nv.models.lineChart()
          .margin({top: 20, right: 40, bottom: 30, left: 60})
          .x(function(d) { return d.date; })
          .y(function(d) { return d.value; })
          .xScale(d3.time.scale())
          .useInteractiveGuideline(true) // This affects tooltip behavior
          .showXAxis(true)
          .showLegend(true) // Show legend
          .noData(NO_DATA_AVAILABLE)
          .duration(CONST.ANIMATION_DURATION)
          ;

        chart.color(colorsGeneric);
        chart.interactiveLayer.tooltip.enabled(true);
        chart.interactiveLayer.tooltip.hideDelay(0);
        chart.interactiveLayer.tooltip.gravity('w'); // 's', 'n', 'w', 'e'
        if (that.tooltipContentGenerator !== null) {
          chart.interactiveLayer.tooltip.contentGenerator(that.tooltipContentGenerator);
        }

        chart.xAxis
          .ticks(8)
          .tickFormat(function(d) {
            return d3.time.format('%d-%m %H:%M')(new Date(d))
          })
          ;

        /* Alternative allDataZeroOrNan test
        for (var i = 0, allDataZeroOrNan = true; i < that.data.length; i++) {
          if (!that.data[i].isZeroOrNaN) {
            allDataZeroOrNan = false;
            break;
          }
        }
        */
        /*
        // Test Zero or NaN values
        for (var i = 0, allDataZeroOrNan = true; i < that.data.length; i++) {
          //console.log('TESTING Y-SCALE FOR DATASET %d', i);
          if (!isZeroOrNull(that.data[i].values, 'value')) {
            allDataZeroOrNan = false;
            break;
          }
        }
        */

        if (limits.isZero) {
          console.log('FORCING Y-SCALE');
          chart.forceY([0,1]);
        } else {
          chart.forceY([0, null]);
        }

        if (!limits.isZero && that.threshold &&
          that.threshold < (limits.max * ULTP) && that.threshold > (limits.max * 0.90)) {
          yMax = limits.max * ULTP;
        } else {
          yMax = limits.max;
        }

        // NVD3 workaround
        if (that.threshold) {
          chart.yDomain([0, yMax]);
        }

        chart.yAxis
          //.scale(d3.scale.linear().domain([0, yMax]))
          .axisLabel(that.units)
          .ticks(6)
          .showMaxMin(false)
          .tickFormat(function(d) {
            if (d == 0) {
              return d3.format('.0f')(d);
            } else if (d >= 10.0) {
              return d3.format('.0f')(d);
            } else if (d >= 1.0) {
              return d3.format('.1f')(d);
            } else {
              return d3.format('.2f')(d);
            }
          })
          ;

        d3.select(svgSelector)
          .datum(that.data)
          .call(chart)
          ;

        d3.select(that.jqSelector).select(".nv-lineChart")
          .on("mouseleave", function() {
            that.tooltipMgr.onMouseLeave(chart);
          })
          ;

        nv.utils.windowResize(chart.update);

        // If data-found and limit-defined
        //if (!allDataZeroOrNan && that.threshold &&
        if (!limits.isZero && that.threshold &&
          that.threshold <= (yMax * ULTP)) {
          var xAxisScale = chart.xAxis.scale(),
            yAxisScale = chart.yAxis.scale(),
            xPos = xAxisScale(that.data[0].getSelectedInterval().to),
            yPos = yAxisScale(that.threshold);

          var licLimit = d3.select(svgSelector+' .nvd3');
          licLimit.append('line')
            .style('stroke', 'red')
            .style('stroke-width', 1)
            .style('opacity', 0.4)
            .attr('x1', 0)
            .attr('y1', yPos)
            .attr('x2', xPos)
            .attr('y2', yPos)
            ;
          /* TEMPORARY DISABLED
          */
          licLimit.append('text')
            .text('License')
            .style('stroke', 'red')
            .style('font-size', '10px')
            .style('opacity', 0.4)
            .attr('x', (xPos - 50))
            .attr('y', (yPos - 6))
            ;
        }

        return chart;
      });
    },
  };

  // Options:
  //   columnNameF: (function) mapping from CLI column name to Dataset name
  //   newF: (function) function to execute in object creation (new)
  function MultiColStats(reloadDataCb, name, options) {
    var that = this,
      opts = options || {},
      jqSelector = '#'+name;

    this.data = []; // Container for datasets
    this.tile = new MultiColStatsTile(name);
    this.parseDate = d3.timeParse('%Y-%m-%dT%H:%M:%S');
    this.range = $('#select' +
      name.charAt(0).toUpperCase() + name.slice(1) + 'Range');
    this.sliderSelector = '#range' +
      name.charAt(0).toUpperCase() + name.slice(1);
    this.timeline = new TimeSlider(this, this.sliderSelector );

    this.parserUserItemF = null;
    this.parser = new window.TimeSeries();
    this.parser.setHeaderF(function(lines) {
      var names = lines[0].split(/\s+/),
        columnNameF = (opts.columnNameF ? opts.columnNameF : name => name);

      names.shift(); // Remove <head_spaces> item
      names.shift(); // Remove 'TIME' item
      for (var i = 0; i < names.length; i++) {
        this.datasets[i] = new MultiColStatsDataset(columnNameF(names[i]));
      }
      return 1; // Hdr number of lines
    });
    this.parser.setGapF('minutes', 5, function(date) {
      for (var i = 0; i < this.datasets.length; i++) {
        //this.datasets[i].pushData({date: date, value: null}); // Alternative
        this.datasets[i].storedValues.push({date: date, value: null});
      }
    });
    this.parser.setLineF(function(date, attrs) {
      var valueStr,
        itemF = (that.parserUserItemF ? that.parserUserItemF : function(a, i) { return a; });
      for (var i = 0; i < this.datasets.length; i++) {
        valueStr = attrs[i+1];
        //this.datasets[i].pushData({date: date, // Alternative
        this.datasets[i].storedValues.push({date: date,
          value: (valueStr != CONST.N_A_VALUE ? itemF(Number(valueStr), i) : null)});
      }
    });
    this.parser.setEndF(function() {
      console.log('MultiColStats.parser.endF: entries=%d', this.datasets[0].storedValues.length);
      for (let ds of this.datasets) {
        console.log('MultiColStats.parser.endF: key=%s, isZeroOrNaN=%s', ds.key, ds.isZeroOrNaN);
      }
    });

    this.range.change(function() {
      reloadDataCb();
    });
    $(jqSelector+'ReloadBtn').click(function() {
      reloadDataCb();
    });
    $(jqSelector+'HelpBtn').click(function() {
      $(jqSelector+'Help').modal({backdrop: true});
    });

    if (opts.newF) opts.newF(this);
  };
  MultiColStats.prototype = {
    resetData: function() {
      this.data = [];
    },
    getPeriodSelector: function() {
      return this.range.get(0).value;
    },
    setPeriodSelector: function(range) {
      this.range.val(range);
    },
    setParserUserItemF: function(f) {
      this.parserUserItemF = f;
    },
    setDatasetEvalSelectionF: function(f) {
      // ATTENTION! the function will be executed in the Dataset context
      MultiColStatsDataset.prototype.evalSelection = f;
    },
    parse: function(resp, period, dateStr) {
      var lines = resp.split('\n'),
        period = new Period(period, dateStr);

      this.resetData();
      this.data = this.parser.process(period, lines);
    },
    setUnits: function(units) {
      this.tile.setUnits(units);
    },
    setThresholdLine: function(value) {
      this.tile.setThresholdLine(value);
    },
    setTooltipContentGenerator: function(tooltipContentGenerator) {
      this.tile.setTooltipContentGenerator(tooltipContentGenerator);
    },
    selectIntervalAndRender: function(interval) {
      this.data[0].selectInterval(interval);
      var params = this.data[0].getSelectedParameters();
      for (var i = 0; i < this.data.length; i++) {
        //this.data[i].selectInterval(interval);
        this.data[i].selectIntervalUsingParameters(interval, params);
      }
      this.tile.render();
    },
    render: function() {
      this.tile.setData(this.data);
      this.timeline.render(this.data[0].getStoredInterval(),
        this.selectIntervalAndRender);
    }
  };

  return MultiColStats;
})();


//
// MultiColBarStats
//
var MultiColBarStats = (function() {
  var CONST = {
    N_A_VALUE: 'n/a',
    POINTS_PER_CHART_MAX: 288,
    ANIMATION_DURATION: 200,
    COLORS: '#d62728#1f78b4#a6cee3#33a02c#b2df8a',
  };

  function MultiColBarStatsDataset(key, ppc) {
    // Call the parent constructor
    Dataset.call(this, key, ppc);
  }
  // MultiColBarStatsDataset.prototype = new Dataset();
  // This way we avoid calling Dataset constructor on 'new'
  MultiColBarStatsDataset.prototype = Object.create(Dataset.prototype);
  MultiColBarStatsDataset.prototype.constructor = MultiColBarStatsDataset;
  MultiColBarStatsDataset.prototype.evalSelection = function() {
    //console.log('TE-PILLE !!!!!!!!!!!!!!! %s', this.key);
    // From this.low to this.high [closed interval]
    // Samples are decimated at 'subsample' rate,
    // choosing the maximum of attribute values in the interval
    var oriItem,
      maxItem,
      oriAttrValue;

    this.values = [];
    if (this.high !== 0 || this.low !== 0) {
      // Iterate range [this.low,this.high]
      for (var i = 0, j = this.low; j <= this.high; i++) {
        // Create new maxItem for subsamples chunk
        maxItem = {date: this.storedValues[j].date};
        // Iterate subsamples chunk
        for (len = j + this.subsample; j < len; j++) {
          oriItem = this.storedValues[j];
          for (attr in oriItem) {
            if (oriItem.hasOwnProperty(attr)) {
              if (attr != 'date') {
                oriAttrValue = this.storedValues[j][attr];
                // Add attribute to maxItem if undefined
                if (maxItem[attr] === undefined) {
                  // Add attribute to maxItem if undefined
                  maxItem[attr] = oriAttrValue;
                } else if (oriAttrValue !== null) {
                    if (oriAttrValue > maxItem[attr]) maxItem[attr] = oriAttrValue;
                }
              }
            }
          }
        }
        this.values[i] = maxItem;
      }
    }
  };

  function MultiColBarStats(reloadDataCb, name, columnToChartName) {
    var that = this;

    this.atkCnt = {vol:0, syn:0};
    this.data = []; // Container for datasets
    this.units = '';
    this.columnToChartName = columnToChartName;
    this.tooltipContentGenerator = null;
    this.tooltipMgr = new TooltipMgr();
    this.jqSelector = '#'+name;
    this.synTabName = this.jqSelector+'SynTbl';
    this.volTabName = this.jqSelector+'VolTbl';
    this.synTabShowSel = $(this.jqSelector+'SynShow');
    this.volTabShowSel = $(this.jqSelector+'VolShow');

    this.parseDate = d3.timeParse('%Y-%m-%dT%H:%M:%S');
    this.range = $(this.jqSelector + 'Range');
    this.timeline = new TimeSlider(this, this.jqSelector+'Slider');

    this.parser = new TimeSeries();
    this.parser.setHeaderF(function(lines) {
      var names = lines[0].split(/\s+/);

      names.shift(); // Remove <head_spaces> item
      names.shift(); // Remove 'TIME' item
      for (var i = 0; i < names.length; i++) {
        this.datasets[i] = new MultiColBarStatsDataset(that.columnToChartName(names[i]));
        this.datasets[i].color = CONST.COLORS.substring(i*7, i*7+7);
      }
      return 1; // Hdr number of lines
    });
    this.parser.setGapF('minutes', 5, function(date) {
      for (var i = 0; i < this.datasets.length; i++) {
        //this.datasets[i].pushData({date: date, value: null}); // Alternative
        this.datasets[i].storedValues.push({date: date, value: null});
      }
    });
    this.parser.setLineF(function(date, attrs) {
      for (var i = 0; i < this.datasets.length; i++) {
        valueStr = attrs[i+1];
        //this.datasets[i].pushData({date: date, // Alternative
        this.datasets[i].storedValues.push({date: date,
          value: (valueStr != CONST.N_A_VALUE ? Number(valueStr) : null)});
      }
    });

    this.range.change(function() {
      reloadDataCb();
    });
    $(this.jqSelector+'ReloadBtn').click(function() {
      reloadDataCb();
    });
    $(this.jqSelector+'HelpBtn').click(function() {
      $(that.jqSelector+'Help').modal({backdrop: true});
    });
  };
  MultiColBarStats.prototype = {
    resetData: function() {
      this.data = [];
      this.syn = [];
      this.vol = [];
    },
    getPeriodSelector: function() {
      return this.range.get(0).value;
    },
    setParserUserItemF: function(f) {
      this.parserUserItemF = f;
    },
    renderTile: function() {
      var that = this;

      // Remove previous tooltip if present
      this.tooltipMgr.clear();

      // Clear previous nvd3 chart (remove all 'g' children)
      $(this.jqSelector + ' svg').find('*').remove();
      // $(this.jqSelector + ' svg:nth-of-type(1)').find('*').remove();

      $(this.jqSelector + ' svg').attr('height', Math.ceil(window.innerHeight/2));

      nv.addGraph(function() {
        var chart = nv.models.multiBarChart()
          .margin({top: 20, right: 40, bottom: 30, left: 60})
          .x(function(d) { return d.date; })
          .y(function(d) { return d.value; })
          .showControls(false)
          .stacked(true)
          .useInteractiveGuideline(true) // This affects tooltip behavior
          .showLegend(true) // Show legend
          .noData(NO_DATA_AVAILABLE)
          .duration(CONST.ANIMATION_DURATION)
          ;

        chart.color(colorsGeneric);
        chart.interactiveLayer.tooltip.enabled(true);
        chart.interactiveLayer.tooltip.hideDelay(0);
        chart.interactiveLayer.tooltip.gravity('w'); // 's', 'n', 'w', 'e'
        if (that.tooltipContentGenerator !== null) {
          chart.interactiveLayer.tooltip.contentGenerator(that.tooltipContentGenerator);
        }

        // Test Zero or NaN values
        for (var i = 0, allDataZeroOrNan = true; i < that.data.length; i++) {
          //console.log('TESTING Y-SCALE FOR DATASET %d', i);
          if (!isZeroOrNull(that.data[i].values, 'value')) {
            allDataZeroOrNan = false;
            break;
          }
        }
        if (allDataZeroOrNan) {
          console.log('FORCING Y-SCALE');
          chart.forceY([0,1]);
        } else {
          if (that.units == '%') {
            chart.forceY([0, 100]);
          } else {
            chart.forceY([0, null]);
          }
        }

        chart.xAxis
          .ticks(8)
          .tickFormat(function(d) {
            return d3.time.format('%d-%m %H:%M:%S')(new Date(d))
          })
          ;

        chart.yAxis
          .axisLabel(that.units)
          .ticks(5)
          .tickFormat(function(d) {
            if ((d % 1) == 0) return d3.format('d')(d);
            else return null;
          })
          ;

        d3.select(that.jqSelector+' svg')
          .on("mouseleave", function() {
            that.tooltipMgr.onMouseLeave(chart);
          })
          .datum(that.data)
          .call(chart)
          ;

        d3.selectAll('.nv-bar')
          .on('click', function(d) {
            //console.log('CLICK-DATA: %s', JSON.stringify(d));
            that.showDetails(d.date);
          })
          ;

        nv.utils.windowResize(chart.update);

        return chart;
      });
    },
    renderSynTab: function(lines) {
      var tabBodyName = this.synTabName+' tbody',
        attrs;

      $(tabBodyName).empty();
      if (lines.length == 0) {
        this.synTabShowSel.hide();
      } else {
        for (var i = 0, len = lines.length; i < len; i++) {
          attrs = lines[i].split(/\s+/);
          tableRow.initialize(tabBodyName);
          tableRow.addAttribute(attrs[0].substring(0,10)+' '+
            attrs[0].substring(11));
          tableRow.addAttribute(attrs[6]);
          tableRow.addAttribute(attrs[1]);
          tableRow.addAttribute(attrs[2]);
          tableRow.addAttribute(attrs[4], 'class="align-right"');
          tableRow.addAttribute(attrs[5], 'class="align-right"');

          tableRow.addAttribute(attrs[7] == CONST.N_A_VALUE ? '-' : attrs[7]);
          tableRow.addAttribute(attrs[8], 'class="align-right"');
          tableRow.render();
        }
        this.synTabShowSel.show();
      }
    },
    renderVolTab: function(lines) {
      var tabBodyName = this.volTabName+' tbody',
        attrs;

      $(tabBodyName).empty();
      if (lines.length == 0) {
        this.volTabShowSel.hide();
      } else {
        for (var i = 0, len = lines.length; i < len; i++) {
          attrs = lines[i].split(/\s+/);
          tableRow.initialize(tabBodyName);
          tableRow.addAttribute(attrs[0].substring(0,10)+' '+
            attrs[0].substring(11));
          tableRow.addAttribute(attrs[6]);
          tableRow.addAttribute(attrs[1]);
          tableRow.addAttribute(attrs[4], 'class="align-right"');
          tableRow.addAttribute(attrs[5], 'class="align-right"');

          tableRow.addAttribute(attrs[7] == CONST.N_A_VALUE ? '-' : attrs[7]);
          tableRow.addAttribute(attrs[8], 'class="align-right"');
          tableRow.render();
        }
        this.volTabShowSel.show();
      }
    },
    getActiveAtacks: function(iniSint, cnt, allLines) {
      var ss = this.data[0].getSelectedParameters().subsample,
        finSint = moment(iniSint),
        selectedLines = [],
        iniAtk, finAtk,
        attrs,
        duration = function(durStr) {
          var chunk = durStr.split(':');
          return moment.duration(Number(chunk[2])+
            Number(chunk[1])*60+
            Number(chunk[0])*3600, 'seconds');
        },
        areOverlaping = function(iniSint, finSint, iniAtk, finAtk) {
          if (iniAtk.isBetween(iniSint, finSint) ||
            finAtk.isBetween(iniSint, finSint) ||
            iniSint.isBetween(iniAtk, finAtk) ||
            finSint.isBetween(iniAtk, finAtk)) return true;
          else return false;
        };

      // Compute interval end
      finSint.add(ss*5, 'minutes');

      // Locate first attack greater than finSint
      for (var i = 0, len = allLines.length; i < len; i++) {
        // isSameOrAfter ???
        if (moment(allLines[i].substring(0, 19)).isAfter(finSint)) break;
      }
      //console.log('FOUND-LAST-ATTACK: %s', allLines[i-1]);

      // Walk backwards until all active atacks are identified or no more data
      for (var j = i-1; j >= 0; j--) {
        attrs = allLines[j].split(/\s+/);
        iniAtk = moment(attrs[0]);
        finAtk = moment(iniAtk);
        finAtk.add(duration(attrs[6]));
        if (areOverlaping(iniSint, finSint, iniAtk, finAtk)) {
          selectedLines.unshift(allLines[j]);
          if (--cnt == 0) {
            return selectedLines;
          }
        }
      }
      return selectedLines;
    },
    showDetails: function(date) {
      var dateMom = moment(date);
      //console.log('CLICK-DATE: %s', moment(date).format('YYYY-MM-DD HH:mm:ss'));

      this.renderVolTab(this.getActiveAtacks(dateMom, this.atkCnt.vol, this.vol));
      this.renderSynTab(this.getActiveAtacks(dateMom, this.atkCnt.syn, this.syn));

      $(this.jqSelector+'DetailModal').modal({backdrop: true});
    },
    parse: function(respAttacs, respSyn, respVol, period, dateStr) {
      this.resetData();
      this.syn = respSyn.split('\n');
      if (this.syn.length > 2) {
        this.syn.shift();
        this.syn.pop();
      } else {
        this.syn = [];
      }
      this.vol = respVol.split('\n');
      if (this.vol.length > 2) {
        this.vol.shift();
        this.vol.pop();
      } else {
        this.vol = [];
      }
      this.data = this.parser.process(period, respAttacs.split('\n'));
    },
    setUnits: function(units) {
      this.units = units;
      currentChartUnits = units;
    },
    setTooltipContentGenerator: function(tooltipContentGenerator) {
      this.tooltipContentGenerator = tooltipContentGenerator;
    },
    selectIntervalAndRender: function(interval) {
      this.data[0].computeIntervalSelParameters(interval);
      var params = this.data[0].getSelectedParameters();
      for (var i = 0; i < this.data.length; i++) {
        //this.data[i].selectInterval(interval);
        this.data[i].selectIntervalUsingParameters(interval, params);
      }
      this.renderTile();
    },
    render: function() {
      this.timeline.render(this.data[0].getStoredInterval(),
        this.selectIntervalAndRender);
    }
  };

  return MultiColBarStats;
})();



//
// BtcStats
//
var BtcStats = (function() {
  var CONST = {
    TILE_PREFIX: '#btcStats',
    SELECT_PREFIX: '#selectBtcStats',
    CHART_NAME: 'Chart',
    RANGE_NAME: 'Range',
    BITS_UNIT: 1000000,
    ANIMATION_DURATION: 200,
    ATTRIBUTE_NAME: [
      'date',
      'numDetect',
      'numComp',
      'inBytes',
      'outBytes',
      'Compression Ratio'
    ],
    SELECTOR_NAME: [
      'Volume',
      'Compression Ratio',
    ],
  };

  function BtcStatsDataset(key) {
    // Call the parent constructor
    Dataset.call(this, key);
  }
  // BtcStatsDataset.prototype = new Dataset();
  // This way we avoid calling Dataset constructor on 'new'
  BtcStatsDataset.prototype = Object.create(Dataset.prototype);
  BtcStatsDataset.prototype.constructor = BtcStatsDataset;


  function BtcStatsTile() {
    this.units = '';
    this.tooltipContentGenerator = null;
    this.jqSelector = CONST.TILE_PREFIX;
    this.tooltipMgr = new TooltipMgr();
    this.data = null;
    this.inOutData = null;
    this.ratioData = null;
  };
  BtcStatsTile.prototype = {
    setUnits: function(units) {
      currentChartUnits = units;
      this.units = units;
    },
    setTooltipContentGenerator: function(tooltipContentGenerator) {
      this.tooltipContentGenerator = tooltipContentGenerator;
    },
    setData: function(data) {
      this.data = data;
      this.ratioData = [this.data[0]];
    },
    remove: function() {
      // Remove previous tooltip if present
      this.tooltipMgr.clear();

      // Clear previous nvd3 chart (remove all 'g' children)
      $(this.jqSelector + ' svg').find('*').remove();
      // $(this.jqSelector + ' svg:nth-of-type(1)').find('*').remove();
    },
    render: function(dataSelector) {
      var that = this,
        currentData = null,
        isNullOrZero = false;

      if (dataSelector == 'Volume') {
        this.data[0].key = 'In';
        this.data[1].key = 'Out';
        this.setUnits('MB');
        currentData = this.data;

        if (that.data[0].values.length > 0 || that.data[1].values.length > 0) {
          var extent0 = d3.extent(that.data[0].values, function(d)
            {return d[dataSelector]});
          var extent1 = d3.extent(that.data[1].values, function(d)
            {return d[dataSelector]});
          isNullOrZero = isZeroOrNull(that.data[0].values, dataSelector) &&
            isZeroOrNull(that.data[1].values, dataSelector);
        } else {
          isNullOrZero = true;
        }
      } else {
        this.data[0].key = 'Compression Ratio';
        this.setUnits('%');
        currentData = this.ratioData;

        if (that.data[0].values.length > 0) {
          var extent0 = d3.extent(that.data[0].values, function(d)
            {return d[dataSelector]});
          isNullOrZero = isZeroOrNull(that.data[0].values, dataSelector);
        } else {
          isNullOrZero = true;
        }
      }

      this.remove();

      nv.addGraph(function() {
        var chart = nv.models.lineChart()
          .margin({top: 20, right: 40, bottom: 30, left: 60})
          .x(function(d) { return d.date; })
          .y(function(d) { return d[dataSelector]; })
          .xScale(d3.time.scale())
          .useInteractiveGuideline(true) // This affects tooltip behavior
          .showXAxis(true)
          .showLegend(true) // Show legend
          .duration(CONST.ANIMATION_DURATION)
          ;

        chart.color(colorsGeneric);
        chart.interactiveLayer.tooltip.enabled(true);
        chart.interactiveLayer.tooltip.hideDelay(0);
        chart.interactiveLayer.tooltip.gravity('w'); // 's', 'n', 'w', 'e'
        if (that.tooltipContentGenerator !== null) {
          chart.interactiveLayer.tooltip.contentGenerator(that.tooltipContentGenerator);
        }

        chart.xAxis
          .ticks(8)
          .tickFormat(function(d) {
            return d3.time.format('%d-%m %H:%M')(new Date(d))
          })
          ;

        if (isNullOrZero) {
          console.log('FORCING Y-SCALE');
          chart.forceY([0,1]);
        } else {
          chart.forceY([0, null]);
        }

        chart.yAxis
          .axisLabel(that.units)
          .ticks(8)
          .showMaxMin(false)
          .tickFormat(function(d) {
            var d1 = Math.abs(d);
            if (d1 == 0) {
              return d3.format(',.0f')(d);
            } else if (d1 > 10.0) {
              return d3.format(',.0f')(d);
            } else if (d1 > 0.1) {
              return d3.format(',.1f')(d);
            } else {
              return d3.format(',.2f')(d);
            }
          })
          ;

        d3.select(that.jqSelector+' svg')
//          .datum(that.data)
          .datum(currentData)
          .call(chart)
          ;

        d3.select(CONST.TILE_PREFIX).select(".nv-lineChart")
          .on("mouseleave", function() {
            that.tooltipMgr.onMouseLeave(chart);
          });

        nv.utils.windowResize(chart.update);

        return chart;
      });
    },
  };

  function BtcStats(reloadDataCb) {
    var that = this;

    this.data = []; // Container for several datasets
    this.dataset = [];
    this.dataset[0] = new BtcStatsDataset(null);
    this.dataset[1] = new BtcStatsDataset(null);
    this.tile = new BtcStatsTile();
    this.timeline = new TimeSlider(this, '#rangeBtcStats');
    this.currentDataSelector = null;
    this.parseDate = d3.timeParse('%Y-%m-%dT%H:%M:%S');

    $(CONST.SELECT_PREFIX+CONST.CHART_NAME).change(function() {
      //console.log('CHART_SELECT: %s', $(this).val());
      that.currentDataSelector = $(this).val();
      that.tile.render(that.currentDataSelector);
    });

    $(CONST.SELECT_PREFIX+CONST.RANGE_NAME).change(function() {
      //console.log('RANGE_SELECT: %s', $(this).val());
      reloadDataCb($(this).val());
    });
  };
  BtcStats.prototype = {
    resetData: function() {
      this.dataset[0].resetData();
      this.dataset[1].resetData();
      this.data = [];
    },
    getPeriodSelector: function() {
      return $(CONST.SELECT_PREFIX+CONST.RANGE_NAME).get(0).value;
    },
    setIfSelector: function(name) {
      $(CONST.SELECT_PREFIX+CONST.CHART_NAME).get(0).value = name;
      $(CONST.SELECT_PREFIX+CONST.CHART_NAME).get(0).text = name;
    },
    createIfSelectorOptions: function() {
      var jqSelect = $(CONST.SELECT_PREFIX+CONST.CHART_NAME),
        item;
      jqSelect.empty(); // Jquery selector
      for (var i = 0; i < CONST.SELECTOR_NAME.length; i++) {
        item = document.createElement('option');
        item.text = CONST.SELECTOR_NAME[i];
        item.value = CONST.SELECTOR_NAME[i];
        jqSelect.get(0).options.add(item); // Javascript selector
      }
      if (this.currentDataSelector === null) {
        this.currentDataSelector = CONST.SELECTOR_NAME[0];
      }
      // Update current selected option
      this.setIfSelector(this.currentDataSelector);
    },
    parse: function(resp, period, dateStr) {
      var lines = resp.split('\n'),
        period = new Period(period, dateStr),
        prevDate = period.minDate(),
        attrs = null,
        cnt = 0,
        currDate = null;

      // ItemsCnt = total_resp_lines - 1 (headers) - 1 (last_empty_line)
      this.resetData();
      for (var i = 1; i < (lines.length - 1); i++) {
        var item = {},
          item1 = {};

        //console.log('[%d] %s', i, lines[i]);
        attrs = lines[i].split(/\s+/);
        currDate = this.parseDate(attrs[0]);

        // Parse line
        if (period.isOutOfDate(currDate)) {
          // Bqn CLI may send out of date data
          // Discard
          //console.log('discarding line: %s', attrs.join());
          cnt++;
          continue;
        } else {
          this.generateItems(prevDate, currDate, attrs);
        }
        prevDate = currDate;
      }

      // Generate tail fake items
      if (this.dataset[0].storedValues.length > 0) {
        this.generateItems(prevDate, period.maxDate(), null);
      }
      console.log('BtcStats.parse: discarded=%d, final=%d', cnt, this.dataset[0].storedValues.length);

      this.data = this.data.concat(this.dataset[0]);
      this.data = this.data.concat(this.dataset[1]);

      //console.log('storedValues: %s', JSON.stringify(this.data));
      this.createIfSelectorOptions();
    },
    generateItems: function(prevDate, currDate, attrs) {
      // Generate items from (prevDate, currDate]
      var prevMoment = moment(prevDate),
        currMoment = moment(currDate);

      // Fake entries
      //console.log('generateFakeItems: %d\' (%s, %s]', currMoment.diff(prevMoment, 'minutes'), prevMoment.format(), currMoment.format());
      prevMoment.add(5, 'minutes'); // Previous item exists
      while (prevMoment.isBefore(currMoment)) {
        // Create fake items
        var item = {};

        // Create a new 'moment' instance
        item['date'] = moment(prevMoment).toDate();
        item['Volume'] = null;
        item['Compression Ratio'] = null;
        this.dataset[0].storedValues.push(item);
        this.dataset[1].storedValues.push(item);
        prevMoment.add(5, 'minutes');
      }

      if (attrs !== null) {
        // Create current item
        var item0 = {},
          item1 = {};

        item0['date'] = this.parseDate(attrs[0]);
        item1['date'] = item0['date'];
        // Actual entries
        item0['Volume'] = (new Number(attrs[3])) / CONST.BITS_UNIT;
        item1['Volume'] = (new Number(attrs[4])) / CONST.BITS_UNIT;
        item0['Compression Ratio'] = new Number(attrs[5]);
        //item1['Ratio'] = null;

        this.dataset[0].storedValues.push(item0);
        this.dataset[1].storedValues.push(item1);
      }
    },
    setUnits: function(units) {
      this.units = units;
    },
    setTooltipContentGenerator: function(tooltipContentGenerator) {
      this.tile.setTooltipContentGenerator(tooltipContentGenerator);
    },
    selectIntervalAndRender: function(interval) {
      for (var i = 0; i < this.data.length; i++) {
        this.data[i].selectInterval(interval);
      }
      this.tile.render(this.currentDataSelector);
    },
    render: function() {
      this.tile.setData(this.data);
      this.timeline.render(this.data[0].getStoredInterval(),
        this.selectIntervalAndRender);
    },
    refresh: function() {
      this.tile.render();
    }
  };

  return BtcStats;
})();

//
//
// Diagnostic
//
var Diagnostic = (function() {
  function Diagnostic () {
    var that = this;

    this.link = document.getElementById('diagnosticDownload');

    $('#btnDiagnostic').click(function(event) {
      // Confirmation
      that.getDiagnosticFile();
    });
  };
  Diagnostic.prototype = {
    saveRemoteFileOld: function(filename, user) {
      this.link.setAttribute('href', user+'/'+filename); // Remote name
      this.link.setAttribute('download', filename); // Local name
      this.link.click();
      console.log('Diagnostic.request-sent: %s', user+'/'+filename);
    },
    saveRemoteFileNew: function(filePath) {
      getFile.execute(filePath);
    },
    getDiagnosticFile: function() {
      var that = this,
        modalWait = $('#modalWait'),
        progress = $('#modalWaitProgress');

      $('#modalWaitTxt').text('Generating system diagnostic...');
      modalWait.modal('show');
      progress.show();
      progress[0].max = 11; // Eleven chunks response to 'system diagnostic'
      progress[0].value = 0;

      ifCl.executeStream('system diagnostic', DIAGNOSTICS_STEP_TMOUT_MS,
        function() {
          progress.hide();
          modalWait.modal('hide');
          showModalError('Cannot get system diagnostic', '');
        }, function(res) {
          if (res.CmdResponse.partial) {
            (progress[0].value)++;
            var response = res.CmdResponse.response,
              fileNameStr = response.match(/diagnostic file:\s+(.*)/g);
            if (response.match(/Permission denied/) ||
              response.match(/Operation not permitted/)) {
              window.setTimeout (function(res) {
                 modalWait.modal('hide');
                 showModalError('Operation not permitted', null);
               }, 200);
            }
            if (fileNameStr) {
              var fileName = fileNameStr[0].split(/:\s+/)[1];
              modalWait.modal('hide');
              that.saveRemoteFileNew(fileName);
              /* OLD code, but do no remove
              // Ad hoc basename implementation
              window.setTimeout (function(res) {
                that.saveRemoteFileOld(
                  fileName.substr(fileName.lastIndexOf('/')+1), login.user);
              }, 400);
              */
            }
          } else {
            progress.hide();
            modalWait.modal('hide');
          }
      });
    },
    execute: function() {
      // Confirm diagnostics generation
      $('#modalDiagAsk').modal('show');
    },
  };

  return Diagnostic;
})();

// system security code at src/common/api.js
const securityFieldTranslations = {
  'Access lock failures': 'maxFailures',
  'Access unlock time': 'unlockTime',
  'Password minlen': 'passwordMinLength',
  'Password lcredit': 'passwordLCredit',
  'Password ucredit': 'passwordUCredit',
  'Password dcredit': 'passwordDCredit',
  'Password ocredit': 'passwordOCredit',
  'Password enforce for root': 'enforceRoot',
  'Password reject username': 'enforceUsername',
}

const asSecurityFieldValue = row => {
  const [fieldStr, valueStr] = row.split(/:\s+/);
  const field = securityFieldTranslations[fieldStr] || fieldStr;
  const value = valueStr === 'n/a' ? null :
    valueStr === 'yes' ? true :
    valueStr === 'no' ? false :
    parseInt(valueStr);
  return [field, value]
}

const intoSecuritySettings = (settings, [field, value]) =>
  ({...settings, [field]: value})

const _SECURITY_DEFAULTS = {
  maxFailures: 0,
  unlockTime: 0,
  passwordMinLength: 0,
  passwordLCredit: 0,
  passwordUCredit: 0,
  passwordDCredit: 0,
  passwordOCredit: 0,
  enforceRoot: false,
  enforceUsername: false,
}

const parseSystemSecurity = (response) =>
  response.trim('\n').split('\n').map(asSecurityFieldValue).reduce(intoSecuritySettings, _SECURITY_DEFAULTS);

const notNAAndHigherOrEqualTo = threshold => value => value !== null && value >= threshold
const notNAAndLowerOrEqualTo = threshold => value => value !== null && value <= threshold
const validCredit = notNAAndLowerOrEqualTo(-1);

const checkSystemSecurityActive = ({
  maxFailures, unlockTime, passwordMinLength,
  passwordLCredit, passwordUCredit, passwordDCredit, passwordOCredit,
  enforceUsername, enforceRoot,
}) =>
  console.warn('checkSystemSecurityActive') ||
  notNAAndHigherOrEqualTo(4)(maxFailures) &&
  notNAAndHigherOrEqualTo(300)(unlockTime) &&
  notNAAndHigherOrEqualTo(9)(passwordMinLength) &&
  validCredit(passwordLCredit) &&
  validCredit(passwordDCredit) &&
  validCredit(passwordOCredit) &&
  validCredit(passwordUCredit) &&
  enforceRoot === true &&
  enforceUsername === true

const securityIsStrict = (params) => {
  if (checkSystemSecurityActive(params) !== true) {
    throw 'Strict security not enabled';
  }
  return params
}

var Eula = (function() {
  function Eula(element, elementBody, agreeBtn, cancelBtn, okBtn) {
    var that = this;
    this.cancelCb = null;
    this.eulaViewSel = $('#'+element);
    this.eulaBodySel = $('#'+elementBody);
    this.agreeBtnSel = $('#'+agreeBtn);
    this.cancelBtnSel = $('#'+cancelBtn);
    this.okBtnSel = $('#'+okBtn);
    this.localStorageName = 'BQN_EULA_AGREED';

    this.agreeBtnSel.click(function() {
      //console.log('CLICK: eula agree');
      localStorage.setItem(that.localStorageName, 'true');
      that.eulaViewSel.modal('hide');
      // Workaround to reset scroll bar
      that.eulaBodySel.scrollTop(0);
    });
    this.cancelBtnSel.click(function() {
      //console.log('CLICK: eula cancel');
      that.eulaViewSel.modal('hide');
      that.cancelCb();
      // Workaround to reset scroll bar
      that.eulaBodySel.scrollTop(0);
    });
    this.okBtnSel.click(function() {
      //console.log('CLICK: eula ok');
      that.eulaViewSel.modal('hide');
      // Workaround to reset scroll bar
      that.eulaBodySel.scrollTop(0);
    });

    //this.eulaBodySel.get(0).addEventListener("scroll", function () {
    this.eulaBodySel.scroll(function () {
      //console.log('SCROLL: %d', this.scrollTop);
      if (that.cancelCb != null) {
        // First time
        // ATTENTION: this.scrollTop is a float number);
        if (this.scrollHeight < (this.clientHeight + this.scrollTop + 1)) {
          console.log('SCROLL.BOTTOM');
          // Show agree button
          that.agreeBtnSel.show();
        } else {
          // Hide agree button
          that.agreeBtnSel.hide();
        }
      }
    });

    $('#eulaLnk').click(function() {
      that.show();
    });

  };
  Eula.prototype = {
    check: function(cancelCb) {
      this.cancelCb = cancelCb;
      if (localStorage.getItem(this.localStorageName) == null) {
        // First time
        this.eulaViewSel.modal({backdrop: 'static'});
        // Set scroll to top (currently not working HERE)
        this.eulaBodySel.scrollTop(0);

        this.agreeBtnSel.hide();
        this.cancelBtnSel.show();
        this.okBtnSel.hide();
      }
    },
    show: function() {
      this.cancelCb = null;
      this.eulaViewSel.modal({backdrop: 'static'});
      // Set scroll to top (currently not working HERE)
      this.eulaBodySel.scrollTop(0);

      this.agreeBtnSel.hide();
      this.cancelBtnSel.hide();
      this.okBtnSel.show();

      /* Development trace
      console.log('scrollTop=%d', this.eulaBodySel.scrollTop());
      console.log('clientHeight=%d', this.eulaBodySel.get(0).clientHeight);
      console.log('scrollHeight=%d', this.eulaBodySel.get(0).scrollHeight);
      */

    }
  };

  return Eula;
})();



//
// Selector functions
//
function Selector() {
};
Selector.periodToLines = function(rangeName) {
  var lines = {Day: DAY_LINES, Week: WEEK_LINES, 'Month': MONTH_LINES, ThreeMonths: THREE_MONTHS_LINES};
  return lines[rangeName];
};
Selector.periodToText = function(rangeName) {
  var value = {Day: '1 Day', Week: '1 Week', 'Month': '1 Month', ThreeMonths: '3 Months'};
  return value[rangeName];
};
Selector.rttaToText = function(rttaName) {
  var value = {'rtta-small': 'RTTa Small', 'rtta-large': 'RTTa Large'};
  return value[rttaName];
};

//
// Utility functions
//
function isZeroOrNull(values, valuesSelector) {
  for (var i = 0, len = values.length; i < len; i++) {
    const current = values[i];
    if (current === undefined) {
      continue; // Not sure about the intention here.
    }
    const value = current[valuesSelector];
    if (value !=  null && value != 0) return false;
  }
  return true;
}

function decodeUrl(url) {
  var match = url.match(/^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
  return url && {
    url: url,
    protocol: match[1],
    host: match[2],
    hostname: match[3],
    port: match[4],
    pathname: match[5],
    search: match[6],
    hash: match[7]
  }
}


//
// Views manager
//
var Views = (function() {
  var CONST = {
  };

  function Views() {
    this.viewsName = [];
    this.viewsHash = [];
    this.viewsSelector = [];
    this.viewsMenuBtn = [];
    this.viewsLoadF = [];
    this.viewsUnloadF = [];

    this.active = -1;
    this.LoadCnt = 0;
    this.readyCb = null;
    this.next = null;
    this.keep = null;
  };
  Views.prototype = {
    add: function(name, menuBtn, loadF, unloadF) {
      this.viewsName.push(name);
      this.viewsHash.push('#'+name);
      this.viewsSelector.push('#'+name);
      this.viewsMenuBtn.push(menuBtn ? '#'+menuBtn : menuBtn);
      this.viewsLoadF.push(loadF ? loadF : null);
      this.viewsUnloadF.push((unloadF ? unloadF : null));
      //console.log('views.add: %d %s', this.viewsName.length - 1, name);
    },
    doKeep: function(viewId) {
      this.keep = viewId;
    },
    setNext: function(hash) {
      this.next = hash;
    },
    popNext: function() {
      const result = this.next;
      this.next = null;
      return result;
    },
    apply: function(fromHash=false) {
      const loginView = '#viewLogin';
      const proposed = this.popNext() ||  window.location.hash;
      const next = login.isLogged()
        ? ( proposed === loginView ? '#viewDashboard' : proposed )
        : loginView;
      this.setNext(next === loginView ? proposed : null)

      if(!login.isLogged()){
        return;
      }
      
      functionality.execute();
    },
    toggleToId: function(newActive, force, args, fromHash=false) {
      var doShow;
      console.log('views.toggleToId: prev=%d, next=%d', this.active, newActive, fromHash);
      if (newActive < 0)
        return;

      // Remove NavBar from #login view
      if (newActive === 0) {
        $('#navigationBar').hide();
        $('#sectSidebars').hide();
      } else {
        $('#navigationBar').show();
        $('#sectSidebars').show();
        $('#sectContent').show();
        this.setMenuActive('#menu'+this.viewsHash[newActive].substring(5))
      }

      // Avoid refesh when !force
      if (!force &&  (newActive == this.active)) {
        return;
      }

      // If previous view
      if (this.active >= 0) {
        // Hide previous view
        const prevView = $(this.viewsSelector[this.active]);
        prevView.hide().trigger('view:hide');
        // Call previous unloadF if defined
        if (this.keep !== prevView.attr('id')) {
          prevView.trigger('view:destroy');
        } else {
          this.keep = null;
        }
        if (this.viewsUnloadF[this.active]) this.viewsUnloadF[this.active]();
      }

      // Set new view
      this.active = newActive;
      $(this.active).trigger("view:show");
      // Load view
      if (this.viewsLoadF[this.active] != null) {
        console.log('views.LoadF: args=%s', JSON.stringify(args));
        doShow = this.viewsLoadF[this.active](args);
      }

      // Show view if viewsLoadF returns true or nothing (undefined)
      if (doShow === undefined || doShow === true) {
        $(this.viewsSelector[this.active]).show(0, function() {
          $(this).trigger('view:show');
        });
      }
    },
    toggle: function(name, oneArg, fromHash=false) {
      // OneArg may be Boolean force or Object args
      var force, args,
        type = Object.prototype.toString.call(oneArg);
      if (type == '[object Undefined]') {
        force = false;
        args = null;
      } else if (type == '[object Boolean]') {
        force = oneArg;
        args = null;
      } else if (type == '[object Object]') {
        force = true;
        args = oneArg;
      }
      console.log('views.toggle: %s', name);
      const active = this.viewsName.indexOf(name)

      this.toggleToId(active, force, args, fromHash);
    },
/*
    toggle: function(name, force) {
      console.log('views.toggle: %s', name);
      this.toggleToId(this.viewsName.indexOf(name), force);
    },
*/
    toggleToHash: function(completeHash) {
      console.log('views.toggleToHash: %s', location.hash);
      const fromHash = true;
      return this.apply(fromHash);
    },
/*
    toggleToHash: function(hash, force) {
      console.log('views.toggleToHash: %s', hash);
      this.toggleToId(this.viewsHash.indexOf(hash), force);
    },
*/
    currentHash: function() {
      if (this.active < 0) {
        return null;
      } else {
        return this.viewsHash[this.active];
      }
    },
    loading: function(fileName) {
      this.LoadCnt++;
      console.log('views.loading[%d]: file %s', this.LoadCnt, fileName);
    },
    loaded: function() {
      var that = this, petete='petete';
      console.log('views.loaded[%d]', this.LoadCnt);
      this.LoadCnt--;
      if (this.LoadCnt === 0) {
        setTimeout(() => {
          console.log('views.ready');
          // IMPORTANT! forEach creates a closure for each item
          this.viewsMenuBtn.forEach(function(item, idx) {
            if (item) {
              $(`#leftsidebar a[id=${item.replace('#','')}]`).click(function() {
                globalNavigate(that.viewsName[idx])
              });
            }
          });
          // , '#navigationBar a.navbar-brand'
          $('#leftsidebar a.navbar-brand-experimental, #navigationBar a.navbar-brand').click(function() {
            globalNavigate('viewDashboard');
          });
          
          this.readyCb();
        }, 250)
      }
    },
    ready: function(readyCb) {
       this.readyCb = readyCb;
    },
    setMenuActive: function(element) {
      var $found = $.find(element.replace('Single', ''));

      if ($found.length == 0)
        return 22;
      if (!$($found).parent().is('LI'))
        return 22;

      console.log("Activate \"%s\" in leftSideBar", element);

      // Remove the toggled class for non menu-toggle elements.
      $('.sidebar .active a.toggled').each(function() {
        if (!$(this).hasClass('menu-toggle')) {
          $(this).removeClass('toggled');
        }
      });
      $('.sidebar .active').removeClass('active');
      $($found).addClass('toggled');
      $found = $($found).parent();
      while ($($found).length > 0) {
        // Activate all the LI elements on top of the current element.
        if ($($found).is('LI')){
          $($found).addClass('active');
        }else if($($found).is('UL')){
          $($found).css('display', 'block');
          const previousA = $found.prev('a');
          previousA.addClass('toggled');
        }
        $found = $($found).parent();
      }

      return 0;
    }
  };

  return Views;
})();


//
// Global variables
//
window.ifCl,
  window.BTA_COLOR = '#F01212',
  window.CAMBIUM_COLOR = '#003D79',
  window.NOBTA_COLOR = '#545454',
  window.NO_DATA_AVAILABLE = 'No Data Available',
  window.DAY_DOTS = 288,
  window.DAY_LINES = 288,
  window.WEEK_LINES = 2016,
  window.MONTH_LINES = 8640,
  window.THREE_MONTHS_LINES = 25920,
  window.DAY_HOURS = 24,
  window.WEEK_HOURS = 168,
  window.DIAGNOSTICS_STEP_TMOUT_MS = 600000,
  window.SOFTWARE_INSTALL_TMOUT_MS = 300000,
  window.DPI_STATS_TMOUT_MS = 3600000,
  window.POINTS_PER_TILE_CHART = ((window.innerWidth / 5) > DAY_DOTS ? DAY_DOTS : (window.innerWidth / 5)),
  window.PROFILE_LENGTH_MAX = 31,
  window.POLICY_LENGTH_MAX = 63,
  window.dashboard,
  window.btcGraphImg,
  window.diagnostic,
  window.license,
  window.backup,
  window.software,
  window.users,
  window.sysNtp,
  window.genConf,
  window.radiusRestSubs,
  window.btaAccDw,
  window.btaAccUp,
  window.btaSess,
  window.powerCode,
  window.wireConf,
  window.ifThrou,
  window.wireStatus,
  window.mgmtIfConf,
  window.dpiStatsLatency,
  window.subsStatsUsage,
  window.btcStats,
  window.dosConf,
  window.dosStats,
  window.dosStatsTime,
  window.sysCpu,
  window.sysMem,
  window.sysInfo,
  window.subsStatus,
  window.subsMetrics,
  window.generalStngsConf,
//  subsStatusForSubsStatsUsage, // REMOVE
  //subscriber,
  //policySubscribers,
  window.policySubs,
  window.rulesFlow,
  window.rulesRate,
  window.TYPE_FLOW = 0,
  window.TYPE_MONITOR = 1,
  window.TYPE_RATE = 2,
  window.profIf,
  window.profAcc,
  window.profInt,
  window.profVlan,
  window.profPoliciesRate,
  window.profSubscriberId,
  window.profDpi,
  window.profThrou,
  window.eula,
  window.currentChartUnits, // Global varibale to export units to tooltip
  window.FORCE = true,
  window.btcStatusTxt = null,
  window.login,
  window.functionality = new Functionality();
  window.views = new Views();
  window.id = new Id(),
  window.tableRow = new TableRow(),
  window.resize = new Resize(1500, 1800),
  window.getFile = new GetFile();

// Views must be created here
// views.add(html_view_id, html_fire_button_id, view_service_function);
// viewLogin must be the first
views.add('viewLogin', null);
views.add('viewDashboard', 'menuDashboard');
views.add('viewStatusTCPO', 'menuStatusTCPO');
views.add('viewFlowsPerSubs', 'menuFlowsPerSubs', () => {});
views.add('viewFlowsPerProtocol', 'menuFlowsPerProtocol');

views.add('viewStatusSubscriberGroups', 'menuStatusSubscriberGroups');
views.add('viewStatusSubscriberGroups', 'menuConfSubscriberGroups');
views.add('viewStatusSubscriberGroups', 'menuStatisticsSubscriberGroups');

views.add('viewStatusSubscribers', 'menuStatusSubscribers');
views.add('viewSubscriberQuotas', 'menuSubscriberQuotas');
views.add('viewSubscriberQuotas', 'menuConfSubscriberQuotas');
views.add('viewSubscriberAttributes', 'menuSubscriberAttributes');
views.add('viewSubscriberThroughput', 'menuSubscriberThroughput');
views.add('viewSubscriberThroughput', 'menuStatsSubscriberThroughput');
//views.add('viewSubscriber', 'menuSubscriber', loadSubscriberView);
//views.add('viewPolicySubscribers', 'menuPolicySubscribers', loadPolicySubscribersView);
//views.add('viewPolicySubs', 'menuPolicySubs', loadPolicySubsView);
views.add('viewBillingConf', 'menuBillingConf');
views.add('viewCongestionACM', 'menuCongestionACM', () => {  });
views.add('viewSNMPConf', 'menuSNMPConf');
views.add('viewRESTConf', 'menuRESTConf');
views.add('viewRADIUSConf', 'menuRADIUSConf');
views.add('viewOptimizationConf', 'menuOptimizationConf');
views.add('viewDoSConf', 'menuDoSConf');
views.add('viewTCPOptimizationSpeed', 'menuTCPOptimizationSpeed', () => {} );
views.add('viewTCPOptimizationAcceleration', 'menuTCPOptimizationAcceleration', () => {} );
views.add('viewBtaStatsSess', 'menuBtaStatsSess', () => {});
views.add('viewBtaStatsTraffic', 'menuBtaStatsTraffic');
views.add('viewFlowsPerPolicy', 'menuFlowsPerPolicy');

//views.add('viewDpiStatsHr', 'menuDpiStatsHr', loadDpiStatsHrView);
views.add('viewDPIHourlyVolume', 'menuDPIHourlyVolume');
views.add('viewDPITotalVolume', 'menuDPITotalVolume');
views.add('viewDPILatency', 'menuDPILatency');
views.add('viewDPIServiceMetrics', 'menuDPIServiceMetrics');
views.add('viewSubscribersHourlyVolume', 'menuSubscribersHourlyVolume');
views.add('viewSubscribersTotalVolume', 'menuSubscribersTotalVolume');
views.add('viewSubscribersPerPolicy', 'menuSubscribersPerPolicy');
views.add('viewSubsStatsUsage', null, loadSubsStatsUsageView);
views.add('viewDosStatsTime', 'menuDosStatsTime', loadDosStatsTimeView);
views.add('viewLatencyStats', 'menuLatencyStats');
views.add('viewRtxStats', 'menuRtxStats');
views.add('viewRulesFlow', 'menuRulesFlow');
views.add('viewRulesRate', 'menuRulesRate');
views.add('viewRulesMonitor', 'menuRulesMonitor');
views.add('viewTimeProfileConfig', 'menuTimeProfileConfig');
views.add('viewVLANProfileConfig', 'menuVLANProfileConfig');
views.add('viewAccessProfileConfig', 'menuAccessProfileConfig');
views.add('viewInternetProfileConfig', 'menuInternetProfileConfig');
views.add('viewInterfaceProfileConfig', 'menuInterfaceProfileConfig');
views.add('viewPolicyRateProfileConfig', 'menuPolicyRateProfileConfig');
views.add('viewSubscriberIDProfileConfig', 'menuSubscriberIDProfileConfig');
views.add('viewSubscriberGroupProfileConfig', 'menuSubscriberGroupProfileConfig');
views.add('viewThroughputProfileConfig', 'menuThroughputProfileConfig');
views.add('viewDSCPProfileConfig', 'menuDSCPProfileConfig');
views.add('viewDPIProfileConfig', 'menuDPIProfileConfig');
//views.add('viewBtcStats', 'menuBtcStats', loadBtcStatsGraphViews); Uses Chart
views.add('viewBtcStats', 'menuBtcStats', loadBtcStatsView);
views.add('viewStatusInterfacesThroughput', 'menuStatusInterfacesThroughput');
views.add('viewStatusInterfacesLinkState', 'menuStatusInterfacesLinkState');
views.add('viewStatusInterfacesDataWires', 'menuStatusInterfacesDataWires');

views.add('viewIfaceStkStats', 'menuIfaceStkStats');
views.add('viewManagementInterfaceConf', 'menuManagementInterfaceConf');
views.add('viewManagementFirewallConf', 'menuManagementFirewallConf');
views.add('viewInterfacesBypassConf', 'menuInterfacesBypassConf');
views.add('viewInterfacesDataWiresConf', 'menuInterfacesDataWiresConf');
views.add('viewInterfacesAllConf', 'menuInterfacesAllConf');
views.add('viewLicense', 'menuLicense', loadLicenseView);
views.add('viewBackupLoad', 'menuBackupLoad');
views.add('viewBackupSave', 'menuBackupSave');
views.add('viewBackupSaveSubsAttributes', 'menuBackupSaveSubsAttributes');
views.add('viewBackupLoadSubsAttributes', 'menuBackupLoadSubsAttributes');
views.add('viewSoftware', 'menuSoftware');
views.add('viewDiagnostic', 'menuDiagnostic', loadDiagnosticView);
views.add('viewSysNtp', 'menuSysNtp');
views.add('viewSysDate', 'menuSysDate');
views.add('viewStatusSystemDisk', 'menuStatusSystemDisk');
views.add('viewStatusSystemMemory', 'menuStatusSystemMemory');
views.add('viewStatusSystemCPU', 'menuStatusSystemCPU');
views.add('viewStatusSystemInfo', 'menuStatusSystemInfo');
views.add('viewStatusBilling', 'menuStatusBilling');
views.add('viewSysStatsCpu', 'menuSysStatsCpu');
views.add('viewSysStatsMem', 'menuSysStatsMem');
views.add('viewUsersAdmin', 'menuUsersAdmin');
views.add('viewRatePolicies', 'menuRadiusRestPoli');
views.add('viewRatePolicies', 'menuRatePolicies');
views.add('viewFlowPolicies', 'menuFlowPolicies', () => {  });
views.add('viewMonitoringPolicies', 'menuMonitoringPolicies');
views.add('viewGeneralSettingsConfig', 'menuGeneralSettingsConfig');
views.add('viewThroughputProtocols', 'menuThroughputProtocols', (...params) => { });
views.add('viewThroughputPerPolicy', 'menuThroughputPerPolicy', (...params) => { });
views.add('viewLogsKernel', 'menuLogsKernel');
views.add('viewLogsSystem', 'menuLogsSystem');

views.add('viewDoSVolumeStats', 'menuDoSVolumeStats');
views.add('viewDoSSynStats', 'menuDoSSynStats');
// Create a chart img file and export it as an href
//
var BtcGraphImg = (function() {
  var CONST = {
    TYPE: 'png',
    //TYPE: 'jpeg',
    MIME_TYPE: 'image/'+'png',
    FILE_BASE_NAME: 'bqn_graphic'
  };

  function BtcGraphImg() {
    // Create a solid white backgroud
    Chart.pluginService.register({
      beforeDraw: function(chartInstance) {
        // Draw a white backgroud
        var ctx = chartInstance.chart.ctx;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0,
          chartInstance.chart.width, chartInstance.chart.height);
      }
    });
  };
  BtcGraphImg.prototype = {
    create: function(graphContainerId) {
      var containerSelector = '#'+graphContainerId,
        canvasId = graphContainerId+'Canvas',
        canvasHtml = '<canvas id="'+canvasId+'" style="border:1px solid #000000;"></canvas>',
        btn,
        canvas;

      // Re-create canvas
      $(containerSelector).empty();
      $(containerSelector).append(canvasHtml);

      // Get references to html elements
      btn = $('#btcDownloadImgBtn').get(0),
      //canvas = $(graphCanvasId).get(0);
      canvas = document.getElementById(canvasId);
      //canvas = document.createElement('canvas');
      //canvas.style = 'border:1px solid #000000;';

      // Arm download button
      btn.addEventListener('click', function () {
        // We use a link in order to download the img
        var link = document.createElement('a');
        link.download = 'bqn_graph.'+CONST.TYPE;
        link.href = canvas.toDataURL(CONST.MIME_TYPE);
        link.dataset.downloadurl = [CONST.MIME_TYPE, link.download,
          link.href].join(':');

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, false);

      return canvas;
    }
  };

  return BtcGraphImg;
})();

function saveLocalFile(filename, content) {
  var link = document.createElement('a');
  link.setAttribute('href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(content));
  link.setAttribute('download', filename);
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

//
// Load View Functions
//

/* To be added in the future ???
function btaAccTest() {
  var switchItem = $('#btaAccTestSwitch'),
    ipItem = $('#btaAccTestIp'),
    blacklist,
    blacklisted;

  function setAccOn(ipVal) {
    ifCl.execute('clear bta blacklist client '+ipVal, function(res) {
      if (res.CmdResponse.response == '' ||
        res.CmdResponse.response.indexOf('Cannot find rule')) {
        ifCl.execute('clear bta connection address '+ipVal, function(res) {
          if (res.CmdResponse.response == '') {
            //showModalInfo('Successfully removed from blacklist:', ipVal);
            ifCl.execute('show bta blacklist', function(res) {
              blacklist = res.CmdResponse.response;
            });
          } else {
            $('#modalBtaAccTest').modal('hide');
            showModalError('Warning:', res.CmdResponse.response);
          }
        });
      } else {
        $('#modalBtaAccTest').modal('hide');
        showModalError('Warning:', res.CmdResponse.response);
      }
    });
  }

  function setAccOff(ipVal) {
    ifCl.execute('set bta blacklist client '+ipVal, function(res) {
      if (res.CmdResponse.response == '') {
        ifCl.execute('clear bta connection address '+ipVal, function(res) {
          if (res.CmdResponse.response == '') {
            //showModalInfo('Successfully added to blacklist:', ipVal);
            ifCl.execute('show bta blacklist', function(res) {
              blacklist = res.CmdResponse.response;
            });
          } else {
            $('#modalBtaAccTest').modal('hide');
            showModalError('Warning:', res.CmdResponse.response);
          }
        });
      } else {
        // Show modal error message
        $('#modalBtaAccTest').modal('hide');
        showModalError('Warning:', res.CmdResponse.response);
      }
    });
  }

  // Disarm event previously (.off)
  $('#btaAccTestSwitch').off().change(function() {
    blacklisted = !blacklisted;
    if (!blacklisted) {
      setAccOn(ipItem.val());
    } else {
      setAccOff(ipItem.val());
    }
  });

  ifCl.execute('show bta blacklist', function(res) {
    blacklist = res.CmdResponse.response;
    $('#modalBtaAccTest').modal({backdrop: 'static'});
    ipItem.val('');
    // Focus on a modal input
    $('#modalBtaAccTest').on('shown.bs.modal', function () {
      ipItem.focus();
    })
    ipItem.on('keyup', function(e) {
      $('#btaAccTestSwitchBlock').hide();
      if (IpV4.isValid(ipItem.val())) {
        blacklisted = (blacklist.indexOf(ipItem.val()) !== -1);
        //document.querySelector('#btaAccTestSwitch').checked = !found;
        switchItem.prop('checked', !blacklisted);
        $('#btaAccTestSwitchBlock').show();
      }
    });
  });
}

function exittAccTest() {
  $('#modalBtaAccTest').modal('hide');
  $('#btaAccTestSwitchBlock').hide()
  loadBtaBlacklistView();
}
*/
function myTooltipContentGenerator(d) {
  //console.log('NVD3-TOOLTIP-DATA: %s', JSON.stringify(d));
  if (d === null) {
    return '';
  }

  var table = d3.select(document.createElement("table"));
  var theadEnter = table.selectAll("thead")
    .data([d])
    .enter().append("thead");

  theadEnter.append("tr")
    .append("td")
    .attr("colspan", 3)
    .append("strong")
    .classed("x-value", true)
    //.html(headerFormatter(d.value));
    //.html(d.value);
    // ISO 8601 date/time format.
    .html(d3.time.format('%Y-%m-%d %H:%M:%S')(d.value));

  var tbodyEnter = table.selectAll("tbody")
    .data([d])
    .enter().append("tbody");

  var trowEnter = tbodyEnter.selectAll("tr")
    .data(function(p) { return p.series})
    .enter()
    .append("tr")
    .classed("highlight", function(p) { return p.highlight});

  // Bullet
  trowEnter.append("td")
    .classed("legend-color-guide",true)
    .append("div")
    .style("background-color", function(p) { return p.color});

  // Key
  trowEnter.append("td")
    .classed("key",true)
    .classed("total",function(p) { return !!p.total})
    //.html(function(p, i) { return keyFormatter(p.key, i)});
    .html(function(p, i) {
      return (p.key.indexOf('(') >= 0 ? p.key.substring(0, p.key.indexOf('(')) : p.key);
    });

  // Value
  // Each chart updates currentChartUnits
  var units = currentChartUnits;
  trowEnter.append("td")
    .classed("value",true)
    //.html(function(p, i) { return tooltipValueFormatter(p.value, i, p) });
    .html(function(p, i) {
      if (p.value === undefined)
        return ('N/A');
      else
        return (p.value !== null ? p.value.toFixed(2)+' '+units : 'N/A');
    });

  trowEnter.filter(function (p,i) { return p.percent !== undefined }).append("td")
    .classed("percent", true)
    .html(function(p, i) { return "(" + d3.format('%')(p.percent) + ")"
    });

  trowEnter.selectAll("td").each(function(p) {
    if (p.highlight) {
      var opacityScale = d3.scale.linear().domain([0,1]).range(["#fff",p.color]);
      var opacity = 0.6;
      d3.select(this)
        .style("border-bottom-color", opacityScale(opacity))
        .style("border-top-color", opacityScale(opacity))
      ;
    }
  });

  var html = table.node().outerHTML;
  if (d.footer !== undefined)
    html += "<div class='footer'>" + d.footer + "</div>";
  return html;
};

const getIPv6Prefix = () =>
  ifCl.run("show subscriber ipv6-prefix").then((resp) => resp.trim("\n"));

const expressAddr = (addr) =>
   addr.includes(":") === false
    ? addr
    : addr.includes("/") === true
    ? addr
    : getIPv6Prefix().then( ipv6prefix => /*IPv6*/ `${addr}/${ipv6prefix}`);

const expressTarget = ({addr=null, subsId=null}, all='') =>
  Promise.resolve( (addr !== null &&  addr.length > 0)
    ? expressAddr(addr)
    : (subsId !== null &&  subsId.length > 0)
    ? `id ${safeSubsId(subsId)}`
    : all);

function loadSubsStatsUsageView(optArgs) {
  subsStatsUsage.execute(optArgs);
}

function loadBtcStatsView() {
  if (btcStatusTxt == 'disabled') {
    showModalNotif('BTC status is '+btcStatusTxt, null);
    // Returning false we notify Views Manager to not render the view
    return false;
  } else {
    ifCl.execute('show system date', function(res) {
      var dateStr = res.CmdResponse.response,
        period = btcStats.getPeriodSelector();
      ifCl.execute('show statistics compress lines ' + Selector.periodToLines(period), function(res) {
        btcStats.resetData();
        btcStats.parse(res.CmdResponse.response, period, dateStr);
        btcStats.setTooltipContentGenerator(myTooltipContentGenerator);
        btcStats.render();
      });
    });
  }
}

function loadBtcStatsListView() {
  ifCl.execute('show statistics compress', function(res) {
    var htmlLine,
      lines = res.CmdResponse.response.split('\n');
    // Remove all tr descendants from #btcStatsTab (Clean table rows)
    $('#btcStatsTab > tr').remove();
    // Skip last empty line produced by split
    for (var i = 0, len = lines.length - 1; i < len; i++) {
      htmlLine = '<tr><td style="white-space: pre;">'+lines[i]+'</td></tr>';
      $('#btcStatsTab').append(htmlLine);
    }
  });
}

function loadBtcStatsGraphViews() {
  ifCl.execute('show statistics compress', function(res) {
    var chunks = [],
      dataNumber = [],
      dataVolumenIn = [],
      dataVolumenOut = [],
      dataRatio = [],
      lines = res.CmdResponse.response.split('\n');
    for (var i = 1, len = lines.length - 1; i < len; i++) {
      chunks = lines[i].split(/\s+/);
      date = new Date(chunks[0]);
      dataNumber[i-1] = {x: date, y: Number(chunks[2])};
      dataVolumenIn[i-1] = {x: date, y: (Number(chunks[3]) / 1048576)};
      dataVolumenOut[i-1] = {x: date, y: (Number(chunks[4]) / 1048576)};
      dataRatio[i-1] = {x: date, y: Number(chunks[5])};
    }
    renderBtcNumberGraphView(dataNumber);
    renderBtcVolumenGraphView(dataVolumenIn, dataVolumenOut);
    renderBtcRatioGraphView(dataRatio);
  });
}

function loadDosStatsTimeView() {
  ifCl.execute('show system date', function(res) {
    var dateStr = res.CmdResponse.response,
      period = new Period(dosStatsTime.getPeriodSelector(), dateStr),
      hours = period.hours(),
      range = { from: period.minDate(), to: period.maxDate() },
      cmds = [], resps = [];
    const dosHours = dosStatsTime.getPeriodSelector() === 'ThreeMonths' ? 2160 : hours;
    cmds.push(`show statistics dos volume hours ${dosHours}`);
    cmds.push(`show statistics dos syn hours ${dosHours}`);
    SequentialCmd.execute(cmds, resps, function() {
      dosStatsTime.execute(range, resps[0], resps[1]);
    });
  });
}

/*
function loadSubscriberView() {
  subscriber.execute();
}

function loadPolicySubscribersView() {
  policySubscribers.execute();
}
*/

function loadPolicySubsView() {
  policySubs.execute();
}

function loadProfIfView() {
  profIf.execute();
}

function loadPowerCodeView() {
  powerCode.execute();
}

function loadDiagnosticView() {
  diagnostic.execute();
}

function loadLicenseView() {
  license.execute();
}

function renderBtcNumberGraphView(dataNumber) {
    var ctx = document.getElementById('canvasBtcStatsNumber').getContext('2d');

    var myChart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [{
          label: 'Compressions',
          data: dataNumber,
          backgroundColor: 'rgba(75, 192, 192, 0.4)'
        }]
      },
      options: {
        animation: {
          duration: 500,
        },
        scales: {
          xAxes: [{
            display: true,
            type: 'time',
            time: {
              //minUnit: 'minute',
              displayFormats: {
                minute: 'h:mm'
                //minute: 'h:mm a'
              }
            }
          }],
          yAxes: [{
            display: true,
            ticks: {
              min: 0
            }
          }]
        }
      }
    });
}

function renderBtcVolumenGraphView(dataVolumenIn, dataVolumenOut) {
    var ctx = document.getElementById('canvasBtcStatsVolumen').getContext('2d');

    var myChart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [{
          label: 'Input',
          data: dataVolumenIn,
          fill: false,
          borderColor: 'rgba(75, 192, 192, 0.4)'
        },
        {
          label: 'output',
          data: dataVolumenOut,
          fill: false,
          borderColor: 'rgba(255, 99, 132, 0.4)'
        }]
      },
      options: {
        animation: { duration: 500 },
        scales: {
          xAxes: [{
            display: true,
            type: 'time',
            time: {
              //minUnit: 'minute',
              displayFormats: {
                minute: 'h:mm'
                //minute: 'h:mm a'
              }
            }
          }],
          yAxes: [{
            display: true,
            ticks: {
              min: 0,
              callback: function(value, index, values) {
                return value + ' MB';
              }
            }
          }]
        }
      }
    });
}

function renderBtcRatioGraphView(dataRatio) {
    var ctx = document.getElementById('canvasBtcStatsRatio').getContext('2d');

    var myChart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [{
          //lineTension: 0,
          label: 'Compression Ratio',
          data: dataRatio,
          backgroundColor: 'rgba(255, 99, 132, 0.4)'
        }]
      },
      options: {
        animation: { duration: 500 },
        scales: {
          xAxes: [{
            display: true,
            type: 'time',
            time: {
              //minUnit: 'minute',
              displayFormats: {
                minute: 'h:mm'
                //minute: 'h:mm a'
              }
            }
          }],
          yAxes: [{
            display: true,
            ticks: {
              min: 0,
              max: 100
            }
          }]
        }
      }
    });
}

//
// FUNCTION:
//   setInfoBox
//
// DESCRIPTION:
//   Set the content of an info-box, and set its color depending
//   pn the value of "level".
//
function setInfoBox(element, text, level) {
  var $found = $.find(element);
  var $infoBox;

  if ($found.length == 0)
    return 22;

  console.log("Set \"%s\" info-box to \"%s\" [%s]", element, text, level);
  $($found).text(text);
  $infoBox = $($found).parent().parent();
  if (($($infoBox).length > 0) && ($($infoBox).hasClass("info-box-3"))) {
    $($infoBox).removeClass("bg-green");
    $($infoBox).removeClass("bg-grey");
    $($infoBox).removeClass("bg-orange");
    $($infoBox).removeClass("bg-red");
    if (level == 1)
      $($infoBox).addClass("bg-green");
    else if (level == 2)
      $($infoBox).addClass("bg-orange");
    else if (level == 3)
      $($infoBox).addClass("bg-red");
    else
      $($infoBox).addClass("bg-grey");
  }
  return 0;
}

//
// FUNCTION:
//   showModalError
//
// DESCRIPTION:
//   Show a modal with the error icon and text.
//
window.showModalError = function showModalError(title, body) {
  showModalGeneric('error_outline', title, body, 'static');
}

//
// FUNCTION:
//   showModalGeneric
//
// DESCRIPTION:
//   Show a the modalInfo modal with an icon and text.
//
window.showModalGeneric = function showModalGeneric(icon, title, body, backdrop, onOk) {
  var obj;
  if (icon === '' || icon === null)
    $('#modalInfoIcon').text('info_outline');
  else if (icon === 'hide')
    $('#modalInfoIcon').hide();
  else
    $('#modalInfoIcon').text(icon);
  if (title === '' || title === null) {
    $('#modalInfoTitle').html('&nbsp;');
  } else {
    obj = $('#modalInfoTitle').text(title);
    obj.html(obj.html().replace(/\n/g,'<br/>'));
  }
  var obj = $('#modalInfoStr').html(body);
  obj.html(obj.html().replace(/\n/g,'<br/>'));
  $('#modalInfoOkButton').unbind('click').click(function(){
    if(onOk){
      onOk();
    }
  })

  $('#modalInfo').modal({backdrop: backdrop});
}

//
// FUNCTION:
//   showModalInfo
//
// DESCRIPTION:
//   Show a modal with the info icon and text.
//
window.showModalInfo = function showModalInfo(title, body, cb) {
  showModalGeneric('info_outline', title, body, 'static', cb);
}

//
// Show modal info with {backdrop: true}
//
window.showModalNotif =function showModalNotif(title, body) {
  showModalGeneric('info_outline', title, body, true);
}

window.showModalConfirm = function showModalConfirm(title, body, icon, confirmCb, cancelCb, confirmText="YES", cancelText="CANCEL") {
  $('#modalConfirmTitle').html((title === '' ? '&nbsp;' : title));
  $('#modalConfirmBody').html(body);
  $('#modalConfirmIcon').text((icon === '' ? 'info_outline' : icon));
  $('#modalConfirmBtn').off().text(confirmText).click(confirmCb);
  if (cancelCb) {
    $('#modalCancelBtn').off().text(cancelText).click(cancelCb);
  }
  $('#modalConfirm').modal('show');
}

//
// Ssh2 tests
//
function ssh2PeriodicTest() {
  window.setInterval (function(res) {
    ifCl.execute('show version', function(res) {
      var dataRsp = res.CmdResponse.response.match(/BQN release: (.*)/);
      var release = "unknown";
      if (dataRsp) {
        release = dataRsp[0].split(/:\s+/)[1];
        if (!release)
          release = "unknown";
      }
      console.log('ssh2Test: BQN release %s', release);
    });
  }, 200);
}
function getFileTest() {
  //var filePath = '/home/jcasado/ehome/projects/bqngui/policy';
  var filePath = '/home/jcasado/ehome/projects/bqngui/diagnostic-20210923081815';
  getFile.execute(filePath);
}

$(document).ready(function() {
  // Load html 'includes'
  var includes = $('[data-include]');
  jQuery.each(includes, function() {
    var hostname = process.env.REACT_APP_BQN_URL ? process.env.REACT_APP_BQN_URL + '/' : '';
    var file = hostname + $(this).data('include');
    views.loading(file);
    $(this).load(file, function(resp, status, it) {
      views.loaded();
    });
  });
  var script = document.createElement('script');
  script.onload = function () {
      console.log('views loaded')
  };
  script.src = 'js/views.js';
  document.head.appendChild(script)
});

function connectServer() {
  var url = decodeUrl(process.env.REACT_APP_BQN_URL ? process.env.REACT_APP_BQN_URL : window.location.href);
  var alertWasDisplayed = false;

  login.remoteAddr = url.hostname;

  window.ifCl = new BqnIfCl.Client(url.hostname, url.port);
  ifCl.on('connected', function(e) {
    login.onConnected(e);
  });
  ifCl.on('disconnected', function(e) {
    console.error('disconnected', e);
    if(!alertWasDisplayed){
      alertWasDisplayed = true;
      $('#sectSidebars').hide();
      $('#sectContent').hide();
      alert('CONNECTION IS CLOSED! '+ e );
    }
    login.doLogout();
    setTimeout(function(){
      window.location.reload();
    });
  });
  ifCl.on('error', function(e) {
    if(!alertWasDisplayed){
      alertWasDisplayed = true;
      alert('ERROR! '+ e + '.\nTry browser reload button.');
    }
  });
  ifCl.on('unavailable', function(e) {
    login.onUnavailable(e);
  });
  ifCl.on('getfile', function(e) {
    var gf = e.GetFileEvent;
    if (gf.length > 0) {
      getFile.add(gf.id, gf.length, gf.chunk);
    } else {
      getFile.end(e.GetFileEvent.id);
    }
  });

  // Start bqn interface client
  ifCl.start();
};

views.ready(function() {
  // Callback fired when all html 'data-include' modules are loaded
  // All html elements callbacks from 'data-include' modules must be here

  window.license = undefined;
  window.login = new Login();
  // License and Login execute license and platform restrictions
  // License and Login must be created before starting connection mechanism
  connectServer();
  
  window.dashboard = undefined;
  window.btcStats = new BtcStats(loadBtcStatsView);
  window.btcGraphImg = new BtcGraphImg();
  window.diagnostic = new Diagnostic();
  window.software = undefined;
  window.users = undefined;
  window.sysNtp = undefined;
  window.sysDate = undefined;
  window.radiusRestSubs = undefined;
  
  window.btaSess = undefined;
  window.subsStatsUsage = new SubsStatsUsage(loadSubsStatsUsageView, 'subsStatsUsage');
  //sysMem = new SysMem(loadSysMemView, 'sysMem');
  window.sysInfo = undefined;

  window.profVlan = undefined;
  window.profIf = undefined;
  window.profPoliciesRate = undefined;
  window.profSubscriberId = undefined;
  window.profDpi = undefined;
  window.profThrou = undefined;

  window.dosStats = new DosStats('dosStats', 'DoS ATTACKS OVER TIME');
  window.dosStatsTime = new DosStatsTime(loadDosStatsTimeView, 'dosStatsTime');
  window.policySubs = new PolicySubs('policySubs');

  window.eula = new Eula('eulaModal', 'eulaModalBody',
    'eulaAgreeBtn', 'eulaCancelBtn', 'eulaOkBtn');

    setTimeout(() => {
      viewsReady();
    }, 500)

  // Trace start parameters
  console.log('POINTS_PER_TILE_CHART: %d', POINTS_PER_TILE_CHART);

  // Tooltips and Popovers delays
  $('[data-toggle="popover"]').popover({'delay': { show: 200, hide: 0 }});
  $('[data-toggle="tooltip"]').tooltip({'delay': { show: 0, hide: 0 }});

  $('#menuReboot').click(function() {
    showModalConfirm('Reboot?','The BQN will be rebooted and traffic may be interrupted for some minutes', 'refresh', function() {
      ifCl.operation('REBOOT', function(res) {
        if (res.CmdResponse.code != 0) {
          showModalError('Error:', res.CmdResponse.response);
        }
      });
    })
  });
  $('#menuShutdown').click(function() {
    showModalConfirm('Shutdown?', 'The BQN will be stopped and traffic may be interrupted', 'power_settings_new', function() {
      ifCl.operation('SHUTDOWN', function(res) {
        if (res.CmdResponse.code != 0) {
          showModalError('Error:', res.CmdResponse.response);
        }
      });
    })
  });
  $('#menuUserGuide').click(function() {
    window.open('bqn-userguide.pdf');
  });
  $('#menuAbout').click(function() {
    $('#aboutHelp').modal({backdrop: true});
  });

  // Interface Statistics Reload button.
  $('#btnIfaceStatsReload').click(function() {
    loadIfaceStatsView();
  });

  $('#btaAccTestExit').click(function() {
    exittAccTest();
  });

  $('#btaAccReloadBtn').click(function() {
    loadBtaAccView();
  });
/* REMOVE when corresponding, now desactivated, core is removed
  $('#btaAccTestBtn').click(function() {
    btaAccTest();
  });
*/
  $('#btaSessReloadBtn').click(function() {
  });
  $('#btaStatsSpReloadBtn').click(function() {
  });
  $('#btaStatsAcReloadBtn').click(function() {
  });
  $('#btcStatsReloadBtn').click(function() {
    //loadBtcStatsGraphViews();
  });
  $('#btcStatsDownloadImgBtn').click(function() {
    //console.log('CLICL: btcStatsDownloadImgBtn');
  });



  $('.draggable-hdr').on("mousedown", function(mousedownEvt) {
      var $draggable = $(this);
      // Click modal-header inside coordinates
      var x = mousedownEvt.pageX - $draggable.offset().left,
          y = mousedownEvt.pageY - $draggable.offset().top;
      $("body").on("mousemove.draggable", function(mousemoveEvt) {
          // New modal-dialog outside coordinates
          $draggable.closest(".modal-dialog").offset({
              "left": mousemoveEvt.pageX - x,
              "top": mousemoveEvt.pageY - y
          });
      });
      $("body").one("mouseup", function() {
          $("body").off("mousemove.draggable");
      });
      $draggable.closest(".modal").one("bs.modal.hide", function() {
          $("body").off("mousemove.draggable");
      });
  });
  $('#sysStatsCpuHelpBtn').click(function() {
    $('#sysStatsCpuHelp').modal({backdrop: true});
  });
  $('#ifStatsHelpBtn').click(function() {
    $('#ifStatsHelp').modal({backdrop: true});
  });
  $('#btaAccHelpBtn').click(function() {
    $('#btaAccHelp').modal({backdrop: true});
  });
  $('#btaSessHelpBtn').click(function() {
  });
  $('#btaStatsSpHelpBtn').click(function() {
    $('#btaStatsSpHelp').modal({backdrop: true});
  });
  $('#btaStatsAcHelpBtn').click(function() {
    $('#btaStatsAcHelp').modal({backdrop: true});
  });
  $('#btcStatsHelpBtn').click(function() {
    $('#btcStatsHelp').modal({backdrop: true});
  });

  // Used by Jquery-knob
  $(function($) {
    $(".knob").knob({
      change : function (value) {
      },
      release : function (value) {
        console.log("KNOB release: " + value);
      },
      cancel : function () {
        console.log("KNOB cancel: ", this);
      },
      format : function (value) {
        return value + '%';
      },
    }).children().off('mousewheel DOMMouseScroll');
  });

  // Used by Jquery.spinner.
  $(".spinner")
    .spinner('delay', 200) //delay in ms
    .spinner('changed', function(e, newVal, oldVal) {
      // trigger lazed, depend on delay option.
    })
    .spinner('changing', function(e, newVal, oldVal) {
      // trigger immediately
    });
});

(function handleCtrlP(){
  $(document).keydown(function(e){
    if(e.keyCode === 80 && e.ctrlKey){
      prepareAndPrint();
      return false;
    }
  })
})()

window.prepareAndPrint = function prepareAndPrint(){
  var timer, timer2;
  var oldWidth, oldDisplay;
  var isSidebarOpen = !$(document.body).hasClass('ls-closed')
  if(isSidebarOpen){
    $(document.body).addClass('ls-closed')
  }
  $(document.body).addClass('no-transitions');
  oldWidth = document.body.style.width;
  document.body.style.width = '21.1cm';
  oldDisplay = $('#sectSidebars').css('display')
  $('#sectSidebars').css('display', 'none')
  clearTimeout(timer);
  clearTimeout(timer2);
  timer = setTimeout(() => {
    document.body.style.width = '21cm';
    timer2 = setTimeout(() => {
      window.print();
      document.body.style.width = oldWidth;
      $('#sectSidebars').css('display', oldDisplay)
      if(isSidebarOpen){
        $(document.body).removeClass('ls-closed')
      }
      setTimeout(()=>{
        $(document.body).removeClass('no-transitions');
      }, 500)
    }, 500)
  },500)
}

var inactivityTimer;
var timeout;

function onBodyClick(){
  if(inactivityTimer && timeout){
    clearTimeout(inactivityTimer);
    setInactivityTimeout();
  }
}

function setInactivityTimeout(){
  clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(() => {
    ifCl.logout(function() {
      return;
    });
    sessionStorage.removeItem(CONST.LOCAL_STORAGE_TOKEN_NAME);
    navigateToLogin();
    $(document.body).unbind('click', onBodyClick)
    showModalInfo('Session ended because of inactivity', null)
  }, timeout*1000)
}

async function configureInactivityTimeout(){
  timeout = null;
  window.serverTimeout = null;

  try{
    var resp = await ifCl.run('show service web parameters');
    var lines = resp.split('\n');
    for(var line of lines){
      var [param, value] = line.split(':')
      if(param.match('Session inactivity timeout')){
        window.serverTimeout = Number(value);
        break;
      }
    }
    if(serverTimeout && !isNaN(serverTimeout)){
      timeout = serverTimeout;
      setInactivityTimeout()
    }
  }catch(e){
    clearTimeout(inactivityTimer);
  }

  $(document.body).unbind('click', onBodyClick).click(onBodyClick)
}

function navigateToLogin(){
  if(location.pathname === '/viewLogin'){
    return;
  }
  const nextUrl = location.pathname.slice(1);

  const searchParams = {};
  if(location.search){
    location.search.slice(1).split('&').forEach(searchParam => {const [key, value] = searchParam.split('='); searchParams[key] = value;})
  }

  if(!nextUrl){
    searchParams.url = 'viewDashboard'
  }else if(nextUrl !== 'viewLogin'){
    searchParams.url = nextUrl;
  }

  const search = Object.entries(searchParams).map(([key, value]) => `${key}=${value}`)
  location.href = '/viewLogin' + (search ? `?${search.join('&')}` : '');
}

async function loadStrictSecurity() {
  return await ifCl.run('show system security').then(parseSystemSecurity).then(securityIsStrict)
    .catch( error => {
      if (error === 'Strict security not enabled') {
        return false; /*not enabled*/
      }
      throw error;
    });
}

const root = createRoot(document.getElementById('root'));
root.render(
    <App />
);
